import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import {
  getVectorPlacements,
  startVectorLayoutAsync,
  pollJob,
  cancelJob,
  isJobTerminal,
  prepressPreflight,
  confirmVectorJob,
  isConfirmResubmitRequired,
  interpretVectorResult,
  netUtilization as vecNetUtil,
  hasActiveMargins as vecHasMargins,
  buildPressPdfPayload, pressReport, effectiveKerf, clampSpacing, SPACING_MAX_MM,
  borderReport,
  downloadPressPdf,
  saveBlob,
  VectorApiError,
  pollDelayMs,
  requestPrepressTile,
  describePrepressCarton,
} from './exportVector';
import { RemoveBordersSwitch, BorderReportList, PressSheetSwitches, PressReportList } from './PressPdfBorders';
// PR ⑬a (prepress): counting, the tiling gate, and the sync-fallback guard.
// PR ⑬b: the per-carton tagging surface and §6 positive verification.
import {
  cartonsPlaced,
  utilizationCaveat,
  quotingOnlyNotice,
  requestsAuto,
  taggedCartonNames,
  sourceIdOf,
  verifyPrepressRan,
  describePrepressError,
  unflappedBlockReason,
  offerableCartonTypes, suggestedCartonTypes,
  flapsMissing, describeRequest, parseDescribe,
  cartonTypeBlockReason, cartonTypeBlock, parseTopologyMismatch,
  isModeOfferable,
  isTilingDisabledError,
  parseConfirmationRequired,
  preflightPieces,
  needsPreflight,
  parsePreflight,
  preflightHalts,
  duplicateNameBlockReason,
  firmLineBlockReason, firmLineHonoured, requiredOwnershipOf,
  unflappedUntaggedBlockReason, unflappedUntaggedReason,
  cleanPrepressConfig,
  reducedCapacity,
  reducedCapacityNotice,
  reducedByCarton,
  reducedAttributionNotice,
  hasFeature,
  normalizeCapabilities,
  offerableModes,
  runSignature,
  prepressSignature,
  detectDedupCollision,
  isBackendDefect,
  estimatedRunNote,
  collectPrepressModes,
} from './prepress';
import VectorLayoutView from './VectorLayoutView';
import AddCartonPdfOverlay from './AddCartonPdfOverlay';
import PrepressReport from './PrepressReport';
import PrepressConfigOverlay from './PrepressConfigOverlay';
import SelectionPanel from './SelectionPanel';
import LibraryView from './LibraryView';
import { getCarton, getCartonSource, uploadCarton } from './library';
import { benchUploadArgs } from './libraryDuplicates';
import { benchAddSummary, fileDigest, mergeIntoBench } from './benchMerge';
import LenInput from './LenInput';
import { useToast } from './ToastProvider';
import { useJobs, JobsMenu } from './JobsProvider';
import HealthIndicator from './HealthIndicator';
import SignInScreen from './SignInScreen';
import { useAuth } from './AuthProvider';
import { signOutLocal, signOutEverywhere, authRequiredKnown, authFetch } from './auth';
import PlannerPanel from './PlannerPanel';
import PlannerSettings from './PlannerSettings';
import { upsertSkus, listSkus, addOrderLines, listOrderLines, uploadOrderLinesCsv, startPlanAsync, pollPlanJob, cancelPlanJob, isPlanTerminal, isPersistedRecord, terminalStatusMessage, getConfig, patchConfig, resetConfig, CONFIG_FIELDS, skusFromVectorResult, skuFromVectorSource, PlannerApiError } from './planner';
// PR ④ (workspace persistence): hydration bundle, remembered presets, ui-state autosave.
import { getWorkspace, putPresets, putUiState, sanitizePresets, splitCartonRefs, normalizeJobKind, pickRecoverableJobs, WorkspaceApiError } from './workspace';
// Saved jobs (decided 2 Nov with #85): a run is kept only when saved, by name.
import { lastRunEntry, missingCartonsMessage, missingFilesMessage, missingSourceFiles, readLastRun,
  rowsLeavingTheTray, saveControl, saveOutcome, saveRequestFrom, savedByJob, savedJobRows,
  unsavedLastRun } from './savedJobs';
import { fetchCarton, fetchCartonSource, getSavedJob, listSavedJobs, removeSavedJob, renameSavedJob,
  saveJob } from './savedJobsApi';
import SaveJobDialog from './SaveJobDialog';
import { PLATFORM_ERROR_COPY } from './platformErrors';
// PR ⑥ (approvals, contract v2.0 §7): advisory badge + approver queue.
import { ApprovalBadge, ApprovalsQueue } from './ApprovalsPanel';
import { canApprove } from './approvals';
// Layout Editor: hand-finishing a nested sheet. Exports through the same
// /export/vector/pdf call as an untouched nest — see editorDoc.js.
import MarginFields from './MarginFields';
import LayoutEditor from './LayoutEditor';
import {
  buildEditorDoc, editorInit, editorApply, marginsAreUniform, collapseToUniform,
  marginCollapseError, createEditorDoc, addSourceCarton, removeSourceCarton,
} from './editorDoc';
import EditorFlapPrompt from './EditorFlapPrompt';
import LoadDestinationPrompt from './LoadDestinationPrompt';

import { useTheme } from './theme';

// ── Backend host ──
// Defined in config.js (a leaf module) so auth.js can import it without
// cycles or mount-order races; re-exported here for existing importers.
import { API_BASE } from './config';
export { API_BASE };

/**
 * Auto-persisted canvas drops go in as DRAFTS (PR ⑧ §1).
 *
 * `?draft=true` keeps scratch bytes out of the curated library picker and out
 * of `recent_cartons`, while still giving each drop a `carton_id` that
 * `ui_state` can reference for restore — and that reference is what protects
 * the draft from server-side cleanup (§5). Promotion to the real library is an
 * explicit user action ("Save to library", library.js `promoteCarton`).
 *
 * On (per the PR ⑧ contract). Kept as a named constant only so the draft/curated
 * decision stays legible and reversible, not because it's expected to change.
 */
const DRAFT_UPLOADS = true;

/**
 * How many cartons "Recent · quick load" offers.
 *
 * A shortcut, not a browser. The backend returns the recent list ordered by
 * last use and the rail wraps it into a chip grid, so eight of them filled most
 * of the visible rail above the actual carton list and pushed the sheet
 * controls below the fold — for a row of buttons every one of which is also
 * one click away in the add-carton overlay's library tab. Four keeps the last
 * few jobs reachable at a glance and gives the rail back to what is on the
 * bench NOW.
 */
const QUICK_LOAD_LIMIT = 4;

function Btn({ children, onClick, primary = true, disabled = false, small = false, color }) {
  const { P, FN } = useTheme();
  return (
    <button onClick={onClick} disabled={disabled} style={{
      fontFamily: FN, fontSize: small ? 12 : 12.5, fontWeight: 500,
      padding: small ? '5px 12px' : '8px 18px', borderRadius: 6, border: 'none',
      cursor: disabled ? 'default' : 'pointer', letterSpacing: 0.1, whiteSpace: 'nowrap',
      background: primary ? (color || P.ink) : 'transparent',
      color: primary ? (color ? '#fff' : P.sheet) : P.muted, opacity: disabled ? 0.4 : 1,
      outline: primary ? 'none' : `0.5px solid ${P.rule}`,
    }}>{children}</button>
  );
}

function Ttl({ children }) {
  const { P, DISPLAY } = useTheme();
  return <h2 style={{ fontFamily: DISPLAY, fontSize: 26, fontWeight: 400, color: P.ink, margin: '0 0 12px', paddingBottom: 8, borderBottom: `0.5px solid ${P.rule}`, letterSpacing: '-0.01em' }}>{children}</h2>;
}

/**
 * `muted` demotes a number that is still true but must not be quoted on —
 * PR ⑬a gates it on `prepress_tiled`, so plain-mode utilization is untouched.
 * `title` carries the reason on hover.
 */
function Stat({ label, value, title, muted }) {
  const { P, MO } = useTheme();
  return (
    <div title={title} style={{ padding: '8px 12px', borderRadius: 6, background: P.elev, border: `0.5px solid ${P.ruleSoft}` }}>
      <div style={{ fontFamily: MO, fontSize: 9, color: P.dim, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}{muted ? ' ⚠' : ''}
      </div>
      <div style={{ fontFamily: MO, fontSize: 14, fontWeight: 500, color: muted ? P.muted : P.ink, marginTop: 3 }}>{value}</div>
    </div>
  );
}

function Mark() {
  const { P } = useTheme();
  return (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none" style={{ flexShrink: 0 }} aria-hidden>
      <rect x="1.6" y="1.6" width="18.8" height="18.8" rx="3" stroke={P.ink} strokeWidth="1.2" />
      <rect x="6" y="6" width="10" height="10" rx="1.5" fill={P.accent} />
    </svg>
  );
}

function AccountControl() {
  const { P, FN, MO } = useTheme();
  const auth = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [signInOpen, setSignInOpen] = useState(false);

  if (!auth.signedIn) {
    return (
      <>
        <button onClick={() => setSignInOpen(true)} style={{
          fontFamily: MO, fontSize: 10.5, padding: '5px 11px', borderRadius: 6, cursor: 'pointer',
          background: 'transparent', color: P.muted, border: `0.5px solid ${P.rule}`,
        }}>{auth.resuming ? 'Resuming\u2026' : 'Sign in'}</button>
        {signInOpen && <SignInScreen overlay onClose={() => setSignInOpen(false)} />}
      </>
    );
  }

  const u = auth.user || {};
  const initial = (u.display_name || u.email || '?').trim().charAt(0).toUpperCase();
  return (
    <div style={{ position: 'relative' }}>
      <button onClick={() => setMenuOpen((o) => !o)} title={u.email || ''} style={{
        width: 28, height: 28, borderRadius: '50%', background: P.chipBg, color: P.chipInk,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: MO, fontSize: 11, fontWeight: 600, cursor: 'pointer', border: 'none',
      }}>{initial}</button>
      {menuOpen && (
        <>
          <div onClick={() => setMenuOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 90 }} />
          <div style={{
            position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 100, width: 230,
            background: P.sheet, border: `0.5px solid ${P.rule}`, borderRadius: 10,
            boxShadow: '0 16px 40px rgba(20,16,12,.22)', padding: 6, animation: 'mono-in .14s ease both',
          }}>
            <div style={{ padding: '8px 10px', borderBottom: `0.5px solid ${P.ruleSoft}`, marginBottom: 4 }}>
              <div style={{ fontFamily: FN, fontSize: 12.5, fontWeight: 500, color: P.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.display_name || u.email}</div>
              <div style={{ fontFamily: MO, fontSize: 9.5, color: P.dim, marginTop: 2 }}>{u.email}{u.role ? ` \u00b7 ${u.role}` : ''}{u.is_superuser ? ' \u00b7 superuser' : ''}</div>
            </div>
            {[
              { label: 'Sign out', act: () => { setMenuOpen(false); signOutLocal(); } },
              { label: 'Sign out all devices', act: () => { setMenuOpen(false); signOutEverywhere(); } },
            ].map((it) => (
              <button key={it.label} onClick={it.act} style={{
                display: 'block', width: '100%', textAlign: 'left', fontFamily: MO, fontSize: 11,
                color: P.muted, background: 'transparent', border: 'none', borderRadius: 6,
                padding: '7px 10px', cursor: 'pointer',
              }}>{it.label}</button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * The tenant's mark, when their org has uploaded one in the control center.
 *
 * It replaces the "Carton Imposition" strapline rather than sitting next to it:
 * the strapline exists to say whose tool this is, and once an org has branded
 * it, their own mark says that better. No logo — or a logo that fails to load —
 * falls back to the strapline, so the header is never empty.
 *
 * The URL is API-relative and arrives on the user payload (auth.js recovers the
 * user from the token bundle without a /auth/me round trip), so this renders on
 * the first paint after a session resume rather than a beat later.
 */
function OrgMark() {
  const auth = useAuth();
  const { P, MO } = useTheme();
  const [failed, setFailed] = React.useState(false);
  const path = auth.user?.org_logo_url || null;
  const src = path ? `${API_BASE}${path}` : null;

  const strapline = (
    <span style={{
      fontFamily: MO, fontSize: 10, color: P.dim, textTransform: 'uppercase',
      letterSpacing: 1, borderLeft: `0.5px solid ${P.rule}`, paddingLeft: 10,
    }}>
      Carton Imposition
    </span>
  );
  if (!src || failed) return strapline;

  return (
    <span style={{
      display: 'flex', alignItems: 'center', minWidth: 0,
      borderLeft: `0.5px solid ${P.rule}`, paddingLeft: 10,
    }}>
      <img
        src={src}
        alt={auth.user?.org_name ? `${auth.user.org_name} logo` : 'Organisation logo'}
        title={auth.user?.org_name || undefined}
        onError={() => setFailed(true)}
        style={{ height: 22, maxWidth: 190, objectFit: 'contain', display: 'block' }}
      />
    </span>
  );
}

function TopBar({ workspace, setWorkspace, showApprovals = false, savedJobs }) {
  const { P, MO, DISPLAY, toggleDark, unit, toggleUnit } = useTheme();
  const tab = (active) => ({
    fontFamily: MO, fontSize: 11, fontWeight: 500, letterSpacing: 0.3,
    padding: '6px 14px', borderRadius: 6, cursor: 'pointer', whiteSpace: 'nowrap',
    background: active ? P.ink : 'transparent', color: active ? P.sheet : P.muted, border: 'none',
  });
  const inertItem = {
    fontFamily: MO, fontSize: 10.5, color: P.dim, padding: '5px 6px',
    cursor: 'default', opacity: 0.75, whiteSpace: 'nowrap',
  };
  return (
    <header style={{
      position: 'sticky', top: 0, zIndex: 50,
      display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: 16,
      height: 56, padding: '0 20px',
      background: P.surface, borderBottom: `0.5px solid ${P.rule}`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
        <Mark />
        <span style={{ fontFamily: DISPLAY, fontSize: 22, fontWeight: 400, color: P.ink, lineHeight: 1 }}>Mono</span>
        <OrgMark />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 2, padding: 3, borderRadius: 8, background: P.elev, border: `0.5px solid ${P.ruleSoft}` }}>
        <button style={tab(workspace === 'layout')} onClick={() => setWorkspace('layout')}>Auto Imposer</button>
        <button style={tab(workspace === 'editor')} onClick={() => setWorkspace('editor')}>Editor</button>
        <button style={tab(workspace === 'planner')} onClick={() => setWorkspace('planner')}>Job Planner</button>
        <button style={tab(workspace === 'library')} onClick={() => setWorkspace('library')}>Library</button>
        {showApprovals && (
          <button style={tab(workspace === 'approvals')} onClick={() => setWorkspace('approvals')}>Approvals</button>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 1, padding: 2, borderRadius: 6, background: P.elev, border: `0.5px solid ${P.ruleSoft}` }} title="Display units for sheet, kerf, and margins">
          {['mm', 'in'].map((u) => (
            <button key={u} onClick={() => { if (unit !== u) toggleUnit(); }} style={{
              fontFamily: MO, fontSize: 10, fontWeight: 600, padding: '3px 8px', borderRadius: 4, cursor: 'pointer',
              background: unit === u ? P.ink : 'transparent', color: unit === u ? P.sheet : P.dim, border: 'none',
            }}>{u}</button>
          ))}
        </div>
        <span style={{ width: 1, height: 18, background: P.rule }} />
        <HealthIndicator apiBase={API_BASE} />
        <JobsMenu {...savedJobs} />
        <span style={inertItem} title="Coming with multi-user (Layer C)">Approvals</span>
        <span style={inertItem} title="Coming with multi-user (Layer C)">🔔</span>
        <button onClick={toggleDark} title="Toggle dark mode" style={{
          fontFamily: MO, fontSize: 12, padding: '5px 9px', borderRadius: 6,
          background: 'transparent', color: P.muted, border: `0.5px solid ${P.rule}`, cursor: 'pointer',
        }}>{P.isDark ? '☀' : '☾'}</button>
        <AccountControl />
      </div>
    </header>
  );
}

// ─── Main ───────────────────────────────────────────────────────────────────

// §7: pre-flip the app works signed-out (no gate, bearer attached when
// present). The full-page gate appears only when auth is actually demanded —
// a business 401 with no recoverable session, or a revoked session family.
// Gate lives in this wrapper (NOT as an early return inside MainApp) so
// MainApp's hook order never changes when the gate flips.
/** Quiet branded state while a stored session is silently resumed — shown
 *  INSTEAD of the dashboard, so returning users never see a pre-auth flash. */
function BootSplash() {
  const { P, MO, DISPLAY } = useTheme();
  return (
    <div style={{ minHeight: '100vh', background: P.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
        <Mark />
        <div style={{ fontFamily: DISPLAY, fontSize: 22, color: P.ink, lineHeight: 1 }}>Mono</div>
        <div style={{ fontFamily: MO, fontSize: 10.5, color: P.dim, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: P.accent, animation: 'mono-pulse 1.2s ease-in-out infinite' }} />
          Resuming your session…
        </div>
      </div>
    </div>
  );
}

// Shell router. One real route: #/login (hash-based on purpose — a path route
// like /login 404s on GitHub Pages hard-refresh without the 404-redirect hack;
// a hash route needs zero server config and survives refresh/bookmark).
//   · resuming        → BootSplash (never the dashboard)
//   · sign-in needed  → #/login, full-page SignInScreen
//     ("needed" = gate demanded, OR known up front via authRequiredKnown —
//      the learned hint / REACT_APP_AUTH_REQUIRED — so post-flip boots go
//      straight to login with no dashboard paint)
//   · signed in on #/login → redirected back to the app
// Pre-flip with no hint, the app stays usable signed-out, unchanged.
export default function App() {
  const auth = useAuth();
  const [route, setRoute] = useState(() => window.location.hash);

  useEffect(() => {
    const onHash = () => setRoute(window.location.hash);
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const wantLogin = !auth.signedIn && !auth.resuming && (auth.gate || authRequiredKnown());

  useEffect(() => {
    if (wantLogin && route !== '#/login') {
      window.location.hash = '#/login';
    } else if (auth.signedIn && route === '#/login') {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
      setRoute('');
    }
  }, [wantLogin, auth.signedIn, route]);

  if (auth.resuming) return <BootSplash />;
  if (wantLogin || (route === '#/login' && !auth.signedIn)) return <SignInScreen />;
  return <MainApp />;
}

function MainApp() {
  const { P, FN, MO, DISPLAY, TC, inp, unit, fmtLen } = useTheme();
  const toast = useToast();
  const jobs = useJobs();
  const auth = useAuth();   // PR ④: hydration trigger + admin gate for shop config
  // Phase 2 rail primitives (editorial card + section head + field label).
  const railCard = { background: P.surface, border: `0.5px solid ${P.rule}`, borderRadius: 10, padding: 14 };
  const railHead = { fontFamily: MO, fontSize: 10, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', color: P.dim, marginBottom: 10, display: 'block' };
  const railField = { flex: 1, fontFamily: MO, fontSize: 10.5, color: P.muted, display: 'block' };

  // Layout
  const [shW, setShW] = useState(500);
  const [shH, setShH] = useState(350);

  // v3.0 — gripper margins. Either a single uniform margin applied to all four
  // edges, or per-side values. Per the contract, margin keys map to MNE's Y-up
  // sheet edges: bottom = press gripper (leading edge), top = tail, left/right
  // = guides. Values are mm, must be >= 0.
  const [marginMode, setMarginMode] = useState('uniform'); // 'uniform' | 'perSide'
  const [marginUniform, setMarginUniform] = useState(0);
  /**
   * Switch which editor is showing WITHOUT changing what is reserved under the
   * operator's hands.
   *
   * uniform -> per side: seed all four from the single value, so the sheet
   *   preview does not move at the moment of switching.
   * per side -> uniform: adopt the LARGEST edge. Collapsing four numbers into
   *   one has to pick, and any other pick silently reduces a clearance someone
   *   set deliberately — a gripper most of all.
   */
  const switchMarginMode = useCallback((next) => {
    setMarginMode((cur) => {
      if (next === cur) return cur;
      if (next === 'perSide') {
        setMarginSides((prev) => (marginsAreUniform(prev) && prev.top === marginUniform
          ? prev
          : { top: marginUniform, right: marginUniform, bottom: marginUniform, left: marginUniform }));
      } else {
        setMarginSides((prev) => { setMarginUniform(collapseToUniform(prev)); return prev; });
      }
      return next;
    });
  }, [marginUniform]);
  // setter added in PR ④ so hydrated (per-side) preset margins can be applied.
  const [marginSides, setMarginSides] = useState({ bottom: 0, top: 0, left: 0, right: 0 });

  // ── Vector export path (Mono Export Vector Contract v1.2) ──
  // The only carton path: PDF cartons that carry the die naming convention
  // (DIELINE layer / CutContour spot). The backend auto-detects the die and runs
  // MNE; the manual calibrate/draw/offset pipeline was removed in Phase 2.
  // Job Planner (contract v1.0) — demand-driven planning that sits in front of
  // the nesting/export flow; approving a plan writes its up_counts into the
  // layout and hands off.
  // 'layout' IS the Auto Imposer. The VALUE stays `layout` on purpose: it is
  // written into `ui_state` (v7) and read back at hydration, so renaming the key
  // alongside the label would silently drop every saved session back to the
  // default workspace. The label is a product name and lives in TopBar; the key
  // is a wire value and lives here.
  const [workspace, setWorkspace] = useState('layout');   // 'layout' (Auto Imposer) | 'editor' | 'planner' | 'library' | 'approvals'
  const plannerMode = workspace === 'planner';            // derived — keeps existing gates working
  // Layout Editor state: { doc, past, future }, or null before a layout is
  // loaded into it. Lifted to App so switching tabs mid-edit does not throw the
  // operator's work (or their undo stack) away — LayoutEditor unmounts when the
  // workspace changes, and nothing here is recoverable from the server.
  const [editorState, setEditorState] = useState(null);
  /**
   * THE EDITOR'S OWN DESK.
   *
   * The two workspaces used to share `vecFiles`, which read as a convenience
   * and behaved as a coupling: removing a carton from the Layout rail orphaned
   * whatever the editor had already placed of it, and the editor had no way of
   * its own to put a carton on a sheet — the only door in was "Open in editor"
   * on a finished nest.
   *
   * So the editor holds its own list, in the same shape
   * (`{ id, libraryId, file, name, count, detection, prepress }`), because it
   * needs the same thing from it: the PDF BYTES, re-sent one part per
   * source_id, are what makes an edited sheet plateable. Opening a layout in
   * the editor COPIES the bench across; adding a carton in the editor does not
   * reach back into Auto Imposer.
   */
  const [editorFiles, setEditorFiles] = useState([]);
  // Which desk the add-carton overlay is committing to — null when closed.
  // One overlay, two destinations: the upload + library + detect flow is the
  // same work wherever the carton is going to land.
  const [addTarget, setAddTarget] = useState(null);      // null | 'layout' | 'editor'
  // The Library workspace's destination question — { carton, busy, error }, or
  // null when nothing is waiting on an answer.
  const [libLoad, setLibLoad] = useState(null);
  const [vecFiles, setVecFiles] = useState([]);          // [{ id, file, name, count, detection, prepress }]
  // PR ⑬b §6: positive verification that prepress actually ran for the cartons
  // we tagged. The failure the backend shipped and fixed was SILENT — the job
  // succeeded and the carton nested with no flaps — so absence of error is not
  // evidence. Null when nothing was tagged.
  const [prepressCheck, setPrepressCheck] = useState(null);
  // Carton name from a prepress 400's `piece` field, so the rail can highlight
  // the offending row instead of showing a job-level error.
  const [prepressErrPiece, setPrepressErrPiece] = useState(null);
  // ⑭a / rev 5 §8.1: ASK what this deployment can do. The session-state
  // `tilingCap` that used to sit here inferred the gate from a successful
  // result (`prepress_tiled: true` → enabled) — a safety posture derived from
  // an outcome. `capabilities.allow_tiling` is authoritative and `modes`
  // already omits `pair` when the gate is shut, so the inference is deleted,
  // not replaced.
  const [prepressCaps, setPrepressCaps] = useState(null);
  // ⑬i: non-404 failure of the capabilities call. Held so the rail can say the
  // deployment's abilities are unknown rather than silently presenting defaults
  // as if they had been confirmed.
  const [prepressCapsError, setPrepressCapsError] = useState(null);
  // ⑭c: a halt awaiting confirmation. NOT an error — the job can still run,
  // and the operator decides. Holds the token to echo back, the warnings to
  // show verbatim, and how to continue (`confirm` re-queues uploads the server
  // already has; `resubmit` sends them again).
  //   { ackToken, warnings, kind: 'preflight'|'submit'|'job', jobId }
  const [prepressHalt, setPrepressHalt] = useState(null);
  // ⑭d: { requested, capacity, shortfall, notice } when prepress reduced the
  // run to protect a firm order line, else null.
  const [prepressReduced, setPrepressReduced] = useState(null);
  // ⑭e: the bench carton whose config editor is open, by id. Config is a
  // property of the carton, so the editor is opened FROM a carton and can
  // never be ambiguous about what it applies to.
  const [configCartonId, setConfigCartonId] = useState(null);
  // Rev 4 §1: the dedup guarantee is verifiable client-side — same artwork with
  // different tagging MUST yield a different job_id, because prepress rewrites
  // the geometry before the payload is hashed. A collision means the transform
  // was skipped and a cached bare-outline result came back.
  const vecRunHistoryRef = useRef([]);
  // ⑬i: capabilities is an AUTHENTICATED call, and its failure is visible.
  //
  // It used bare `fetch`, so it carried no Authorization header, got no token
  // refresh and no 401 retry — and `if (!r.ok) return` swallowed the 401
  // silently. That was survivable while a session-state fallback existed. ⑭a
  // deleted the tiling inference and made this endpoint the ONLY authority, so
  // a silent failure now means the rail quietly runs on defaults: no firm-line
  // checkbox (`features.required_pieces` unreadable), no gate warning, no
  // carton types, and a tiling posture nobody asked the server about.
  //
  // Re-run when the session changes: signing in must not leave the UI stuck on
  // the capabilities of a signed-out boot.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await authFetch(`${API_BASE}/planner/prepress/capabilities`);
        if (!r.ok) {
          // A 404 is a pre-rev-3 backend and genuinely fine. Anything else —
          // 401, 503 PrepressUnavailable, a gateway error — means we do not
          // know what this deployment can do, and saying so beats guessing.
          if (alive && r.status !== 404) setPrepressCapsError(r.status);
          return;
        }
        const body = await r.json();
        if (alive) { setPrepressCaps(normalizeCapabilities(body)); setPrepressCapsError(null); }
      } catch (e) {
        if (alive) setPrepressCapsError(e?.name === 'AbortError' ? null : 'offline');
      }
    })();
    return () => { alive = false; };
  }, [auth.signedIn]);
  const [vecKerf, setVecKerf] = useState('0');           // mm, cut clearance (§6)
  // Counts from an applied plan, keyed by sku_id (= source_id). Applied to
  // matching cartons now AND to any matching cartons added later, so the
  // plan → layout handoff works regardless of whether the PDFs are uploaded
  // before or after choosing the plan.
  const [pendingPlanCounts, setPendingPlanCounts] = useState({});
  const [vecResult, setVecResult] = useState(null);      // /export/vector response
  const [vecLoading, setVecLoading] = useState(false);
  // PR ⑥: the succeeded layout job's id, retained after vecJob is cleared, so
  // the approval badge has something to attach to (§7.1).
  const [vecJobId, setVecJobId] = useState(null);
  const [vecJob, setVecJob] = useState(null);             // central-jobs snapshot while a layout job runs (phase/message/fraction)
  const [vecError, setVecError] = useState(null);
  const [vecWarn, setVecWarn] = useState(null);
  // Per-source operator decisions for RGB-flagged cartons: id -> 'reexport' | 'proceed'
  const [vecDecisions, setVecDecisions] = useState({});
  // Press-ready PDF export (POST /export/vector/pdf)
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  // One setting for both download buttons (Layout and the Layout Editor): take
  // each carton's borders off. And what the backend reported doing about it for
  // the last export: { report: borderReport(manifest) | null, requested }.
  const [removeBorders, setRemoveBorders] = useState(true);
  const [pdfBorders, setPdfBorders] = useState(null);
  // 1.11: the printer's carton marks and bleed, one setting for both download
  // buttons like the borders switch; and what came back about them.
  const [pdfMarks, setPdfMarks] = useState(true);
  const [pdfBleed, setPdfBleed] = useState(true);
  // 1.12: with bleed on, stretch the edge where the artwork stops at it.
  const [pdfAutoBleed, setPdfAutoBleed] = useState(true);
  const [pdfMarksOnNeighbours, setPdfMarksOnNeighbours] = useState('skip');
  const [pdfPress, setPdfPress] = useState(null);
  // SKU auto-registration (Option A / trigger #2): vector-detected dies become
  // planner SKUs after a successful /export/vector run. Tracks which source_ids
  // are registered, plus a transient status line. Best-effort: never blocks layout.
  const [registeredSkuIds, setRegisteredSkuIds] = useState([]);
  const [skuRegStatus, setSkuRegStatus] = useState(null);

  // ── Job Planner durable state (lifted from PlannerPanel so it survives the
  // panel unmount when the user switches workspace mode — fixes the reset bug) ──
  const [plSkus, setPlSkus] = useState([]);              // registered SKUs (GET /planner/skus)
  const [plOrderLines, setPlOrderLines] = useState([]);  // demand draft
  const [plPlan, setPlPlan] = useState(null);            // last PlanResponse
  const [plSheet, setPlSheet] = useState({ w: 720, h: 520, gripper: '12' });
  const [plBusy, setPlBusy] = useState(false);
  const [plError, setPlError] = useState(null);
  const [plCsvWarnings, setPlCsvWarnings] = useState([]);
  const [plDraftSku, setPlDraftSku] = useState('');
  const [plDraftQty, setPlDraftQty] = useState('');
  const [plDraftDue, setPlDraftDue] = useState('');
  const [plDraftOverTol, setPlDraftOverTol] = useState('');
  // ⑭d: firm order line — per LINE, never per SKU (rev 5 §6.1). The same SKU
  // can be firm in one line and flexible in another.
  const [plDraftRequired, setPlDraftRequired] = useState(false);
  const [plDraftUnderTol, setPlDraftUnderTol] = useState(''); // under_tolerance_pct (optional)

  // Planner sub-tab + shop-wide config (Settings screen, brief §4)
  const [plTab, setPlTab] = useState('plan');                 // 'plan' | 'settings'
  // Async planning (cancel + progress, Contract v1.2). When a job is active we
  // poll it; plPlan is populated from the job's result on success.
  const [plJob, setPlJob] = useState(null);                   // latest job state from poll
  const [plJobUrls, setPlJobUrls] = useState(null);           // { poll, cancel } ready-made URLs
  // Mirror of plJobUrls for closures captured before the state commits (the
  // Jobs-tray onCancel is created in plComputePlan, BEFORE setPlJobUrls lands;
  // reading the state there would see null and silently no-op — v1.3 cancel fix).
  const plJobUrlsRef = useRef(null);
  useEffect(() => { plJobUrlsRef.current = plJobUrls; }, [plJobUrls]);
  const [plCancelling, setPlCancelling] = useState(false);
  const [cfg, setCfg] = useState(null);                       // loaded config
  const [cfgDraft, setCfgDraft] = useState(null);             // editable copy
  const [cfgBusy, setCfgBusy] = useState(false);
  const [cfgError, setCfgError] = useState(null);
  const [cfgSavedAt, setCfgSavedAt] = useState(null);
  const [cfgAdvancedOpen, setCfgAdvancedOpen] = useState(false);

  // ── PR ④ (workspace persistence) ──────────────────────────────────────────
  // Remembered planning defaults (presets, §2). Sheet W/H, margins and kerf map
  // onto the existing Layout controls (shW/shH, marginSides, vecKerf); these two
  // are new nesting defaults with no live control yet:
  //   · rev 8 §1/§2, widened by rev 11 §3.1: `allowed_rotations` and
  //     `time_limit` are withdrawn from the client surface entirely — both are
  //     engine policy, resolved by the backend from deployment configuration,
  //     and sending either is 400 EnginePolicyNotAccepted on any of SIX routes.
  //     The state, the controls and the wire parameter are all gone rather
  //     than left inert.
  //
  // ⑭j: rev 7 §9 ("there is no backend default, so send it only where an
  // operator touched the control") is marked SUPERSEDED in rev 11 §15 —
  // withdrawn entirely, not migrated. Its comment block lived here and is
  // deleted rather than annotated: a rule that no longer holds, left in place
  // with a note, is how six stacked revisions became a hazard of their own.

  // Save-defaults status (the "Save as my defaults" button in the Layout rail).
  const [presetsBusy, setPresetsBusy] = useState(false);
  const [presetsError, setPresetsError] = useState(null);
  const [presetsSavedAt, setPresetsSavedAt] = useState(null);
  // Hydration payloads (§1). recent_cartons → quick-load; active_jobs is ALWAYS
  // empty this release but rendered so the next PR (durable jobs) just fills it.
  // §2A/§2B: what the user had configured when each job was submitted, so a
  // tray click can put the workspace back exactly as it was. The job result
  // carries sheet/margins but NOT requested quantities (placements only show
  // what fit), so the request context has to be recorded at submit time.
  const [jobContexts, setJobContexts] = useState({});
  const jobContextsRef = useRef({});
  jobContextsRef.current = jobContexts;

  // Saved jobs (decided 2 Nov with #85): a run is kept only when its owner
  // saves it, by name. `savedJobs` is the server's list (an admin's holds the
  // organisation's, each with its owner); `lastRun` is the latest successful
  // run with its setup, kept in ui_state so it can still be saved after the
  // window is closed; `returnedRun` is that run as found on sign-in, which is
  // offered for saving once.
  const [savedJobs, setSavedJobs] = useState([]);
  const [savedLoaded, setSavedLoaded] = useState(false);
  const [lastRun, setLastRun] = useState(null);
  const lastRunRef = useRef(null);
  lastRunRef.current = lastRun;
  const [returnedRun, setReturnedRun] = useState(null);
  const offeredRunRef = useRef(null);
  const [saveDialog, setSaveDialog] = useState(null);   // { mode: 'save'|'rename', name, row? }
  const [saveBusy, setSaveBusy] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveDone, setSaveDone] = useState(null);       // savedJobs.saveOutcome once saved
  const refreshSavedJobs = useCallback(async () => {
    try { setSavedJobs(await listSavedJobs({ apiBase: API_BASE })); } catch { /* keep what we had */ }
    setSavedLoaded(true);
  }, []);

  const [recentCartons, setRecentCartons] = useState([]);
  // V2 #97: the backend picks a superuser's Recent chips from the view chosen
  // in their Library, so the chips are fetched again when that view changes.
  const refreshRecentCartons = useCallback(async () => {
    try {
      const ws = await getWorkspace({ apiBase: API_BASE });
      setRecentCartons(ws.recent_cartons);
    } catch { /* the chips keep what they had */ }
  }, []);
  const [activeJobs, setActiveJobs] = useState([]);
  // Which user id we've already hydrated (guards the effect against re-runs and
  // StrictMode double-mount); the ready flag gates ui-state autosave so the
  // initial restore never clobbers the saved blob with defaults.
  const hydratedForRef = useRef(null);
  // In-flight guard, separate from the completion latch above. A StrictMode
  // mount→cleanup→mount must not start a second fetch, but it must ALSO not be
  // mistaken for "already hydrated" — conflating the two is what silently
  // disabled hydration and autosave.
  const hydratingRef = useRef(null);
  // Live signed-in user id, refreshed every render, so the async hydration can
  // tell a StrictMode remount (same uid → keep going) from a genuine user
  // change or sign-out (different uid → discard the result).
  const currentUidRef = useRef(null);
  const wsReadyRef = useRef(false);
  const [wsReady, setWsReady] = useState(false);
  // false ⇒ backend predates PR ④ (GET /workspace 404) — skip presets/autosave.
  const wsSupportedRef = useRef(null);
  const [wsSupported, setWsSupported] = useState(null);
  // Debounce timer + a one-shot "too large" latch so a 413 doesn't re-toast.
  const uiSaveTimerRef = useRef(null);
  // Holds the latest snapshot while a debounced save is still pending, so an
  // unload can flush it (see the pagehide effect).
  const pendingUiRef = useRef(null);
  const uiTooLargeRef = useRef(false);

  // ── Margin derivation ──
  // Resolve the UI state (uniform vs per-side) into a single {bottom,top,left,right}
  // object of mm values, clamped to >= 0. This is the single source of truth that
  // both the payload builder and the renderer consume.
  const effectiveMargins = useMemo(() => {
    const clamp = (n) => {
      const v = Number(n);
      return Number.isFinite(v) && v > 0 ? v : 0;
    };
    if (marginMode === 'uniform') {
      const u = clamp(marginUniform);
      return { bottom: u, top: u, left: u, right: u };
    }
    return {
      bottom: clamp(marginSides.bottom),
      top: clamp(marginSides.top),
      left: clamp(marginSides.left),
      right: clamp(marginSides.right),
    };
  }, [marginMode, marginUniform, marginSides]);

  // Client-side validation matching the backend's 422 gates (Section 2 of the
  // contract). We catch these before sending so the user gets immediate feedback
  // rather than a round-trip error.
  // One rule, one implementation, shared with the editor — which can reach the
  // same invalid state from the other direction by shrinking the sheet under a
  // margin that was fine when it was set.
  const marginError = useMemo(
    () => marginCollapseError(effectiveMargins, shW, shH, fmtLen),
    [effectiveMargins, shW, shH, fmtLen],
  );

  // ── Vector path handlers (contract v1.2) ──────────────────────────────────

  // Commit staged cartons from AddCartonPdfOverlay. Detection + preflight already
  // ran in the overlay; here we append them to the layout, carry any RGB re-export
  // decisions, and register the detected dies as planner SKUs (no re-fetch).
  /**
   * Fetch a library carton's bytes, shaped as one commit item (PR ③).
   *
   * Loading REPLACES the upload+detect step: the carton already carries its
   * SourceResult geometry, and /source gives the original PDF bytes for the
   * lossless export path — so this feeds the same commit path a fresh upload
   * would, no re-detect. getCarton fires the usage metric (called here, on a
   * real load, not for list rendering).
   */
  const fetchLibraryCarton = async (carton) => {
    if (carton.has_source === false || carton.source_kind === 'parametric') {
      throw new Error('This carton has no source PDF and can’t be loaded for export.');
    }
    // Full record (fires usage metric); fall back to the list record on failure.
    let full = carton;
    try { full = await getCarton({ id: carton.id }); } catch { /* metric best-effort */ }
    const filename = full.geometry?.filename || full.filename || `${full.name || full.id}.pdf`;
    const file = await getCartonSource({ id: full.id, filename });
    // Shape one commit item exactly like the overlay produces; geometry IS the
    // detection (same SourceResult contract skuFromVectorSource consumes).
    return { item: { file, name: filename, count: 1, detection: full.geometry, libraryId: full.id }, full };
  };

  /**
   * Put a library carton on the desk(s) the operator chose.
   *
   * `dest` is 'layout' (Auto Imposer) | 'editor' | 'both'. The two desks ingest
   * DIFFERENTLY and that is the point of offering both rather than loading once
   * and copying: the Auto Imposer takes the artwork die and runs prepress as
   * part of the nest, while the editor asks up front whether the carton still
   * needs its flaps, because nothing downstream of a hand-placed die will.
   *
   * One fetch either way — the same File object goes to both desks, which is
   * correct: identical bytes, one row each, removed independently.
   */
  const loadLibraryCartonInto = async (carton, dest, { prompted = false } = {}) => {
    if (prompted) setLibLoad((s) => (s ? { ...s, busy: true, error: null } : s));
    try {
      const { item, full } = await fetchLibraryCarton(carton);
      const toLayout = dest === 'layout' || dest === 'both';
      const toEditor = dest === 'editor' || dest === 'both';
      if (toLayout) commitVectorCartons([item]);
      if (toEditor) commitEditorCartons([item]);
      // Land on the Auto Imposer for `both`: it is the one that needs setting up
      // (counts, sheet, run), and the editor's own flap prompt renders over
      // whichever workspace is showing.
      setWorkspace(toLayout ? 'layout' : 'editor');
      setLibLoad(null);
      // The editor announces its own arrivals — its commit path has more to say
      // (a flap question, a carton it could not place) and two toasts for one
      // click would bury whichever mattered.
      if (toLayout) {
        toast.notify({
          kind: 'success',
          title: 'Carton loaded',
          message: `${full.name} → ${dest === 'both' ? 'Auto Imposer and Editor' : 'Auto Imposer'}`,
        });
      }
    } catch (e) {
      // A failure belongs where the operator is looking. Inside the dialog it
      // stays in the dialog, so the choice they made is still on screen to
      // retry; from a Recent chip there is no dialog, and swallowing it here
      // would leave a click that did nothing and said nothing.
      if (prompted) setLibLoad((s) => (s ? { ...s, busy: false, error: e } : s));
      else throw e;
    }
  };

  /**
   * The Library workspace's "Load →": ASK, do not assume.
   *
   * This is the one entry point with no destination of its own. The + Add carton
   * dialog commits to whichever rail opened it and the Recent chips load into
   * the rail they sit in; the Library is a screen reached from the top bar, and
   * hard-coding the Auto Imposer here meant an operator who wanted the carton on
   * the editor's desk had to run a nest they did not want first.
   *
   * The parametric refusal stays HERE rather than behind the prompt: offering a
   * choice of desk for a carton that can reach neither is a dialog whose every
   * answer fails.
   */
  const loadCartonFromLibrary = async (carton) => {
    if (carton.has_source === false || carton.source_kind === 'parametric') {
      throw new Error('This carton has no source PDF and can’t be loaded for export.');
    }
    setLibLoad({ carton, busy: false, error: null });
  };

  /**
   * Persist locally-dropped PDFs into the carton library so they survive a
   * session (Cloud feedback §1 follow-up; backend confirmed POST
   * /library/cartons handles multipart + dedup).
   *
   * Design constraints:
   *  · **Best-effort and non-blocking.** The layout path runs off `c.file`
   *    bytes held in memory, so a failed upload costs *restorability only* —
   *    never the ability to work. We therefore never block the commit, never
   *    throw, and never make the user wait.
   *  · **Idempotent by contract (§5.3).** A byte-identical re-upload returns
   *    201 with the EXISTING record, so re-dropping the same die doesn't
   *    duplicate it. We key on the returned `id`, never on the status code.
   *  · **Skipped when it can't work**: signed out (no token), or a backend
   *    without the library/workspace surface.
   *  · **503 blob_unconfigured** (§1: object storage down) is reported once,
   *    not once per file — the user can't act on it per-carton anyway.
   */
  const persistDroppedCartons = useCallback(async (added, setBench = setVecFiles) => {
    const candidates = (added || []).filter((c) => c && !c.libraryId && c.file);
    if (!candidates.length) return;
    if (!auth.signedIn) return;                       // needs a bearer token
    if (wsSupportedRef.current === false) return;     // pre-v2.0 backend

    let storageDown = false;
    let failed = 0;
    let noDieline = 0;

    for (const c of candidates) {
      try {
        // §1: scratch drops are drafts; §5.3 dedup makes re-drops free. We key
        // on the returned id, never the status code (201 either way). A draft
        // never meets the duplicate check (V2-AC-26), and benchUploadArgs
        // never asks to override it.
        const rec = await uploadCarton(benchUploadArgs(c, DRAFT_UPLOADS));
        if (rec?.id) {
          // Tag the bench item so ui_state can reference it (splitCartonRefs) —
          // which is also what keeps the draft alive against cleanup (§5).
          // WHICH bench is the caller's: the editor keeps its own desk, and
          // writing a library id into the wrong list would leave the carton
          // that was actually uploaded looking local-only forever.
          setBench((prev) => prev.map((x) => (x.id === c.id ? { ...x, libraryId: rec.id } : x)));
        }
      } catch (e) {
        if (e?.status === 503) { storageDown = true; failed += 1; }
        else if (e?.status === 400) { noDieline += 1; }   // §1: no dieline → canvas-only, not a fault
        else { failed += 1; }
      }
    }

    if (storageDown) {
      toast.notify({
        kind: 'warn', title: 'Cartons not saved',
        message: 'Library storage is unavailable, so these cartons won’t be restored after you sign out. They work normally for now.',
      });
    } else if (failed) {
      toast.notify({
        kind: 'warn', title: `${failed} carton${failed > 1 ? 's' : ''} not saved`,
        message: 'They work normally in this session but won’t be restored after you sign out.',
      });
    }
    // noDieline is intentionally quiet here: the die-detection preflight already
    // told the user, and such a carton is expected to stay canvas-only.
    void noDieline;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.signedIn]);

  const commitVectorCartons = async (items) => {
    if (!items || !items.length) return;
    const pid = (name) => name.replace(/\.pdf$/i, '');
    const added = items.map(({ file, name, count, libraryId, detection, prepress }) => {
      const planned = pid(name) in pendingPlanCounts ? pendingPlanCounts[pid(name)] : null;
      return {
        id: `${name}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        // libraryId is what makes a carton restorable across a session. Library
        // loads arrive with one; locally-dropped PDFs get one from
        // persistDroppedCartons() below, which uploads the bytes so there is
        // something to restore FROM.
        libraryId: libraryId || null,
        file, name, count: planned != null ? planned : Math.max(1, count || 1),
        // PR ⑬b: kept on the bench so the rail can decide whether to OFFER the
        // prepress tag (a finished dieline can't be tagged). restoreCartons()
        // already carried this; commit dropped it, so the two paths disagreed
        // about what a bench carton knows about itself.
        detection: detection || null,
        // Set at drop time in the overlay (D4=c); null for an untagged carton.
        prepress: isModeOfferable(prepress) ? prepress : null,
      };
    });
    const fromPlan = added.filter((c) => pid(c.name) in pendingPlanCounts).map((c) => c.name);
    // One card per carton (benchMerge.js): the same file again adds copies to
    // the card already there; another file with the same name is refused here
    // rather than at layout time (DuplicateCartonName). Digests are taken now,
    // for the cards already on the bench too, so the comparison is by bytes.
    const withDigest = async (c) => (c.digest || !c.file ? c : { ...c, digest: await fileDigest(c.file) });
    const [benchNow, incoming] = await Promise.all([
      Promise.all(vecFiles.map(withDigest)), Promise.all(added.map(withDigest)),
    ]);
    const outcome = mergeIntoBench(benchNow, incoming);
    setVecFiles(outcome.bench);
    // Persist any locally-dropped PDF so it survives sign-out (best-effort,
    // non-blocking — the carton is fully usable this session either way).
    // Only NEW cards: copies added to a card change nothing to save.
    persistDroppedCartons(outcome.added);
    setVecResult(null);
    setVecError(null);
    const summary = benchAddSummary(outcome);
    if (fromPlan.length) summary.message += ` Plan counts applied to ${fromPlan.length}.`;
    toast.notify(summary);

    // Carry RGB decisions (keyed by source_id) so runVectorLayout filters re-exports.
    const decisions = {};
    items.forEach((it) => {
      const sid = it.detection?.source_id || it.name.replace(/\.pdf$/i, '');
      if (it.decision) decisions[sid] = it.decision;
    });
    if (Object.keys(decisions).length) setVecDecisions((prev) => ({ ...prev, ...decisions }));

    // Register the detected dies as planner SKUs (reuse the overlay's detections).
    try {
      const skus = items.map((it) => skuFromVectorSource(it.detection)).filter(Boolean);
      if (skus.length) {
        await upsertSkus({ apiBase: API_BASE, skus });
        const ids = skus.map((s) => s.sku_id);
        setRegisteredSkuIds((prev) => Array.from(new Set([...prev, ...ids])));
        setSkuRegStatus({ kind: 'ok', text: `Registered: ${ids.join(', ')}. Available in the Job Planner.` });
      }
    } catch (e) {
      if (e instanceof PlannerApiError && (e.status === 500 || e.status === 503)) {
        setSkuRegStatus({ kind: 'warn', text: 'Die auto-registration is temporarily unavailable; SKUs will register after you run the layout.' });
      }
      // 404 / network: silent — register-after-/export/vector still covers it.
    }
  };

  /**
   * Put cartons on the editor's desk — bytes, prototype and (for a prepress
   * blank) its report entry, together.
   *
   * ONE door, so every path that adds a carton agrees about what a carton on
   * the desk is: the immediate add, "place as drawn", and "add flaps with
   * prepress" all arrive here. `entries` are
   * `{ carton, blank, reportEntry }` — blank and entry null for a plain die.
   *
   * The bytes and the prototype land in the same commit deliberately. Split
   * across two, a carton the operator skipped at the flap prompt would sit in
   * `editorFiles` with nothing in the palette to place it with.
   */
  const placeOnEditorDesk = (entries) => {
    const usable = (entries || []).filter((e) => e?.carton);
    if (!usable.length) return;

    setEditorFiles((prev) => [...prev, ...usable.map((e) => e.carton)]);

    setEditorState((st) => {
      // No document yet — the operator came straight to the editor rather than
      // through a nest. Give them the sheet the Layout rail is set to, which is
      // the one they last thought about.
      const base = st || editorInit(createEditorDoc({
        width: Number(shW) || 0,
        height: Number(shH) || 0,
        kerf: parseFloat(vecKerf) || 0,
        margins: effectiveMargins,
      }));
      let doc = base.doc;
      for (const { carton, blank, reportEntry } of usable) {
        doc = addSourceCarton(doc, carton.detection, {
          label: sourceIdOf(carton), blank, reportEntry,
        }).doc;
      }
      // Through the history, ALWAYS — including the first carton on an empty
      // desk. Two things depend on it: the operator can undo an add they did
      // not mean, and `openInEditor` decides whether to warn about replacing
      // this desk by asking whether the history is empty. Seeding the state
      // with the carton already in it would make a desk with work on it look
      // untouched, and a nest would then quietly take it away.
      return editorApply(base, doc);
    });

    // Best-effort library persistence, exactly as the layout bench gets: a
    // carton dropped on the editor's desk is as worth keeping as one dropped on
    // the other, and the upload is idempotent by content (§5.3).
    persistDroppedCartons(usable.map((e) => e.carton), setEditorFiles);
  };

  /* ── The flap question ────────────────────────────────────────────────
   *
   * A carton reaching the desk from an upload or the library has had no
   * prepress run on it, so an unflapped base placed as drawn is a die with no
   * glue, tuck or dust flap — cut as a carton that cannot be made up, with
   * nothing downstream to catch it. `flapsMissing` spots the shape of that
   * case and the operator answers it, one carton at a time. See
   * EditorFlapPrompt.jsx for why it is a question and not a transform. */
  const [flapQueue, setFlapQueue] = useState([]);     // cartons awaiting an answer
  const [flapType, setFlapType] = useState(null);     // the family chosen for the head
  const [flapBusy, setFlapBusy] = useState(false);
  const [flapErr, setFlapErr] = useState(null);
  const [flapDescribed, setFlapDescribed] = useState(null);   // parseDescribe result

  /**
   * Clear the per-carton answer and move to the next one.
   *
   * The family is re-seeded from the NEW head, not blanked. Clearing it would
   * make the second carton in a batch arrive with no type chosen and its
   * primary button disabled, even where only one family takes its corner count
   * — a dead prompt the operator has to work out how to revive.
   */
  const nextFlapCarton = () => {
    setFlapQueue((q) => {
      const rest = q.slice(1);
      setFlapType(rest[0]?.cartonType || null);
      return rest;
    });
    setFlapErr(null);
    setFlapDescribed(null);
  };

  /**
   * Commit staged cartons to the EDITOR'S desk.
   *
   * Same overlay, same detection, different destination — and deliberately not
   * the same function. `commitVectorCartons` does three things the editor must
   * not: it clears `vecResult` (the editor's sheet is not that nest and has no
   * business invalidating it), it applies pending plan counts (the editor
   * places pieces one at a time; a "copies" number has nowhere to go), and it
   * registers planner SKUs off the back of a layout the planner will never see.
   *
   * What it DOES share is the part that matters — the bytes are kept, and the
   * detected SourceResult goes into the document, so an added carton exports
   * through the identical `{source_id → file part}` path a nested one does.
   */
  const commitEditorCartons = (items) => {
    if (!items || !items.length) return;
    const added = items.map(({ file, name, count, libraryId, detection, prepress }) => ({
      id: `${name}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      libraryId: libraryId || null,
      file,
      name,
      count: Math.max(1, count || 1),
      detection: detection || null,
      prepress: isModeOfferable(prepress) ? prepress : null,
    }));

    // A carton already on the desk is not added again. The palette is keyed by
    // source_id and so is the export's file map, so a second row for the same
    // die would be two ways to place one carton and two candidate File objects
    // for one part — with nothing on screen to tell them apart.
    const onDesk = new Set([
      ...editorFiles.map((c) => sourceIdOf(c)),
      ...flapQueue.map((q) => q.sourceId),
    ]);
    const fresh = added.filter((c) => !onDesk.has(sourceIdOf(c)));
    const dupes = added.length - fresh.length;

    // A carton whose die was never detected can be held but not placed: the
    // palette has no outline to draw and the sheet has nothing to check. Said
    // rather than silently dropped, because the operator watched it upload.
    const placeable = fresh.filter((c) => (c.detection?.vertices || []).length >= 3);
    const undetected = fresh.length - placeable.length;

    // THE FLAP CHECK, before anything reaches the sheet. Asking after the die
    // is placed would mean swapping the geometry under a piece the operator has
    // already positioned; asking first means the carton arrives once, correct.
    const asking = [];
    const ready = [];
    for (const c of placeable) {
      const missing = flapsMissing(c.detection.vertices, prepressCaps);
      if (missing) asking.push({ ...missing, sourceId: sourceIdOf(c), carton: c });
      else ready.push({ carton: c, blank: null, reportEntry: null });
    }

    placeOnEditorDesk(ready);
    if (asking.length) {
      setFlapQueue((q) => {
        // Only seed the family when this batch supplies the HEAD. Appending to
        // a queue already being answered must not re-point the choice under the
        // carton on screen.
        if (!q.length) setFlapType(asking[0].cartonType || null);
        return [...q, ...asking];
      });
    }

    const notes = [
      undetected ? `${undetected} had no detected die and can't be placed` : null,
      dupes ? `${dupes} already on the desk` : null,
      asking.length ? `${asking.length} need${asking.length === 1 ? 's' : ''} flaps — see the prompt` : null,
    ].filter(Boolean);
    if (ready.length || notes.length) {
      toast.notify({
        kind: (undetected || (!ready.length && !asking.length)) ? 'warn' : 'success',
        title: ready.length
          ? `${ready.length} carton${ready.length === 1 ? '' : 's'} on the editor's desk`
          : 'Nothing added yet',
        message: [ready.map((e) => e.carton.name).join(', '), ...notes].filter(Boolean).join(' · '),
      });
    }
  };

  /**
   * Ask prepress for this carton's blank, then place it.
   *
   * Two clicks, one handler: the first (no `described`) fetches, the second
   * accepts what came back. Separated so the operator SEES the die before it
   * lands on their sheet — this is the one moment the flapped outline can still
   * be rejected, and a blank that silently arrives unflapped (`blankHasFlaps`
   * false) is exactly what §6 verification exists to catch.
   *
   * `plain` mode, one carton, no sheet: sub-second, and the same
   * `apply_prepress` a nest runs, so the blank placed here is the blank a
   * re-run would produce.
   */
  const addFlapsToPending = async (cartonType, described) => {
    const head = flapQueue[0];
    if (!head) return;

    if (described) {
      // `entry` IS the report entry, spread — the same shape `prepress_report`
      // carries, which is what makes the artwork chain resolvable at export.
      placeOnEditorDesk([{
        carton: head.carton,
        blank: described.entry?.assets?.blank?.vertices_mm || null,
        reportEntry: described.entry,
      }]);
      nextFlapCarton();
      return;
    }

    const body = describeRequest({
      vertices: head.carton.detection.vertices,
      cartonType,
      offeredTypes: prepressCaps?.cartonTypes || [],
      // Filed under the carton's own name, because the entry is KEPT: the
      // report is looked up by name at placement and at export.
      name: head.sourceId,
    });
    if (!body) { setFlapErr(new Error('This carton has no detected outline to describe.')); return; }

    setFlapBusy(true); setFlapErr(null);
    try {
      const res = parseDescribe(await describePrepressCarton({ apiBase: API_BASE, body }));
      const blank = res?.entry?.assets?.blank?.vertices_mm;
      if (!Array.isArray(blank) || blank.length < 3) {
        // The package ran but published no drawable blank (pre-0.13.0 `assets`).
        // Nothing to place — and synthesising one from the base outline is the
        // guess this codebase refuses everywhere else.
        setFlapErr(new Error(
          'Prepress ran but this server published no drawable blank for it, so there is '
          + 'nothing to place. Nest the carton in Auto Imposer instead, where the '
          + 'flapped die comes back with the result.'));
        return;
      }
      setFlapDescribed(res);
    } catch (e) {
      setFlapErr(e);
    } finally {
      setFlapBusy(false);
    }
  };

  /** Take the carton exactly as detected — the operator overruling the check. */
  const placePendingAsDrawn = () => {
    const head = flapQueue[0];
    if (!head) return;
    placeOnEditorDesk([{ carton: head.carton, blank: null, reportEntry: null }]);
    nextFlapCarton();
  };

  /** Take a carton off the editor's desk, with everything it put on the sheet. */
  const removeEditorCarton = (sourceId) => {
    if (!sourceId) return;
    setEditorFiles((prev) => prev.filter((c) => sourceIdOf(c) !== sourceId));
    setEditorState((st) => (st?.doc ? editorApply(st, removeSourceCarton(st.doc, sourceId)) : st));
  };

  const updateVecCount = (id, raw) => {
    const n = Math.max(0, parseInt(raw, 10) || 0);
    setVecFiles((prev) => prev.map((c) => (c.id === id ? { ...c, count: n } : c)));
  };

  /**
   * PR ⑬b: tag/untag a carton for prepress.
   *
   * Clears any rendered result, because the previous layout nested a different
   * geometry — a bare 6-corner outline vs a 17-vertex flapped dieline. Leaving
   * the old sheet on screen next to a changed tag is exactly the kind of
   * plausible-but-stale picture that gets a wrong plate cut.
   */
  const updateVecPrepress = (id, mode) => {
    const m = isModeOfferable(mode) ? mode : null;
    setVecFiles((prev) => prev.map((c) => (
      // ⑭d follow-up: THE MARK NO LONGER COMES OFF WITH THE MODE. It used to,
      // because leaving a firm carton outside `auto` was a state the export
      // path rejected — true then, and not true on a deployment where
      // `required_ownership.modes_evaluated` covers plain and pair. A firm
      // order line is a COMMERCIAL fact about the order; the prepress mode is a
      // PRODUCTION decision about the die. Erasing one because the operator
      // changed the other destroys information they entered, and silently.
      // Where the combination genuinely cannot be honoured, `firmLineBlockReason`
      // now says so in terms the operator can act on.
      c.id === id ? { ...c, prepress: m } : c
    )));
    setVecResult(null);
    setVecError(null);
    setPrepressCheck(null);
  };

  /**
   * ⑭d: mark a carton as a firm order line.
   *
   * Same staleness reasoning as the mode: `required` changes which candidate
   * `auto` picks, so the sheet on screen no longer describes what would be
   * produced.
   */
  /**
   * ⑭e: per-carton prepress config.
   *
   * Same staleness reasoning as the mode and the firm mark — config changes
   * the flaps that get drawn, so the sheet on screen no longer describes what
   * would be produced.
   */
  const updateVecConfig = (id, config) => {
    const clean = cleanPrepressConfig(config);
    setVecFiles((prev) => prev.map((c) => (c.id === id ? { ...c, prepressConfig: clean } : c)));
    setVecResult(null);
    setVecError(null);
    setPrepressCheck(null);
  };

  /**
   * ⑭l: set a carton's TYPE — which die gets cut.
   *
   * A manufacturing decision, not a tuning knob, which is why it is deliberately
   * not a `prepress_config` key and why the backend refuses to infer it from
   * geometry. The operator picks; we only order the list so the topology that
   * matches comes first.
   */
  /**
   * 1.11: a carton's SPACING — the clear space it needs around it, mm (0–20).
   * The sheet nests at the largest spacing on it (the engine's kerf is one
   * value per sheet), so the sheet on screen is stale the moment it changes.
   */
  const updateVecSpacing = (id, mm) => {
    const v = clampSpacing(mm);
    setVecFiles((prev) => prev.map((c) => (c.id === id ? { ...c, spacingMm: v } : c)));
    setVecResult(null);
    setVecError(null);
    setPrepressCheck(null);
  };

  const updateVecCartonType = (id, type) => {
    const t = typeof type === 'string' && type ? type : null;
    setVecFiles((prev) => prev.map((c) => (c.id === id ? { ...c, cartonType: t } : c)));
    setVecResult(null);
    setVecError(null);
    setPrepressCheck(null);
  };

  const updateVecRequired = (id, on) => {
    setVecFiles((prev) => prev.map((c) => (c.id === id ? { ...c, required: on === true } : c)));
    setVecResult(null);
    setVecError(null);
    setPrepressCheck(null);
  };

  const removeVecFile = (id) => {
    setVecFiles((prev) => prev.filter((c) => c.id !== id));
    setVecResult(null);
  };


  /** Run the vector nesting call (/export/vector). */
  /**
   * Register vector-detected dies as planner SKUs (Option A / trigger #2).
   * Maps each /export/vector source's `vertices` (already CCW Y-up local-origin
   * mm — the planner's die_polygon contract) to a SKU and upserts them.
   * Idempotent server-side (by sku_id), and best-effort: failures are surfaced
   * quietly without disturbing the layout flow.
   */
  const registerSkusFromVector = async (result) => {
    const skus = skusFromVectorResult(result);
    if (!skus.length) return;
    setSkuRegStatus({ kind: 'busy', text: `Registering ${skus.length} SKU(s)…` });
    try {
      await upsertSkus({ apiBase: API_BASE, skus });
      const ids = skus.map((s) => s.sku_id);
      setRegisteredSkuIds((prev) => Array.from(new Set([...prev, ...ids])));
      setSkuRegStatus({ kind: 'ok', text: `Registered as SKU(s): ${ids.join(', ')}. Available in the Job Planner.` });
    } catch (e) {
      // Non-fatal — the layout/export still works without the registry.
      setSkuRegStatus({
        kind: 'warn',
        text: `Could not auto-register SKUs (${e instanceof PlannerApiError ? e.message : String(e)}). You can retry from the Job Planner.`,
      });
    }
  };

  /** Manually (re-)register a single vector source as a SKU. */
  const registerOneSku = async (source) => {
    const sku = skuFromVectorSource(source);
    if (!sku) { setSkuRegStatus({ kind: 'warn', text: 'No usable die polygon to register for this carton.' }); return; }
    setSkuRegStatus({ kind: 'busy', text: `Registering ${sku.sku_id}…` });
    try {
      await upsertSkus({ apiBase: API_BASE, skus: [sku] });
      setRegisteredSkuIds((prev) => Array.from(new Set([...prev, sku.sku_id])));
      setSkuRegStatus({ kind: 'ok', text: `Registered ${sku.sku_id} as a SKU.` });
    } catch (e) {
      setSkuRegStatus({ kind: 'warn', text: `Could not register ${sku.sku_id} (${e instanceof PlannerApiError ? e.message : String(e)}).` });
    }
  };

  // ── Job Planner handlers (state lives in App; see lifted state above) ──

  const plRefreshSkus = useCallback(async () => {
    try {
      const s = await listSkus({ apiBase: API_BASE });
      setPlSkus(Array.isArray(s) ? s : (s?.skus || []));
    } catch { /* best-effort listing */ }
  }, []);

  const plRefreshOrderLines = useCallback(async () => {
    try {
      const ol = await listOrderLines({ apiBase: API_BASE });
      const arr = Array.isArray(ol) ? ol : (ol?.order_lines || []);
      if (arr.length) setPlOrderLines(arr);
    } catch { /* best-effort */ }
  }, []);

  // Load SKUs/order lines once on mount so the planner is populated even before
  // the user opens it (and after SKUs get registered from the vector flow).
  useEffect(() => { plRefreshSkus(); plRefreshOrderLines(); }, [plRefreshSkus, plRefreshOrderLines]);
  // Re-pull SKUs whenever a registration happens, so new ones appear in the picker.
  useEffect(() => { if (registeredSkuIds.length) plRefreshSkus(); }, [registeredSkuIds, plRefreshSkus]);

  const plAddOrderLine = async (line) => {
    setPlBusy(true); setPlError(null);
    try {
      // The response echoes the stored line WITH its server-assigned line_id.
      // Capture that — it's what lets /plan reference the line by id (§6.3,
      // Scenario B) and carry its saved tolerances without re-sending them.
      const resp = await addOrderLines({ apiBase: API_BASE, lines: [line] });
      const stored = Array.isArray(resp) ? resp[0]
        : (resp?.order_lines?.[0] || resp?.[0] || null);
      const merged = stored ? { ...line, ...stored } : line;  // prefer server fields (line_id, defaults)
      setPlOrderLines((prev) => {
        const i = prev.findIndex((l) => l.sku_id === merged.sku_id);
        if (i >= 0) { const next = [...prev]; next[i] = { ...next[i], ...merged }; return next; }
        return [...prev, merged];
      });
      setPlDraftSku(''); setPlDraftQty(''); setPlDraftDue('');
      setPlDraftOverTol(''); setPlDraftUnderTol('');
    } catch (e) {
      setPlError(e instanceof PlannerApiError ? e.message : String(e));
    } finally { setPlBusy(false); }
  };

  const plUploadCsv = async (file) => {
    if (!file) return;
    setPlBusy(true); setPlError(null); setPlCsvWarnings([]);
    try {
      const res = await uploadOrderLinesCsv({ apiBase: API_BASE, file });
      setPlCsvWarnings(res.warnings || []);
      await plRefreshOrderLines();
    } catch (e) {
      setPlError(e instanceof PlannerApiError && e.status === 422 ? `CSV rejected: ${e.message}` : (e instanceof PlannerApiError ? e.message : String(e)));
    } finally { setPlBusy(false); }
  };

  // Build the shared /plan request args (scenarios A/B/C, §6.3) used by both the
  // sync and async paths.
  const buildPlanArgs = () => {
    const sheet = { width: Number(plSheet.w) || 0, height: Number(plSheet.h) || 0 };
    const g = parseFloat(plSheet.gripper) || 0;
    if (g > 0) { sheet.margin_bottom = g; sheet.margin_top = g; sheet.margin_left = g; sheet.margin_right = g; }

    const bySku = new Map();
    for (const ol of plOrderLines) {
      const prev = bySku.get(ol.sku_id);
      if (!prev) { bySku.set(ol.sku_id, { ...ol }); continue; }
      prev.quantity = (prev.quantity || 0) + (ol.quantity || 0);
      if (!prev.line_id && ol.line_id) prev.line_id = ol.line_id;
    }
    const lineIds = []; const orderLines = [];
    for (const ol of bySku.values()) {
      if (ol.line_id) { lineIds.push(ol.line_id); }
      else {
        const inline = { sku_id: ol.sku_id, quantity: ol.quantity };
        if (ol.over_tolerance_pct != null) inline.over_tolerance_pct = ol.over_tolerance_pct;
        if (ol.under_tolerance_pct != null) inline.under_tolerance_pct = ol.under_tolerance_pct;
        if (ol.due_date != null) inline.due_date = ol.due_date;
        if (ol.moq != null) inline.moq = ol.moq;
        if (ol.priority != null) inline.priority = ol.priority;
        orderLines.push(inline);
      }
    }
    return {
      sheet,
      orderLines: orderLines.length ? orderLines : undefined,
      lineIds: lineIds.length ? lineIds : undefined,
    };
  };

  // Map a PlannerApiError to user-facing text for the plan calls (§7).
  const planErrorText = (e) => {
    if (e instanceof PlannerApiError && e.status === 400) {
      return `${e.message}\nIf a referenced order line was removed on the backend, re-add it; otherwise register the SKU (from Vector Export) or include it inline.`;
    }
    if (e instanceof PlannerApiError && e.status === 422) return e.message;
    if (e instanceof PlannerApiError && e.status === 503) return 'Planning is temporarily unavailable (nesting engine down). Retry shortly.';
    return e instanceof PlannerApiError ? e.message : String(e);
  };

  // Async planning (Contract v1.2): start a background job, then poll for
  // progress + result, with a cancel button. The actual polling runs in an
  // effect keyed off plJobUrls; this handler just kicks it off.
  const plComputePlan = async () => {
    if (!plOrderLines.length) { setPlError('Add at least one order line first.'); return; }
    // v1.3 §4: do NOT clear plPlan here. The previous plan stays (dimmed in the
    // panel) while the new job runs, so a cancel/failure leaves the user exactly
    // what they had before they started planning. It's replaced only on success.
    setPlBusy(true); setPlError(null); setPlJob(null); setPlCancelling(false);
    const trayId = jobs.startJob({ kind: 'plan', label: 'Computing plans', message: 'Queued…', onCancel: () => plCancel() });
    try {
      const { sheet, orderLines, lineIds } = buildPlanArgs();
      // /plan/async validates inputs synchronously (400/503) before a job exists.
      const started = await startPlanAsync({ apiBase: API_BASE, orderLines, lineIds, sheet });
      // Seed an initial job snapshot so the UI shows "queued" immediately.
      setPlJob({ job_id: started.job_id, status: started.status || 'queued', progress: null, result: null, cancel_requested: false });
      setPlJobUrls({ poll: started.poll, cancel: started.cancel, jobId: started.job_id, trayId });
      // plBusy stays true; the poll effect clears it on a terminal state.
    } catch (e) {
      setPlError(planErrorText(e));
      setPlBusy(false);
      jobs.endJob(trayId, { status: 'failed', error: planErrorText(e) });
    }
  };

  // Cancel the running job (v1.3: interrupts the engine mid-computation; terminal
  // `cancelled` typically arrives < 1 s later via the poll). Fire-and-forget-then-
  // poll: this call only REQUESTS the stop — the 202 body is a snapshot (status
  // still 'running', cancel_requested true). Never flip the UI to "cancelled"
  // here; the poll loop delivers the real terminal status, which may still be
  // 'succeeded' if the cancel raced completion (§5).
  const plCancel = async () => {
    const urls = plJobUrlsRef.current;    // ref, not state: tray closures predate setPlJobUrls
    if (!urls) return;
    setPlCancelling(true);
    if (urls.trayId) jobs.updateJob(urls.trayId, { message: 'Cancelling…' });
    try {
      const state = await cancelPlanJob({ apiBase: API_BASE, cancelUrl: urls.cancel, jobId: urls.jobId });
      setPlJob(state);   // snapshot: cancel_requested=true → the panel button shows "Cancelling…"
      // Keep polling — terminal status arrives through the normal poll path.
    } catch (e) {
      if (e instanceof PlannerApiError && e.status === 404) {
        // §5: the job already finished (token released) — the cancel was simply
        // too late. Not an error; the running poll delivers the real terminal
        // status (likely 'succeeded'). Leave everything to it.
      } else {
        // Couldn't deliver the cancel (network etc.). Job keeps running; make
        // the button usable again and say so quietly.
        setPlCancelling(false);
        if (urls.trayId) jobs.updateJob(urls.trayId, { message: 'Cancel not delivered — still running.' });
        toast.notify({ kind: 'info', title: "Couldn't send cancel", message: 'The job is still running. You can try again.' });
      }
    }
  };

  // Poll the active plan job until it reaches a terminal state (§2, §6).
  // Cadence ~700ms (contract suggests 0.5–1s). Cleans up on unmount or when the
  // job changes. Branches on FINAL status so a cancel that raced completion still
  // surfaces the succeeded result.
  useEffect(() => {
    if (!plJobUrls) return undefined;
    let stopped = false;
    let timer = null;
    // ⑭i: cadence comes from pollDelayMs — see exportVector.js. Was a bare 700
    // here and in the two layout loops, which is three copies of one decision.
    const startedAt = Date.now();

    const tick = async () => {
      try {
        const job = await pollPlanJob({ apiBase: API_BASE, pollUrl: plJobUrls.poll, jobId: plJobUrls.jobId });
        if (stopped) return;
        setPlJob(job);
        const tid = plJobUrls.trayId;
        const terminal = isPlanTerminal(job.status);
        // §6.3: on a durable replay (persisted:true) `progress` is a zeroed
        // terminal stub — never let it drive the live progress bar.
        const persisted = isPersistedRecord(job);
        if (tid) jobs.updateJob(tid, {
          fraction: persisted ? null : (job.progress?.fraction ?? null),
          message: job.cancel_requested && !terminal ? 'Cancelling…' : (persisted ? job.status : (job.progress?.message || job.status)),
        });

        if (terminal) {
          // Terminal: stop polling and resolve the outcome.
          if (job.status === 'succeeded' && job.result) {
            setPlPlan(job.result);                 // same PlanResponse as sync
            if (tid) jobs.endJob(tid, { status: 'succeeded', message: 'Plan options ready.' });
            toast.notify({ kind: 'success', title: 'Plans ready', message: 'Open the Plan tab to review and apply.' });
          } else if (job.status === 'cancelled') {
            setPlError(null);                      // not an error; UI shows "cancelled"
            if (tid) jobs.endJob(tid, { status: 'cancelled', message: 'Cancelled.' });
            toast.notify({ kind: 'info', title: 'Planning cancelled' });
          } else {
            // failed · interrupted · anything unrecognised (§6.2). `interrupted`
            // is a server restart, not a user error — say so, and offer a re-run.
            const msg = terminalStatusMessage(job.status, job.error);
            setPlError(msg);
            if (tid) jobs.endJob(tid, { status: 'failed', error: msg });
            toast.notify({
              kind: job.status === 'interrupted' ? 'warn' : 'error',
              title: job.status === 'interrupted' ? 'Server restarted' : 'Planning failed',
              message: msg,
            });
          }
          setPlBusy(false);
          setPlCancelling(false);
          setPlJobUrls(null);                      // tear down the loop
          return;
        }
        timer = setTimeout(tick, pollDelayMs(Date.now() - startedAt));
      } catch (e) {
        if (stopped) return;
        const tid = plJobUrls.trayId;
        // §6.3: finished jobs no longer 404 — a durable record answers instead.
        // So a 404 now means a genuinely unknown id (wrong job, or another
        // user's — existence is not revealed, §6.4).
        if (e instanceof PlannerApiError && e.status === 404) {
          setPlError('This planning job could not be found. Run the plan again.');
          if (tid) jobs.endJob(tid, { status: 'failed', error: 'Job not found — run the plan again.' });
        } else {
          setPlError(e instanceof PlannerApiError ? e.message : String(e));
          if (tid) jobs.endJob(tid, { status: 'failed', error: e instanceof PlannerApiError ? e.message : String(e) });
        }
        setPlBusy(false);
        setPlCancelling(false);
        setPlJobUrls(null);
      }
    };

    tick();
    return () => { stopped = true; if (timer) clearTimeout(timer); };
    // jobs/toast are stable context refs; this poll is intentionally keyed on plJobUrls only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plJobUrls]);
  const cfgLoad = useCallback(async () => {
    setCfgBusy(true); setCfgError(null);
    try {
      const c = await getConfig({ apiBase: API_BASE });
      setCfg(c); setCfgDraft({ ...c });
    } catch (e) {
      setCfgError(e instanceof PlannerApiError ? e.message : String(e));
    } finally { setCfgBusy(false); }
  }, []);

  // Load config the first time the user opens the Settings tab.
  useEffect(() => { if (plTab === 'settings' && !cfg && !cfgError) cfgLoad(); }, [plTab, cfg, cfgError, cfgLoad]);

  const cfgSave = async () => {
    if (!cfgDraft || !cfg) return;
    // Send ONLY changed fields (brief §4.1).
    const patch = {};
    for (const f of CONFIG_FIELDS) {
      if (cfgDraft[f.key] !== cfg[f.key]) patch[f.key] = cfgDraft[f.key];
    }
    if (!Object.keys(patch).length) return;
    setCfgBusy(true); setCfgError(null);
    try {
      const updated = await patchConfig({ apiBase: API_BASE, patch });
      setCfg(updated); setCfgDraft({ ...updated }); setCfgSavedAt(Date.now());
    } catch (e) {
      // Out-of-range values return 422 (brief §4.2) — surface the message.
      setCfgError(e instanceof PlannerApiError ? e.message : String(e));
    } finally { setCfgBusy(false); }
  };

  const cfgReset = async () => {
    setCfgBusy(true); setCfgError(null);
    try {
      const def = await resetConfig({ apiBase: API_BASE });
      setCfg(def); setCfgDraft({ ...def }); setCfgSavedAt(Date.now());
    } catch (e) {
      setCfgError(e instanceof PlannerApiError ? e.message : String(e));
    } finally { setCfgBusy(false); }
  };

  // ── PR ④: workspace persistence (hydration · presets · ui-state autosave) ──

  // Admin gate for the shop-config editor (§4). Signed OUT ⇒ pre-flip open path,
  // leave editable (the backend still allows it before the flag flips, and after
  // the flip authFetch demands sign-in anyway). Signed IN ⇒ only admins/superusers
  // may edit; everyone else gets a read-only form.
  const isAdmin = !!auth.user && (auth.user.role === 'admin' || auth.user.is_superuser === true);
  const canEditConfig = !auth.signedIn || isAdmin;

  // Assemble the COMPLETE preset dict from live form state (§2: PUT replaces the
  // whole object, so we always send the full set — sanitize drops anything the
  // backend would 422 on and clamps ranges).
  const collectPresets = useCallback(() => sanitizePresets({
    sheet_w_mm: Number(shW) || 0,
    sheet_h_mm: Number(shH) || 0,
    margin_top_mm: effectiveMargins.top,
    margin_right_mm: effectiveMargins.right,
    margin_bottom_mm: effectiveMargins.bottom,
    margin_left_mm: effectiveMargins.left,
    kerf_mm: parseFloat(vecKerf) || 0,
  }), [shW, shH, effectiveMargins, vecKerf]);

  // Apply a saved preset dict onto the forms (§1 "restore the form from presets").
  // Only keys actually present are applied, so a brand-new user's {} leaves the
  // app defaults untouched. Sheet + margins also seed the Planner's simplified
  // sheet form so BOTH surfaces reflect the remembered defaults.
  const applyPresets = useCallback((raw) => {
    const p = sanitizePresets(raw);
    if (p.sheet_w_mm != null) setShW(p.sheet_w_mm);
    if (p.sheet_h_mm != null) setShH(p.sheet_h_mm);

    const m = {
      top: p.margin_top_mm, right: p.margin_right_mm,
      bottom: p.margin_bottom_mm, left: p.margin_left_mm,
    };
    const anyMargin = Object.values(m).some((v) => v != null);
    if (anyMargin) {
      const sides = {
        top: m.top ?? 0, right: m.right ?? 0, bottom: m.bottom ?? 0, left: m.left ?? 0,
      };
      setMarginSides(sides);
      const uniform = sides.top === sides.right && sides.right === sides.bottom && sides.bottom === sides.left;
      if (uniform) { setMarginMode('uniform'); setMarginUniform(sides.top); }
      else { setMarginMode('perSide'); }
    }
    if (p.kerf_mm != null) setVecKerf(String(p.kerf_mm));

    // Seed the Planner sheet form too (its single "gripper" ≈ a uniform margin).
    setPlSheet((s) => {
      const next = { ...s };
      if (p.sheet_w_mm != null) next.w = p.sheet_w_mm;
      if (p.sheet_h_mm != null) next.h = p.sheet_h_mm;
      if (anyMargin) {
        const uniform = (m.top ?? 0) === (m.right ?? 0) && (m.right ?? 0) === (m.bottom ?? 0) && (m.bottom ?? 0) === (m.left ?? 0);
        if (uniform) next.gripper = String(m.top ?? 0);
      }
      return next;
    });
  }, []);

  // ── Session restore helpers (Cloud feedback §1/§2) ────────────────────────

  // One latch shared by BOTH restore paths — `recent_jobs` recovery below and
  // `active_jobs` reattach further down. It is what actually guarantees a job
  // is never driven by two poll loops, so it must be declared before the first
  // consumer rather than next to the second.
  const reattachedRef = useRef(new Set());

  /**
   * Rebuild the carton bench from library references saved in ui_state.
   *
   * Only library-backed cartons can come back: a locally-dropped PDF lives as a
   * File in the tab that dropped it, and the server never received the bytes.
   * Rather than silently losing those, `localOnly` tells the user how many need
   * re-adding — an honest gap beats a mysteriously shorter list.
   */
  const restoreCartons = useCallback(async (refs, localOnly, opts = {}) => {
    if (!Array.isArray(refs) || !refs.length) {
      if (localOnly > 0) {
        toast.notify({
          kind: 'info', title: 'Cartons not restored',
          message: `${localOnly} carton${localOnly > 1 ? 's were' : ' was'} added from your computer, so ${localOnly > 1 ? 'they' : 'it'} can’t be restored. Save cartons to the library to keep them across sessions.`,
        });
      }
      return { restored: 0, missing: [], missingNames: [] };
    }
    const restored = [];
    const missing = [];
    const missingNames = [];
    // #88: a reopened job reads its cartons from where `opts.via` says — a
    // saved job's own routes, or the Library with deleted cartons included —
    // so a carton deleted since still comes back. The bench put back after a
    // refresh reads the Library as it always has.
    const via = opts.via || null;
    for (const ref of refs) {
      if (!ref?.libraryId) continue;
      try {
        const full = via
          ? await fetchCarton({ apiBase: API_BASE, id: ref.libraryId, via })
          : await getCarton({ id: ref.libraryId });
        const filename = full.geometry?.filename || full.filename || `${full.name || full.id}.pdf`;
        const file = via
          ? await fetchCartonSource({ apiBase: API_BASE, id: full.id, filename, via })
          : await getCartonSource({ id: full.id, filename });
        restored.push({
          id: `${filename}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          libraryId: full.id, file, name: filename,
          // #88: it works for this job, and the bench says it is gone from the library
          deleted: !!full.deleted_at,
          count: Math.max(1, Number(ref.count) || 1),
          detection: full.geometry,
          // PR ⑬b (ui_state v6): the export-path tag is per-request, so the
          // bench has to remember it. Absent on a v5 blob → untagged, which is
          // exactly the pre-prepress behaviour.
          prepress: isModeOfferable(ref.prepress) ? ref.prepress : null,
          // ⑭e (v7): the firm mark and the config overrides are per-request
          // too. The `&& mode === 'auto'` clamp is GONE with its twin in
          // updateVecPrepress: which modes honour a firm line is a deployment
          // setting, so a stored blob must not be silently rewritten against a
          // rule this client hard-codes. Restore what the operator saved and
          // let the capability-derived blocker judge it.
          required: ref.required === true,
          prepressConfig: (ref.prepress_config && typeof ref.prepress_config === 'object'
            && !Array.isArray(ref.prepress_config)) ? ref.prepress_config : null,
          // ⑭l (v8): the carton type. Restored only when this deployment still
          // OFFERS it — `carton_types` has both widened and narrowed in this
          // project's life, and a blob written when IL was accepted must not
          // put an IL carton back on a bench that would have it refused.
          // Absent on a v7 blob → unset → BL, which is the pre-⑭l behaviour.
          cartonType: (typeof ref.carton_type === 'string'
            && offerableCartonTypes(prepressCaps).includes(ref.carton_type))
            ? ref.carton_type : null,
          // 1.11 (v9): the carton's spacing. Absent on older blobs -> 0, the
          // pre-spacing behaviour exactly.
          spacingMm: clampSpacing(ref.spacing_mm),
        });
      } catch {
        missing.push(ref.libraryId);   // deleted, unshared, or no longer visible
        missingNames.push(ref.name || ref.libraryId);
      }
    }
    if (restored.length) {
      setVecFiles(restored);
      // Re-register the restored dies as planner SKUs, same as a fresh add.
      try {
        const skus = restored.map((c) => skuFromVectorSource(c.detection)).filter(Boolean);
        if (skus.length) {
          await upsertSkus({ apiBase: API_BASE, skus });
          setRegisteredSkuIds((prev) => Array.from(new Set([...prev, ...skus.map((s) => s.sku_id)])));
        }
      } catch { /* best-effort: a registry hiccup must not block the restore */ }
    }
    const notes = [];
    if (restored.length) notes.push(`${restored.length} carton${restored.length > 1 ? 's' : ''} restored`);
    if (missing.length) notes.push(`${missing.length} no longer available`);
    if (localOnly > 0) notes.push(`${localOnly} local upload${localOnly > 1 ? 's' : ''} can’t be restored`);
    if (notes.length && !opts.silent) {
      toast.notify({ kind: missing.length || localOnly ? 'warn' : 'success', title: 'Session restored', message: `${notes.join(' · ')}.` });
    }
    return { restored: restored.length, missing, missingNames };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Restore a job that finished while the user was signed out.
   *
   * Backend reply #3 §1: hydration now carries `recent_jobs` — the last 5
   * TERMINAL jobs per kind for this user. That replaces the previous workaround
   * of stashing `lastLayoutJobId`/`lastPlanJobId` in `ui_state`, which had two
   * defects the server-side list fixes outright: the 2.5 s autosave debounce
   * could lose the id entirely if the user left immediately, and only one job
   * per kind could ever be remembered.
   *
   * `recent_jobs` and `active_jobs` are disjoint by construction, so no
   * exclusion pass is needed. We still mark each recovered id in the SHARED
   * `reattachedRef` latch — not because the guarantee is doubted, but because
   * that latch is what actually prevents two poll loops on one job, and reusing
   * it costs nothing.
   */
  const recoverFinishedJobs = useCallback(async (recentJobs) => {
    const { layout, plan } = pickRecoverableJobs(recentJobs);

    if (layout && !reattachedRef.current.has(layout.job_id)) {
      reattachedRef.current.add(layout.job_id);
      try {
        // Follow the payload `poll` URL — never construct a job path.
        const job = await pollJob({ apiBase: API_BASE, pollUrl: layout.poll, jobId: layout.job_id });
        if (job?.status === 'succeeded' && job.result) {
          setVecResult(job.result);
          setVecJobId(layout.job_id);       // keeps the approval badge attached
          toast.notify({ kind: 'success', title: 'Layout recovered', message: 'Your last layout finished and has been restored.' });
        }
      } catch { /* gone or not ours (404) — nothing to recover */ }
    }

    if (plan && !reattachedRef.current.has(plan.job_id)) {
      reattachedRef.current.add(plan.job_id);
      try {
        const job = await pollPlanJob({ apiBase: API_BASE, pollUrl: plan.poll, jobId: plan.job_id });
        if (job?.status === 'succeeded' && job.result) {
          setPlJob(job);
          setPlPlan(job.result);
          toast.notify({ kind: 'success', title: 'Plans recovered', message: 'Your last planning run finished — open the Job Planner to review.' });
        }
      } catch { /* gone or not ours — nothing to recover */ }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Hydrate ONCE per signed-in user (§1). Fires for interactive sign-in AND for a
  // silently resumed session on refresh (both set auth.user). Best-effort: a 404
  // means the backend predates PR ④ — mark unsupported and carry on unchanged.
  useEffect(() => {
    const uid = auth.user?.id || null;
    currentUidRef.current = uid;
    if (!auth.signedIn || !uid) return undefined;
    if (hydratedForRef.current === uid) return undefined;   // finished already
    if (hydratingRef.current === uid) return undefined;     // running (StrictMode remount)
    hydratingRef.current = uid;

    (async () => {
      try {
        const ws = await getWorkspace({ apiBase: API_BASE });
        if (currentUidRef.current !== uid) return;   // user changed — discard
        wsSupportedRef.current = true; setWsSupported(true);
        applyPresets(ws.presets);
        // ui_state is our opaque blob — restore the lightweight view state we save.
        const u = ws.ui_state || {};
        // 'editor' is deliberately NOT restorable: its document lives only in
        // memory, so landing there on sign-in would show an empty screen and
        // read as lost work. Fall back to the layout it is opened from.
        if (['layout', 'planner', 'library', 'approvals'].includes(u.workspace)) setWorkspace(u.workspace);
        else if (u.workspace === 'editor') setWorkspace('layout');
        if (u.plTab === 'plan' || u.plTab === 'settings') setPlTab(u.plTab);
        if (typeof u.cfgAdvancedOpen === 'boolean') setCfgAdvancedOpen(u.cfgAdvancedOpen);
        // The live session's sheet beats the saved defaults applied above:
        // presets are "what I usually use", ui_state is "what I was doing".
        if (u.sheet) {
          if (Number(u.sheet.w) > 0) setShW(Number(u.sheet.w));
          if (Number(u.sheet.h) > 0) setShH(Number(u.sheet.h));
          if (Number(u.sheet.kerf) >= 0) setVecKerf(String(u.sheet.kerf));
        }
        // Margins — same precedence rule as the sheet above.
        if (u.margins) {
          if (u.margins.mode === 'uniform' || u.margins.mode === 'perSide') setMarginMode(u.margins.mode);
          if (Number.isFinite(Number(u.margins.uniform))) setMarginUniform(Number(u.margins.uniform));
          const s = u.margins.sides;
          if (s && typeof s === 'object') {
            setMarginSides({
              top: Number(s.top) || 0, right: Number(s.right) || 0,
              bottom: Number(s.bottom) || 0, left: Number(s.left) || 0,
            });
          }
        }
        if (u.decisions && typeof u.decisions === 'object') setVecDecisions(u.decisions);
        if (u.plannerSheet && typeof u.plannerSheet === 'object') {
          setPlSheet((prev) => ({ ...prev, ...u.plannerSheet }));
        }
        if (u.jobContexts && typeof u.jobContexts === 'object') setJobContexts(u.jobContexts);
        const back = readLastRun(u);
        setLastRun(back);
        setReturnedRun(back);
        refreshSavedJobs();
        setRecentCartons(ws.recent_cartons);
        setActiveJobs(ws.active_jobs);   // §2A: offered in the tray, not auto-attached

        // Put the bench back: re-fetch each library carton's source PDF.
        restoreCartons(u.cartons, u.localOnly);
        // Restore a result that finished while the user was signed out —
        // now straight from hydration (backend reply #3 §1), no ui_state ids.
        recoverFinishedJobs(ws.recent_jobs);
      } catch (e) {
        if (currentUidRef.current !== uid) return;   // user changed — discard
        if (e instanceof WorkspaceApiError && (e.status === 404 || e.status === 405)) {
          wsSupportedRef.current = false; setWsSupported(false);   // pre-PR④ backend
        } else {
          // Transient (5xx / edge): leave support unknown so a later action can
          // still try; hydration just doesn't populate this time.
          wsSupportedRef.current = true; setWsSupported(true);
        }
      } finally {
        if (hydratingRef.current === uid) hydratingRef.current = null;
        if (currentUidRef.current === uid) {
          hydratedForRef.current = uid;
          // Set even when hydration failed: autosave is gated on this, and a
          // failed restore must not also cost the user their future saves.
          wsReadyRef.current = true; setWsReady(true);
        }
      }
    })();

    // Deliberately no cleanup-cancel. A StrictMode cleanup is not a real
    // unmount, and aborting here is what previously left the latch set with
    // nothing applied and wsReady never raised.
    return undefined;
  }, [auth.signedIn, auth.user?.id, applyPresets, restoreCartons, recoverFinishedJobs, refreshSavedJobs]);

  // Reset the hydration latch on sign-out so the next sign-in re-hydrates.
  useEffect(() => {
    if (!auth.signedIn) {
      hydratedForRef.current = null;
      hydratingRef.current = null;      // else the next sign-in is treated as in flight
      currentUidRef.current = null;     // discards any hydration still in flight
      wsReadyRef.current = false; setWsReady(false);
      uiTooLargeRef.current = false;
      setRecentCartons([]); setActiveJobs([]);
      setSavedJobs([]); setSavedLoaded(false); setLastRun(null); setReturnedRun(null);
      offeredRunRef.current = null;
    }
  }, [auth.signedIn]);

  // Manual "Save as my defaults" (§2). Explicit, not autosaved: PUT replaces the
  // whole preset object, so overwriting it on every keystroke would be surprising.
  const savePresets = useCallback(async () => {
    if (wsSupportedRef.current === false) return;
    setPresetsBusy(true); setPresetsError(null);
    try {
      await putPresets({ apiBase: API_BASE, presets: collectPresets() });
      setPresetsSavedAt(Date.now());
    } catch (e) {
      setPresetsError(e instanceof WorkspaceApiError ? e.message : String(e));
    } finally { setPresetsBusy(false); }
  }, [collectPresets]);

  // ui-state autosave (§3): debounced (~2.5 s idle), last-write-wins, 256 KB cap.
  // Gated on hydration-complete so the initial restore isn't echoed straight back.
  // ui-state autosave (§4.3): debounced (~2.5 s idle), last-write-wins, 256 KB cap.
  // Gated on hydration-complete so the initial restore isn't echoed straight back.
  //
  // What goes in here is the session a user expects back after signing in again:
  // which cartons were on the bench, the sheet they were imposing onto, and a
  // handle on the last job so a result computed while they were away can be
  // recovered (§6.3 durable records).
  //
  // v3 (backend reply #3 §1): `lastLayoutJobId`/`lastPlanJobId` are GONE — the
  // server's `recent_jobs` supersedes them, without the debounce race that could
  // lose an id or the one-job-per-kind ceiling. Old v2 blobs still read fine;
  // the extra keys are simply ignored.
  //
  // Cartons are stored as LIBRARY REFERENCES only. A locally-dropped PDF exists
  // as a File object in this tab and nowhere else — there are no bytes on the
  // server to restore it from, so it is deliberately not pretended to persist.
  // `localOnly` records how many were dropped, so the UI can say so honestly
  // rather than silently losing them.
  const uiStateSnapshot = useMemo(() => {
    const { refs, localOnly } = splitCartonRefs(vecFiles);
    return {
      // v7 (PR ⑭e): carton refs may also carry `required` and
      // `prepress_config`. Purely additive, same discipline as v6 — a carton
      // with neither is byte-identical to its v6 ref, and a v6 blob reads as
      // "nothing firm, no overrides", which is the pre-⑭e behaviour exactly.
      v: 7,
      workspace, plTab, cfgAdvancedOpen,
      sheet: { w: Number(shW) || 0, h: Number(shH) || 0, kerf: parseFloat(vecKerf) || 0 },
      // Margins are session state, not just a default. They were previously only
      // in `presets`, which is saved by an explicit button — so a refresh reset
      // them to 0 unless the user had clicked "Save as my defaults".
      margins: { mode: marginMode, uniform: Number(marginUniform) || 0, sides: marginSides },
      // Per-carton colour decisions. Export-affecting: `reexport` excludes a
      // carton from the run and `proceed` drives auto_convert, so losing these
      // silently changes what gets imposed.
      //
      // Pruned to the current bench. `vecDecisions` is keyed by filename and is
      // never cleared when a carton is removed, so persisting it wholesale would
      // grow without bound and could re-apply a stale `reexport` to a different
      // carton that happens to share a filename.
      decisions: Object.fromEntries(
        Object.entries(vecDecisions).filter(([k]) =>
          vecFiles.some((c) => c.name.replace(/\.pdf$/i, '') === k)),
      ),
      // Planner sheet form (its gripper has no equivalent in the Layout rail).
      plannerSheet: plSheet,
      // §2B: per-job setup, so tray history can restore cartons + quantities
      // after a refresh. Capped to 10 entries in recordJobContext.
      jobContexts,
      // Saved jobs (decided 2 Nov): the latest successful run, so it can still
      // be saved after the window is closed. OUTSIDE jobContexts on purpose —
      // the draft clean-up ignores jobContexts but keeps any draft named
      // anywhere else here, so this run's drafts outlive the 3 days.
      lastRun,
      cartons: refs,
      localOnly,
    };
  }, [workspace, plTab, cfgAdvancedOpen, shW, shH, vecKerf, vecFiles,
      marginMode, marginUniform, marginSides,
      vecDecisions, plSheet, jobContexts, lastRun]);

  useEffect(() => {
    if (!auth.signedIn || !wsReady || wsSupportedRef.current === false) return undefined;
    if (uiTooLargeRef.current) return undefined;   // latched off until content shrinks
    if (uiSaveTimerRef.current) clearTimeout(uiSaveTimerRef.current);
    pendingUiRef.current = uiStateSnapshot;      // unsaved until the timer fires
    uiSaveTimerRef.current = setTimeout(async () => {
      try {
        await putUiState({ apiBase: API_BASE, uiState: uiStateSnapshot });
        pendingUiRef.current = null;             // durable now; nothing to flush
      } catch (e) {
        if (e instanceof WorkspaceApiError && e.status === 413) {
          uiTooLargeRef.current = true;   // stop retrying; would fail identically
          toast.notify({ kind: 'warn', title: 'UI state too large', message: 'Session view state exceeded 256 KB and was not saved.' });
        }
        // Other failures (transient/offline): silent — a later change retries.
      }
    }, 2500);
    return () => { if (uiSaveTimerRef.current) clearTimeout(uiSaveTimerRef.current); };
  }, [uiStateSnapshot, auth.signedIn, wsReady, toast]);

  // Flush a pending autosave when the page goes away.
  //
  // Without this, the 2.5 s debounce is a hole: set a margin, hit refresh half a
  // second later, and the edit is gone — which reads as "the app reset itself".
  // `keepalive` lets the request outlive the document; a normal fetch would be
  // cancelled mid-flight.
  //
  // `pagehide` rather than `beforeunload`: it fires on mobile/bfcache paths where
  // beforeunload does not. `visibilitychange → hidden` covers tab-switch and
  // app-backgrounding, which on mobile is often the last event before teardown.
  useEffect(() => {
    if (!auth.signedIn || !wsReady || wsSupportedRef.current === false) return undefined;

    const flush = () => {
      const pending = pendingUiRef.current;
      if (!pending || uiTooLargeRef.current) return;
      pendingUiRef.current = null;              // fire once, not per event
      if (uiSaveTimerRef.current) clearTimeout(uiSaveTimerRef.current);
      // Fire-and-forget: the page may be gone before this settles, so failures
      // are unobservable by design. The debounced save is the retry path.
      putUiState({ apiBase: API_BASE, uiState: pending, keepalive: true }).catch(() => {});
    };
    const onVisibility = () => { if (document.visibilityState === 'hidden') flush(); };

    window.addEventListener('pagehide', flush);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pagehide', flush);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [auth.signedIn, wsReady]);


  // ── PR ⑤: reattach to work that was still running when the user left ──────
  // §4.1: `active_jobs` carries the user's unfinished jobs with ready-made
  // `poll`/`cancel` URLs. Restore the running UI and resume polling those URLs
  // — never construct job paths (§2, §6.1).

  // The resume loop below is created once (stable identity) but must never call
  // a stale first-render closure — `finishVectorSuccess` reaches through to
  // `registerSkusFromVector`, which reads live state. Keep a ref pointing at the
  // CURRENT render's handlers and call through it.
  const vecHandlersRef = useRef(null);

  /**
   * Dedicated resume loop for an export/layout job. Deliberately NOT the main
   * runner: that function owns submit-time concerns (upload, watchdog, sync
   * fallback, abort-during-upload) which have no meaning when reattaching to a
   * job the server is already running. Terminal handling matches the main loop.
   */
  const reattachVectorJob = useCallback(async (aj) => {
    const H = () => vecHandlersRef.current;
    const trayId = jobs.startJob({
      kind: 'layout', label: 'Running layout (resumed)', message: 'Reconnecting…',
      onCancel: async () => {
        // §6.5: cancel is idempotent past the end — 202 even for a finished job.
        try { await cancelJob({ apiBase: API_BASE, cancelUrl: aj.cancel, jobId: aj.job_id }); } catch { /* stale UI: harmless */ }
      },
    });
    setVecLoading(true);
    // ⑭i: shared cadence. A resumed job is by definition already old, so it
    // reaches the slow tier almost at once — which is the point.
    const startedAt = Date.now();
    for (;;) {
      let job;
      try {
        job = await pollJob({ apiBase: API_BASE, pollUrl: aj.poll, jobId: aj.job_id });
      } catch (e) {
        if (e instanceof VectorApiError && e.status === 404) {
          jobs.endJob(trayId, { status: 'failed', error: 'Job not found — run the layout again.' });
        } else {
          H().failVectorFromError(e, trayId);
        }
        setVecLoading(false);
        return;
      }
      const terminal = isJobTerminal(job.status);
      const persisted = isPersistedRecord(job);
      setVecJob(job);
      jobs.updateJob(trayId, {
        fraction: persisted ? null : (job.progress?.fraction ?? null),
        message: persisted ? job.status : (job.progress?.message || job.progress?.phase || job.status),
      });
      if (terminal) {
        if (job.status === 'succeeded' && job.result) {
          H().finishVectorSuccess(job.result, trayId, undefined, { jobId: aj.job_id, poll: aj.poll });
        } else if (job.status === 'cancelled') {
          jobs.endJob(trayId, { status: 'cancelled', message: 'Cancelled.' });
        } else if (H().routeHaltIfAny(job, trayId)) {
          // ⑭c: a resumed job can halt too — a job queued before this release,
          // or one whose token went stale.
        } else {
          const msg = terminalStatusMessage(job.status, job.error);
          setVecError(msg);
          jobs.endJob(trayId, { status: 'failed', error: msg });
          toast.notify({
            kind: job.status === 'interrupted' ? 'warn' : 'error',
            title: job.status === 'interrupted' ? 'Server restarted' : 'Layout failed',
            message: msg,
          });
        }
        setVecJob(null);
        setVecLoading(false);
        return;
      }
      await new Promise((r) => setTimeout(r, pollDelayMs(Date.now() - startedAt)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Reattach one hydrated active job. Idempotent per job_id. */
  // ── §2A/§2B: job context capture + tray actions ───────────────────────────

  /**
   * What the bench is set up to run right now — cartons by library id with
   * their per-carton settings, the sheet, the margins. Recorded when a job is
   * submitted (below), and what a run is saved with when nothing was recorded.
   */
  const currentSetup = useCallback(() => {
    const { refs } = splitCartonRefs(vecFiles);
    // Carry names so a row can be labelled even if the carton later leaves the
    // library and can no longer be fetched.
    //
    // PR ⑬b (D5): the prepress mode is SNAPSHOTTED here, not read live at
    // restore time. On the planner path the tag lives on the SKU, which is
    // durable and org-scoped — but also mutable. Edit a SKU from plain to pair
    // next week and a restored job would claim it ran in pair, with a dieline
    // preview and a carton count for a mode it never used. Same reasoning as
    // quantities: request-time setup is not recoverable from live state.
    const cartons = vecFiles
      .filter((c) => c.libraryId)
      .map((c) => ({
        libraryId: c.libraryId, name: c.name, count: c.count, prepress: c.prepress || null,
        // ⑭e: ⑭d omitted these, so a restored firm job claimed it was not
        // firm — the same failure the D5 note above describes for the mode,
        // one field over. A snapshot that drops a request field stops being
        // request-time truth.
        required: c.required === true,
        prepress_config: c.prepressConfig || null,
        // 1.11: spacing decides the kerf the job nested at — request-time truth
        spacing_mm: clampSpacing(c.spacingMm),
        // ⑭l: the carton type is request-time truth too, and restoreCartons
        // reads it back — but it was never recorded here, so a reopened STE
        // job came back as BL. A saved job must not.
        carton_type: c.cartonType || null,
      }));
    return {
      cartons: cartons.length ? cartons : refs,
      sheet: { w: Number(shW) || 0, h: Number(shH) || 0, kerf: parseFloat(vecKerf) || 0 },
      margins: { mode: marginMode, uniform: Number(marginUniform) || 0, sides: marginSides },
      at: Date.now(),
    };
  }, [vecFiles, shW, shH, vecKerf, marginMode, marginUniform, marginSides]);

  /** Snapshot what the user configured for a job, keyed by job_id. */
  const recordJobContext = useCallback((jobId) => {
    if (!jobId) return;
    const ctx = currentSetup();
    setJobContexts((prev) => {
      const next = { ...prev, [jobId]: ctx };
      // Bound the blob: keep the 10 most recent contexts.
      const keys = Object.keys(next).sort((a, b) => (next[b]?.at || 0) - (next[a]?.at || 0));
      return Object.fromEntries(keys.slice(0, 10).map((k) => [k, next[k]]));
    });
  }, [currentSetup]);

  /**
   * Put the workspace back for a job: sheet, margins, cartons and quantities.
   * Prefers the recorded request context; falls back to the job result, which
   * carries sheet size and margins but cannot supply requested quantities.
   */
  const applyJobContext = useCallback(async (ctx, result, opts = {}) => {
    const w = ctx?.sheet?.w ?? result?.sheet?.width;
    const h = ctx?.sheet?.h ?? result?.sheet?.height;
    if (Number(w) > 0) setShW(Number(w));
    if (Number(h) > 0) setShH(Number(h));
    if (ctx?.sheet?.kerf != null) setVecKerf(String(ctx.sheet.kerf));

    const m = ctx?.margins;
    if (m) {
      if (m.mode === 'uniform' || m.mode === 'perSide') setMarginMode(m.mode);
      if (Number.isFinite(Number(m.uniform))) setMarginUniform(Number(m.uniform));
      if (m.sides && typeof m.sides === 'object') {
        setMarginSides({
          top: Number(m.sides.top) || 0, right: Number(m.sides.right) || 0,
          bottom: Number(m.sides.bottom) || 0, left: Number(m.sides.left) || 0,
        });
      }
    } else if (result?.sheet?.margins) {
      const rm = result.sheet.margins;
      const sides = {
        top: Number(rm.top) || 0, right: Number(rm.right) || 0,
        bottom: Number(rm.bottom) || 0, left: Number(rm.left) || 0,
      };
      setMarginSides(sides);
      const uniform = sides.top === sides.right && sides.right === sides.bottom && sides.bottom === sides.left;
      if (uniform) { setMarginMode('uniform'); setMarginUniform(sides.top); } else setMarginMode('perSide');
    }

    if (Array.isArray(ctx?.cartons) && ctx.cartons.length) {
      // Silent: the tray click is the user's own action, so the generic
      // "Session restored" toast would be noise here.
      return restoreCartons(ctx.cartons, 0, { silent: true, via: opts.via });
    }
    return { restored: 0, missing: [], missingNames: [] };
  }, [restoreCartons]);

  /**
   * §2B inspection: load a finished run's layout and its setup into the
   * workspace — the last run, or a saved job (whose setup comes from the
   * server). Returns what came back of its cartons, or null when there was no
   * layout to load.
   */
  const openRun = useCallback(async ({ jobId, poll, ctx, via }) => {
    if (!jobId) return null;
    try {
      const job = await pollJob({ apiBase: API_BASE, pollUrl: poll, jobId });
      if (job?.status === 'succeeded' && job.result) {
        setVecResult(job.result);
        setVecJobId(jobId);              // re-anchors the approval section + Save job
        setVecError(null);
        setWorkspace('layout');
        const got = await applyJobContext(ctx || jobContextsRef.current[jobId], job.result, { via });
        const note = got && missingCartonsMessage(got.missingNames);
        if (note) toast.notify({ kind: 'warn', title: 'Some cartons did not come back', message: note });
        return got;
      }
      toast.notify({ kind: 'warn', title: 'Nothing to load', message: `This job is ${job?.status || 'unavailable'} — no layout to show.` });
    } catch (e) {
      toast.notify({ kind: 'error', title: 'Could not load job', message: e?.message || String(e) });
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyJobContext]);

  /** A saved job, from the tray: its layout, and the setup it was saved with. */
  const openSavedJob = useCallback(async (row) => {
    let full;
    try {
      full = await getSavedJob({ apiBase: API_BASE, id: row.id });
    } catch (e) {
      const copy = e?.code && PLATFORM_ERROR_COPY[e.code];
      toast.notify({ kind: 'warn', title: copy ? copy.title : 'Could not open the saved job', message: copy ? copy.hint : (e?.message || String(e)) });
      if (e?.code === 'saved_job_not_found') refreshSavedJobs();
      return;
    }
    // Its cartons come through the saved job itself (decided 2 Nov, (b)): an
    // admin opening a colleague's job gets the colleague's private cartons,
    // and a carton deleted since still comes back (#88).
    await openRun({ jobId: full.job_id, poll: full.poll, ctx: full.setup, via: { savedJobId: full.id } });
  }, [openRun, refreshSavedJobs, toast]);

  /**
   * §2A: attach to a job that was already running — ONLY when the user asks.
   *
   * This used to run automatically on hydration, which set `vecLoading` and so
   * left "Run layout" disabled on a fresh page load for a job the user had not
   * chosen to resume. The workspace now stays clean until the tray row is
   * clicked; this is what that click does.
   */
  const attachToRunningJob = useCallback(async (aj) => {
    if (!aj || !aj.job_id || !aj.poll) return;
    if (reattachedRef.current.has(aj.job_id)) return;
    reattachedRef.current.add(aj.job_id);

    setWorkspace('layout');
    // Put the panel back before polling starts, so the inputs match the job.
    await applyJobContext(jobContextsRef.current[aj.job_id], null);

    const kind = normalizeJobKind(aj.kind);
    if (kind === 'plan') {
      // Hand the URLs to the existing effect-driven plan loop — it takes over.
      const trayId = jobs.startJob({
        kind: 'plan', label: 'Computing plans (resumed)', message: 'Reconnecting…',
        onCancel: () => plCancel(),
      });
      setPlBusy(true);
      setPlError(null);
      setPlJobUrls({ poll: aj.poll, cancel: aj.cancel, jobId: aj.job_id, trayId });
    } else if (kind === 'layout') {
      reattachVectorJob(aj);
    }
    // 'unknown' → listed but never attached: we can't know which UI to restore,
    // and guessing could hijack the canvas.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reattachVectorJob, applyJobContext]);

  // §2A: put hydrated RUNNING jobs into the tray rather than attaching to
  // them; they land highlighted and unattached, and nothing touches the canvas
  // or the Run button until the user clicks a row. Finished runs from earlier
  // sessions are no longer listed (decided 2 Nov: unsaved runs leave the tray)
  // — saved jobs have their own section, and the last run is offered below.
  useEffect(() => {
    if (!activeJobs.length) return;

    const picked = activeJobs.filter((aj) => aj?.job_id).length;
    if (picked > 0) {
      toast.notify({
        kind: 'info', title: 'Job still running',
        message: `Picked up ${picked} job${picked > 1 ? 's' : ''} still running from your last session. Progress is in the Jobs tray.`,
      });
    }

    activeJobs.forEach((aj) => {
      if (!aj?.job_id) return;
      const k = normalizeJobKind(aj.kind);
      jobs.registerJob({
        jobId: aj.job_id, kind: k, status: 'running',
        label: k === 'plan' ? 'Plan from last session' : 'Layout from last session',
        message: 'Click to reattach and resume progress.',
        unattached: true, poll: aj.poll, cancel: aj.cancel,
        onOpen: () => attachToRunningJob(aj),
      });
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeJobs]);

  // ── Layout run — central job system (HANDOVER_central_jobs) ──
  // Primary path: POST /export/vector/async → poll GET /jobs/{id} → terminal.
  // Cancel is now a REAL backend cancel: the job's token is armed into the
  // engine, so it interrupts nesting mid-computation (terminal `cancelled`
  // measured ≤ 2 s). v1.3 race rules apply: the cancel response is a 202
  // snapshot, a 404 means "too late — already finished", and we only ever
  // branch on the FINAL polled status.
  // Fallback: if the async endpoint doesn't exist (backend predates central
  // jobs → 404/405 at start), run the sync route with a client-side abort.
  const vecCancelRef = useRef(null);          // routed: backend cancel (async) or fetch abort (sync fallback)
  const cancelVectorLayout = () => { if (vecCancelRef.current) vecCancelRef.current(); };
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  // Shared success path (identical result shape from both routes — one pipeline).
  /**
   * `run` is the job's handle — { jobId, poll } from /export/vector/async (NOT
   * `result.job_id`, which is a content fingerprint, not a handle) — or null
   * for a run that went through no background job, which cannot be saved.
   */
  const finishVectorSuccess = (result, trayId, taggedNames, run = null) => {
    const verdict = interpretVectorResult(result);   // §7 render decision
    if (!verdict.renderable) {
      setVecError(verdict.message);
      toast.notify({ kind: 'error', title: 'Layout failed', message: verdict.message });
      jobs.endJob(trayId, { status: 'failed', error: verdict.message });
      return;
    }
    if (verdict.level === 'warning') setVecWarn(verdict.message);

    setVecResult(result);

    // Saved jobs (decided 2 Nov): this is now the LAST run, kept in ui_state
    // with the setup it ran with, so it can still be saved after the window is
    // closed; every older unsaved run leaves the tray (saved ones are listed
    // under Saved jobs). A run with no handle replaces it with nothing: it can
    // never be saved, and an older run is no longer the latest.
    const runJobId = run && run.jobId ? run.jobId : null;
    setLastRun(runJobId ? lastRunEntry({
      jobId: runJobId, poll: run.poll,
      ctx: jobContextsRef.current?.[runJobId] || currentSetup(), at: Date.now(),
    }) : null);
    jobs.dropJobs((rows) => rowsLeavingTheTray(rows, runJobId));

    // PR ⑬a §4: report CARTONS, never pieces. A pair tile is one piece and two
    // cartons, so n_placed under-reports — quote 9, ship 18. cartons_placed is
    // pair-aware and equals n_placed in plain mode, so this reads unconditionally.
    const nCartons = cartonsPlaced(result);
    // Tiled output is quoting-only until a die-maker clears strippability, and
    // this notice is the only thing standing between a tile and a plate. Driven
    // by the RESPONSE (prepress_tiled), never by the requested mode — `auto` can
    // be asked for and return plain.
    const quoting = quotingOnlyNotice(result);
    if (quoting) setVecWarn((prev) => (prev ? `${prev}\n${quoting}` : quoting));

    // ⑭d / rev 5 §6.2: the ONE prepress outcome that deliberately produces less
    // than was ordered. Protecting a firm line can cost cartons, and the
    // operator must see both numbers before quoting. Held in its own state
    // rather than folded into vecWarn — a run can be quoting-only AND reduced,
    // and concatenating them buries the number that changes the invoice.
    const reduced = reducedCapacity(result);
    setPrepressReduced(reduced ? {
      ...reduced,
      notice: reducedCapacityNotice(result),
      // ⑭f / rev 7 §8: which line was protected and which was cut short. The
      // job-wide figures above stay exactly as ⑭d shipped them — this is
      // attribution added beneath, not a replacement, because "ordered 5, got
      // 2" is the sentence that stops a quote and the name is what decides
      // which customer gets the call.
      byCarton: reducedByCarton(result),
      attribution: reducedAttributionNotice(result),
    } : null);

    const summary = `${nCartons} carton${nCartons === 1 ? '' : 's'} placed`;
    jobs.endJob(trayId, {
      status: 'succeeded',
      message: quoting ? `${summary} · quoting only.` : `${summary}.`,
      // so a newer run, or saving this one, can take this row out of the tray
      jobId: runJobId,
    });
    toast.notify({
      kind: quoting ? 'warn' : 'success',
      title: quoting ? 'Layout ready — quoting only' : 'Layout ready',
      message: `${summary}${result.n_unplaced ? ` · ${result.n_unplaced} unplaced` : ''}.`,
    });

    // ── PR ⑬b §6: verify prepress actually RAN ──
    // Checked positively, never by absence of error. The failure the backend
    // shipped and fixed was silent: the job succeeded, the carton nested, and
    // nothing reported that prepress had been skipped — it only surfaced when a
    // production log showed a 6-vertex outline reaching the engine.
    //
    // Tagged names come from the job context recorded at submit (D5), so a job
    // reattached from the tray verifies against what it ACTUALLY ran with, not
    // against whatever the bench happens to hold now.
    const ctxCartons = jobContextsRef.current?.[result?.job_id]?.cartons;
    const tagged = Array.isArray(taggedNames) && taggedNames.length
      ? taggedNames
      : taggedCartonNames(ctxCartons || []);

    // Rev 4 §1: compare job_id across runs of the same artwork. This replaces
    // three heuristics with one direct signal — the §6 checks below stay as a
    // backstop because they cost nothing.
    const ctxForSig = ctxCartons || vecFiles;
    const entry = {
      signature: runSignature(ctxForSig, { w: Number(shW) || 0, h: Number(shH) || 0 },
        parseFloat(vecKerf) || 0, effectiveMargins),
      prepressSignature: prepressSignature(ctxForSig),
      jobId: result?.job_id || null,
    };
    const dedup = detectDedupCollision(vecRunHistoryRef.current, entry);
    if (dedup.collision) {
      setVecError(dedup.reason);
      toast.notify({ kind: 'error', title: 'Cached result returned — do not plate', message: dedup.reason });
    } else if (entry.jobId) {
      // Bounded: this is a defect probe, not an audit log.
      vecRunHistoryRef.current = [...vecRunHistoryRef.current.slice(-9), entry];
    }

    const check = verifyPrepressRan(result, tagged);
    setPrepressCheck(tagged.length ? check : null);
    if (!check.ok) {
      toast.notify({
        kind: 'error', title: 'Prepress did not run',
        message: check.reason,
      });
    }

    // Option A / trigger #2: auto-register the detected dies as planner SKUs.
    // Best-effort — a registry hiccup must not disturb the layout just produced.
    registerSkusFromVector(result);
  };

  /**
   * ⑭c: route a halt to the confirmation prompt, never to the error path.
   *
   * One reader, two carriers (rev 6 §1): the sync route puts the halt on
   * `detail` of a 409, the async job puts the identical object on
   * `error_detail` of a job whose status is `failed`. `failed` is correct and
   * not something to work around — a new status would read as non-terminal to
   * our inverted poll test and would never have stopped the loop.
   *
   * @returns {boolean} true when this was a halt and has been handled
   */
  const routeHaltIfAny = (source, trayId) => {
    const halt = parseConfirmationRequired(source);
    if (!halt) return false;
    setPrepressHalt({
      ackToken: halt.ackToken,
      warnings: halt.warnings,
      // ⑭f / rev 7 §6: the resume URL rides the halt, so it is the only thing
      // that decides how we continue. The old `kind`/`jobId` pair existed to
      // build the path from the job id; with the path supplied, keeping them
      // would leave two encodings of one fact free to disagree. A sync 409 has
      // no job to re-queue and so carries no `confirm` — the resubmit route
      // below is reached by the field being absent, not by a separate flag.
      confirmPath: halt.confirm,
    });
    // The tray row must not read "failed" — nothing failed, and the operator
    // still has a decision to make.
    jobs.endJob(trayId, { status: 'cancelled', message: 'Waiting for confirmation' });
    setVecError(null);
    return true;
  };

  // Sync-route error mapping (§7 status map), also used for async START errors
  // (the async route validates synchronously with the same semantics).
  const failVectorFromError = (e, trayId) => {
    const msg = e instanceof VectorApiError ? e.message : String(e);
    const body = e instanceof VectorApiError ? e.body : null;

    // ⑭c: a 409 halt is not a failure. Checked FIRST, before any error copy —
    // this used to render as "Prepress rejected this carton" with the token and
    // warnings discarded, i.e. a recoverable pause shown as a dead end.
    if (routeHaltIfAny(body, trayId)) return;

    // PR ⑬b: a prepress rejection is a 400 too, but "no DIELINE layer" is the
    // wrong advice for it — that message would send an operator to re-export
    // artwork that is already correct. Check for the structured code first;
    // describePrepressError returns null for every other 400 shape, so the
    // legacy branch below is untouched.
    const prep = describePrepressError(body);
    if (prep) {
      // Rev 4: PrepressResidue arrives as a 500 and is explicitly the backend's
      // defect — the server caught itself about to nest an unflapped outline
      // and refused. Never route an operator into their artwork for it.
      if (isBackendDefect(body)) {
        toast.notify({ kind: 'error', title: 'Server-side defect — report this', message: prep.title });
        jobs.endJob(trayId, { status: 'failed', error: prep.title });
        setVecError(`${prep.title}\n${prep.hint || ''}`);
        return;
      }
      // A TilingDisabled rejection means the gate closed under a live session
      // (capabilities said otherwise when the app loaded). Clear the stale
      // warning; the copy below explains. We do NOT record it as session state
      // — capabilities is re-asked on load and is the only authority.
      if (isTilingDisabledError(body)) setVecWarn(null);
      // ⑭l / rev 12 §0.1: CartonTypeTopologyMismatch carries the fix in its
      // detail. `suggested_carton_types` is what the operator should be
      // OFFERED — never applied — so this appends the offer to the message and
      // the picker on the row is where the choice is made.
      const mismatch = parseTopologyMismatch(body);
      const offer = mismatch && mismatch.suggested.length
        ? `\nSet the carton type on this row: ${mismatch.suggested.join(' or ')}`
          + `${mismatch.wasDefaulted ? ` (it defaulted to ${mismatch.typeUsed || 'BL'} because none was set)` : ''}.`
        : '';
      const line = (prep.hint ? `${prep.title}\n${prep.hint}` : prep.title) + offer;
      toast.notify({
        kind: 'error',
        title: prep.piece ? `${prep.piece} — prepress rejected` : 'Prepress rejected',
        message: prep.title,
      });
      jobs.endJob(trayId, { status: 'failed', error: `${prep.piece ? `${prep.piece}: ` : ''}${prep.title}` });
      setVecError(prep.piece ? `${prep.piece}\n${line}` : line);
      setPrepressErrPiece(prep.piece);   // rail highlights that row
      return;
    }

    toast.notify({ kind: 'error', title: 'Layout failed', message: msg });
    jobs.endJob(trayId, { status: 'failed', error: msg });
    if (e instanceof VectorApiError && e.status === 400) {
      setVecError(`${e.message}\nThis PDF has no DIELINE layer or CutContour spot. Use the classic (draw/offset) path for it, or re-export the artwork with the die convention.`);
    } else if (e instanceof VectorApiError && e.status === 503) {
      setVecError('Engine unavailable — contact the administrator. (Do not retry.)');
    } else {
      setVecError(msg);
    }
  };

  // PR ⑤: keep the resume loop calling THIS render's handlers (see vecHandlersRef).
  vecHandlersRef.current = { finishVectorSuccess, failVectorFromError, routeHaltIfAny };

  // PR ⑬d: derived from the bench, so it appears the moment a carton is set to
  // automatic and disappears when it isn't.
  const runNote = estimatedRunNote(collectPrepressModes(vecFiles));
  // Rev 3 §8.6.4: report entries correlate by name, so two tagged cartons
  // sharing one would attach a dieline to the wrong carton. Backend rejects it
  // with 400 DuplicateCartonName; catching it here saves the round trip.
  const dupeBlock = duplicateNameBlockReason(vecFiles);
  // ⑭d: `required` is inert outside `auto` and the export path rejects it
  // (400 RequiredRequiresAuto). Caught here so the operator is not told so
  // after uploading every carton PDF.
  const requiredBlock = firmLineBlockReason(vecFiles, prepressCaps);
  // PRODUCT RULE: a bare keyline with no dieline plates something unfoldable.
  // Independent of `required` — firmness decides what a shortfall costs, not
  // whether the geometry can be made. Mirrors the server's
  // `UnflappedCartonNotTagged` refusal, so this stops the operator at the rail
  // where the fix is one click instead of after uploading every PDF.
  const unflappedBlock = unflappedUntaggedBlockReason(vecFiles, prepressCaps);
  // ⑭l / rev 12 §0.1: the same refusal the backend now issues up front, checked
  // here so the fix is in front of the operator at the rail rather than after an
  // upload. It never picks a type — it refuses to proceed with one that cannot
  // be right and names what is on offer.
  const typeBlock = cartonTypeBlock(vecFiles, prepressCaps);
  const offerableTypes = offerableCartonTypes(prepressCaps);
  // Whether this deployment understands firm order lines at all.
  const canMarkRequired = hasFeature(prepressCaps, 'required_pieces');
  // Prefer the server's answer over our constant (rev 3 §5.1). `pair` is
  // filtered out inside offerableModes until the quoting-only labelling ships.
  const railModes = offerableModes(prepressCaps);

  /**
   * ⑭c stage 2. The token is ECHOED, never regenerated — backend asked for a
   * token rather than `acknowledge=true` precisely so that an edit between
   * stages produces a fresh halt instead of silently suppressing a different
   * warning.
   *
   * Two ways forward, and the cheap one is preferred: a job that already
   * halted has its uploads on the server, so `confirm` re-queues them. Only
   * when that is impossible (no blob store, or nothing was ever queued) do we
   * send the PDFs again.
   */
  const continueDespiteHalt = async () => {
    const halt = prepressHalt;
    if (!halt?.ackToken) return;
    setPrepressHalt(null);

    // ⑭f: the gate is now the URL the server GAVE us, not a job id we could
    // build a path from. No URL means this deployment predates rev 7 §6 — the
    // cheap route is unavailable, so we take the documented expensive one
    // below instead of guessing at a path.
    if (halt.confirmPath) {
      try {
        const next = await confirmVectorJob({
          apiBase: API_BASE, confirmPath: halt.confirmPath, ackToken: halt.ackToken,
        });
        // A NEW job id: the halted one stays halted. Poll the new one through
        // the resume loop, which exists for exactly this — attaching to work
        // the server is already running, with no submit-time concerns.
        setVecJobId(next.job_id);
        reattachVectorJob({ job_id: next.job_id, poll: next.poll, cancel: next.cancel });
        return;
      } catch (e) {
        if (!isConfirmResubmitRequired(e?.body)) {
          toast.notify({
            kind: 'error',
            title: 'Could not confirm',
            message: e?.message || 'The job could not be confirmed. Run the layout again.',
          });
          return;
        }
        // ConfirmRequiresResubmit: this deployment kept no uploads. Fall
        // through and send them again, ack in hand.
      }
    }
    runVectorLayout(halt.ackToken);
  };

  // ⑭c: any change to what would be submitted invalidates the token. Backend
  // is explicit that echoing a stale token is fine (it halts again), but
  // holding a "Continue anyway" prompt over an edited bench invites the
  // operator to confirm a warning that no longer describes their job.
  useEffect(() => { setPrepressHalt(null); }, [vecFiles]);

  const runVectorLayout = async (ackToken = null) => {
    setVecJobId(null);                  // PR ⑥: drop the previous run's approval badge
    // Exclude zero-count cartons and any RGB-flagged carton the operator chose
    // to re-export (it's awaiting a new CMYK file).
    const srcId = (name) => name.replace(/\.pdf$/i, '');
    const active = vecFiles.filter(
      (c) => c.count > 0 && vecDecisions[srcId(c.name)] !== 'reexport'
    );
    if (!active.length) {
      setVecError('No cartons to nest. Add a PDF (with a DIELINE layer or CutContour spot), set a count > 0, and resolve any re-export decisions.');
      return;
    }

    setVecLoading(true); setVecError(null); setVecWarn(null);
    // PR ⑬b: clear the previous run's prepress verdict and row highlight — a
    // stale "prepress did not run" banner over a fresh sheet is worse than none.
    setPrepressCheck(null); setPrepressErrPiece(null);
    setPrepressHalt(null); setPrepressReduced(null);
    // Names we are asking prepress for, captured BEFORE the run so §6
    // verification compares against what was actually submitted.
    const taggedNames = taggedCartonNames(active);
    // Synthetic pre-job snapshot: the multipart upload can take seconds, and the
    // job doesn't exist server-side until the submit resolves. Showing an honest
    // "uploading" phase (and accepting cancel) from t=0 removes the frozen feel.
    // ⑭c stage 1, bodiless (rev 6 §2.1). Only worth a round trip when a piece
    // is `required` — that is the only thing stage 1 warns about, so with none
    // the answer is a guaranteed empty list. The real submit runs stage 1
    // regardless; this saves the upload, it never skips the check.
    if (!ackToken) {
      const pieces = preflightPieces(active);
      if (needsPreflight(pieces)) {
        try {
          const pre = parsePreflight(await prepressPreflight({ apiBase: API_BASE, pieces }));
          if (preflightHalts(pre)) {
            // ⑭f: one halt shape. A preflight halt has no queued job and so
            // no `confirm` URL — the absence is what routes `continueDespiteHalt`
            // to submit-with-ack, which is correct here: nothing was uploaded.
            setPrepressHalt({ ackToken: pre.ackToken, warnings: pre.warnings, confirmPath: null });
            setVecLoading(false);
            return;                       // nothing uploaded, nothing queued
          }
        } catch {
          // Preflight is advisory. A server that cannot answer it must not
          // block a layout — the real submit still halts if it needs to.
        }
      }
    }
    setVecJob({ status: 'submitting', cancel_requested: false, progress: { phase: 'uploading', message: 'Uploading cartons…', fraction: null } });
    const trayId = jobs.startJob({ kind: 'layout', label: 'Running layout', message: 'Uploading cartons…', onCancel: () => cancelVectorLayout() });
    const reqArgs = {
      apiBase: API_BASE,
      // PR ⑬b: the mode rides ON the carton, so alignment with file order is
      // structural rather than a convention someone has to maintain.
      // ⑭e: `required`, `prepressConfig` and `cartonType` ride on the carton
      // for the same reason — the config list is POSITIONAL and a wrong length
      // is a 400, so it is built from the same iteration as the files.
      cartons: active.map((c) => ({
        file: c.file, count: c.count, prepress: c.prepress || null,
        required: c.required === true,
        prepressConfig: c.prepressConfig || null,
        cartonType: c.cartonType || null,
        // 1.11: the clear space this carton needs; the backend nests the sheet
        // at the largest one and reports it as kerf_effective_mm.
        spacingMm: clampSpacing(c.spacingMm),
      })),
      sheet: { w: shW, h: shH },
      kerf: parseFloat(vecKerf) || 0,
      margins: effectiveMargins,
      // ⑬f: the rail control finally reaches the request. Held since PR ④ as a
      // documented scope call, then held again through ⑬a–⑬e because the wire
      // encoding was ambiguous and a wrong guess 422s every layout run.
      // ⑭c: echoed, never regenerated. Null on a first attempt.
      prepressAck: ackToken,
      // ⑭e / D6: what this deployment ACCEPTS, so the serializer can omit
      // `?carton_type=` entirely while there is only one type to choose. Read
      // from the endpoint, never a literal — `carton_types` returning two
      // values again is the whole notification mechanism for IL coming back.
      offeredCartonTypes: prepressCaps?.cartonTypes || [],
    };

    // ── Cancel, live from the first instant ──
    // Before the job exists (still uploading): abort the submit — no job is
    // created server-side. After: real backend cancel (v1.5 §6). If the click
    // lands exactly while the submit resolves, `cancelWanted` delivers the
    // backend cancel the moment the job id arrives.
    const submitCtrl = new AbortController();
    let live = null;             // { jobId, poll, cancel } once the job exists
    let cancelWanted = false;

    const requestBackendCancel = async () => {
      try {
        await cancelJob({ apiBase: API_BASE, cancelUrl: live.cancel, jobId: live.jobId });
        // 202 snapshot; terminal status arrives via the poll. Nothing to do here.
      } catch (e) {
        if (e instanceof VectorApiError && e.status === 404) {
          // §6.3: too late — the job already finished; the poll resolves it
          // (likely 'succeeded'). Not an error.
        } else {
          cancelWanted = false;
          setVecJob((p) => (p ? { ...p, cancel_requested: false } : p));
          jobs.updateJob(trayId, { message: 'Cancel not delivered — still running.' });
          toast.notify({ kind: 'info', title: "Couldn't send cancel", message: 'The layout job is still running. You can try again.' });
        }
      }
    };

    vecCancelRef.current = () => {
      if (cancelWanted) return;          // already requested
      cancelWanted = true;
      // Instant feedback — the click must never look unanswered.
      setVecJob((p) => (p ? { ...p, cancel_requested: true } : { status: 'running', cancel_requested: true, progress: null }));
      jobs.updateJob(trayId, { message: 'Cancelling…' });
      if (live) requestBackendCancel();
      else submitCtrl.abort();           // still uploading — no job exists yet
    };

    // ── Start: warm the path, then submit with live upload progress ──
    // Warm-up: a tiny GET (3 s cap, errors swallowed) forces the browser to
    // validate/re-establish its pooled connection and wakes the Cloudflare↔
    // cloudflared path BEFORE megabytes of multipart are committed to it. A
    // dead pooled connection or sleeping tunnel is the classic cause of a
    // submit that sits at "provisional headers / 0 B" while backend logs stay
    // silent. Any response — even a 404 — proves the path is alive.
    jobs.updateJob(trayId, { message: 'Connecting to the engine…' });
    setVecJob((p) => (p ? { ...p, progress: { phase: 'connecting', message: 'Connecting to the engine…', fraction: null } } : p));
    try {
      const warmCtrl = new AbortController();
      const warmT = setTimeout(() => warmCtrl.abort(), 3000);
      // /health is the purpose-built ops probe (PR ⓪+①); on older backends it
      // 404s, which still validates/wakes the connection path just the same.
      await fetch(`${API_BASE}/health`, { cache: 'no-store', signal: warmCtrl.signal }).catch(() => {});
      clearTimeout(warmT);
    } catch { /* best-effort only */ }
    if (cancelWanted) {
      jobs.endJob(trayId, { status: 'cancelled', message: 'Cancelled before upload.' });
      toast.notify({ kind: 'info', title: 'Layout cancelled' });
      vecCancelRef.current = null; setVecJob(null); setVecLoading(false);
      return;
    }

    // Upload progress + stall watchdog. XHR-backed submit reports real upload
    // percentages; if neither a progress event nor a response arrives for 12 s,
    // say so honestly instead of looking frozen — Cancel stays live throughout.
    const upl = { lastEvent: Date.now(), done: false, stalled: false };
    const uploadMsg = (pct) => (upl.done
      ? 'Upload complete — waiting for the engine to accept the job…'
      : `Uploading cartons…${pct != null ? ` ${pct}%` : ''}`);
    const onUploadProgress = ({ loaded, total }) => {
      upl.lastEvent = Date.now(); upl.stalled = false;
      upl.done = total != null && loaded >= total;
      const frac = total ? Math.min(1, loaded / total) : null;
      const pct = total ? Math.round((loaded / total) * 100) : null;
      if (!cancelWanted) {
        setVecJob((p) => ({ ...(p || { status: 'submitting', cancel_requested: false }), progress: { phase: 'uploading', message: uploadMsg(pct), fraction: frac } }));
        jobs.updateJob(trayId, { fraction: frac, message: uploadMsg(pct) });
      }
    };
    const watchdog = setInterval(() => {
      if (cancelWanted || upl.stalled) return;
      if (Date.now() - upl.lastEvent > 12000) {
        upl.stalled = true;
        const note = upl.done
          ? 'Upload sent, but the engine hasn\u2019t answered — the edge/tunnel may be stalled. You can cancel and retry.'
          : 'Upload appears stalled — the connection may be stuck (edge/tunnel). You can cancel and retry.';
        setVecJob((p) => (p && !p.cancel_requested ? { ...p, progress: { ...(p.progress || {}), phase: 'uploading', message: note } } : p));
        jobs.updateJob(trayId, { message: 'Connection may be stalled — cancel and retry if it persists.' });
      }
    }, 4000);

    let started = null;
    try {
      started = await startVectorLayoutAsync({ ...reqArgs, signal: submitCtrl.signal, onUploadProgress });
    } catch (e) {
      clearInterval(watchdog);
      if (submitCtrl.signal.aborted || e?.name === 'AbortError') {
        // Cancelled during upload. The submit never completed → no server-side job.
        jobs.endJob(trayId, { status: 'cancelled', message: 'Cancelled during upload.' });
        toast.notify({ kind: 'info', title: 'Layout cancelled' });
        vecCancelRef.current = null; setVecJob(null); setVecLoading(false);
        return;
      }
      if (e instanceof VectorApiError && (e.status === 404 || e.status === 405)) {
        started = null;   // backend predates the async export route — use sync
      } else {
        failVectorFromError(e, trayId);
        vecCancelRef.current = null; setVecJob(null); setVecLoading(false);
        return;
      }
    }
    clearInterval(watchdog);

    if (!started) {
      // ── Sync fallback: single-shot request + client-side abort ──
      // (Same controller: a cancel keeps working seamlessly across the switch.)
      //
      // PR ⑬a (D7): both async routes exist on every supported backend, so
      // reaching here means something is genuinely wrong upstream. Two changes:
      //
      //  1. `auto` can NEVER run here. It nests once per candidate and the sync
      //     route rejects it with 400 AutoRequiresAsync — so refuse locally with
      //     an explanation instead of spending an upload on a guaranteed error.
      //  2. The downgrade is announced. Silently swapping a cancellable
      //     background job for a blocking request is a behaviour change nobody
      //     asked for; if it happens, the operator should know why the run feels
      //     different and why Cancel now only stops the waiting.
      const fallbackModes = (reqArgs.cartons || []).map((c) => c.prepress ?? null);
      if (requestsAuto(fallbackModes)) {
        const msg = 'Automatic prepress needs the background job route, which this '
          + 'server did not accept. Switch those cartons to plain, or try again once '
          + 'the engine is reachable.';
        setVecError(msg);
        toast.notify({ kind: 'error', title: 'Cannot run automatic mode', message: msg });
        jobs.endJob(trayId, { status: 'failed', error: msg });
        vecCancelRef.current = null; setVecJob(null); setVecLoading(false);
        return;
      }
      toast.notify({
        kind: 'warn',
        title: 'Running without a background job',
        message: 'The engine did not accept a background job, so this layout is running '
          + 'as a single blocking request. Cancel will stop waiting, but the engine may still finish.',
      });

      try {
        const result = await getVectorPlacements({ ...reqArgs, signal: submitCtrl.signal });
        finishVectorSuccess(result, trayId, taggedNames);
      } catch (e) {
        if (submitCtrl.signal.aborted || e?.name === 'AbortError') {
          jobs.endJob(trayId, { status: 'cancelled', message: 'Stopped waiting (the engine may still finish server-side).' });
          toast.notify({ kind: 'info', title: 'Layout cancelled', message: 'Stopped waiting for the engine. Adjust inputs and run again.' });
        } else {
          failVectorFromError(e, trayId);
        }
      } finally {
        vecCancelRef.current = null;
        setVecJob(null);
        setVecLoading(false);
      }
      return;
    }

    // ── Async flow: poll the URLs from the submit response (v1.5 §1 — the
    // response's poll/cancel fields ARE the contract; never build paths). ──
    live = { jobId: started.job_id, poll: started.poll, cancel: started.cancel };
    setVecJobId(started.job_id);        // PR ⑥: retained for the approval badge
    recordJobContext(started.job_id);  // §2A/§2B: so a tray click can restore this setup
    if (cancelWanted) requestBackendCancel();   // clicked while the submit was resolving

    // ⑭i: shared cadence, measured from the moment the job exists rather than
    // from the start of the submit — the upload can be slow and does not tell
    // us anything about how long the NEST will take.
    const pollStartedAt = Date.now();
    try {
      for (;;) {
        let job;
        try {
          job = await pollJob({ apiBase: API_BASE, pollUrl: live.poll, jobId: live.jobId });
        } catch (e) {
          if (e instanceof VectorApiError && e.status === 404) {
            // §6.3: finished jobs no longer 404 (a durable record answers), so
            // a 404 now means a genuinely unknown id — or another user's job,
            // which returns 404 rather than 403 (§6.4).
            setVecError('This layout job could not be found. Run the layout again.');
            jobs.endJob(trayId, { status: 'failed', error: 'Job not found — run the layout again.' });
          } else {
            failVectorFromError(e, trayId);
          }
          return;
        }

        // Keep the local cancel intent visible even before the backend acks it.
        setVecJob(cancelWanted && !job.cancel_requested ? { ...job, cancel_requested: true } : job);
        const vTerminal = isJobTerminal(job.status);
        // §6.3: durable replay carries a zeroed progress stub — don't render it live.
        const vPersisted = isPersistedRecord(job);
        jobs.updateJob(trayId, {
          fraction: vPersisted ? null : (job.progress?.fraction ?? null),
          message: (cancelWanted || job.cancel_requested) && !vTerminal
            ? 'Cancelling…'
            : (vPersisted ? job.status : (job.progress?.message || job.progress?.phase || job.status)),
        });

        if (vTerminal) {
          if (job.status === 'succeeded' && job.result) {
            finishVectorSuccess(job.result, trayId, taggedNames, live);
          } else if (job.status === 'cancelled') {
            // result is null by contract; nothing to keep or lose. Neutral, not an error.
            jobs.endJob(trayId, { status: 'cancelled', message: 'Cancelled.' });
            toast.notify({ kind: 'info', title: 'Layout cancelled' });
          } else if (routeHaltIfAny(job, trayId)) {
            // A halted job: uploads are already on the server, so confirming
            // re-queues them rather than sending them again.
          } else {
            // failed · interrupted · unrecognised (§6.2).
            const msg = terminalStatusMessage(job.status, job.error);
            setVecError(msg);
            jobs.endJob(trayId, { status: 'failed', error: msg });
            toast.notify({
              kind: job.status === 'interrupted' ? 'warn' : 'error',
              title: job.status === 'interrupted' ? 'Server restarted' : 'Layout failed',
              message: msg,
            });
          }
          return;
        }
        await sleep(pollDelayMs(Date.now() - pollStartedAt));
      }
    } finally {
      vecCancelRef.current = null;
      setVecJob(null);
      setVecLoading(false);
    }
  };

  /**
   * Download the lossless press-ready PDF (POST /export/vector/pdf).
   * Bridges the /export/vector result into the PDF endpoint's payload: each
   * placement's (x_mm, y_mm) is the min corner of placed_vertices (= placed die
   * lower-left, since kerf is spacing not inflation), and the original PDF bytes
   * are re-sent, one part per unique source_id.
   */
  const pdfAbortRef = useRef(null);
  const cancelPdfExport = () => { if (pdfAbortRef.current) pdfAbortRef.current.abort(); };

  /**
   * @param {Object|null} override  a result to plate INSTEAD of the live nest —
   *   the Layout Editor passes its edited document projected back into result
   *   shape. Both reduce to the same `{source_id, x_mm, y_mm, rotation_deg}`,
   *   so there is one export path and not two.
   */
  const downloadPressReadyPdf = async (override = null) => {
    const result = override || vecResult;
    if (!result?.placements) return;
    setPdfBusy(true); setPdfError(null); setPdfBorders(null); setPdfPress(null);
    const ctrl = new AbortController();
    pdfAbortRef.current = ctrl;
    const jobId = jobs.startJob({ kind: 'export', label: 'Exporting press PDF', message: 'Imposing the sheet…', onCancel: () => ctrl.abort() });
    try {
      // Map source_id -> original File. vecFiles is keyed by filename; source_id
      // is the filename without extension (contract §3.4).
      const srcId = (name) => name.replace(/\.pdf$/i, '');
      const filesBySourceId = {};
      // An OVERRIDE is the editor's sheet, so its artwork comes off the
      // editor's desk. Reading `vecFiles` for both was harmless only while the
      // two shared one list; now a carton the operator added in the editor is
      // not on the Layout bench at all, and plating it from there would send a
      // payload whose `sources[]` has no matching file part — a 400 from the
      // backend describing a carton the operator can plainly see on screen.
      for (const c of (override ? editorFiles : vecFiles)) filesBySourceId[srcId(c.name)] = c.file;
      // #88: a carton that did not come back with a reopened job is said by
      // name, before anything is sent — never "missing file for source_id".
      const notOnBench = missingSourceFiles(result.sources, filesBySourceId);
      if (notOnBench.length) {
        const msg = missingFilesMessage(notOnBench);
        setPdfError(msg);
        jobs.endJob(jobId, { status: 'failed', error: msg });
        return;
      }

      // Carry over the operator's RGB decisions: 'proceed' -> auto_convert,
      // everything else -> as_is. (Re-exported cartons were excluded from the
      // nest, so they won't appear in vecResult.sources.)
      const colorDecisions = {};
      for (const s of result.sources) {
        colorDecisions[s.source_id] = vecDecisions[s.source_id] === 'proceed' ? 'auto_convert' : 'as_is';
      }

      const payload = buildPressPdfPayload(result, {
        colorDecisions, removeBorders,
        marks: pdfMarks, marksOnNeighbours: pdfMarksOnNeighbours, bleed: pdfBleed,
        autoBleed: pdfAutoBleed,
      });
      const { blob, manifest } = await downloadPressPdf({
        apiBase: API_BASE, payload, filesBySourceId, signal: ctrl.signal,
      });
      const borders = borderReport(manifest);
      setPdfBorders({ report: borders, requested: removeBorders });
      const press = pressReport(manifest);
      setPdfPress({ report: press, marks: pdfMarks, bleed: pdfBleed });

      const name = `mono_sheet${override ? '_edited' : ''}_${result.job_id || Date.now()}.pdf`;
      saveBlob(blob, name);
      const converted = manifest && manifest.convert_summaries ? Object.keys(manifest.convert_summaries) : [];
      if (converted.length) {
        // Surface any lossy auto-convert summaries the backend reported.
        setVecWarn(`Press PDF exported. Auto-convert ran on: ${converted.join(', ')}.`);
      }
      toast.notify({
        kind: 'success', title: 'Press-ready PDF downloaded', message: name + (converted.length ? ` · auto-convert: ${converted.join(', ')}` : ''),
      });
      if (press && press.attention) {
        const who = [...new Set([
          ...((press.bleed && press.bleed.rows) || []).filter((r) => r.attention).map((r) => r.sourceId),
          ...((press.marks && press.marks.unresolved) || []).map((u) => u.source_id),
        ])];
        toast.notify({
          kind: 'warn', title: 'Some cartons need a look',
          message: `${who.join(', ')} — see the marks and bleed report beside the download button.`,
        });
      }
      if (borders && borders.attention) {
        // Asked for, not done: those cartons went to plate with their borders.
        const left = borders.rows.filter((r) => r.attention).map((r) => r.sourceId);
        toast.notify({
          kind: 'warn', title: 'Some cartons kept their borders',
          message: `${left.join(', ')} — see the border report beside the download button.`,
        });
      }
      jobs.endJob(jobId, { status: 'succeeded', message: name });
    } catch (e) {
      if (ctrl.signal.aborted || e?.name === 'AbortError') {
        jobs.endJob(jobId, { status: 'cancelled', message: 'Stopped waiting (the export may still finish server-side).' });
        toast.notify({ kind: 'info', title: 'PDF export cancelled', message: 'Stopped waiting for the export. You can run it again.' });
        return;
      }
      toast.notify({ kind: 'error', title: 'PDF export failed', message: e instanceof VectorApiError ? e.message : String(e) });
      jobs.endJob(jobId, { status: 'failed', error: e instanceof VectorApiError ? e.message : String(e) });
      if (e?.code === 'artwork_placement_unavailable') {
        // Not a transport failure: the prepress chain for one carton cannot be
        // reduced to a single placement, so exporting would plate the wrong
        // geometry. Say which carton and why rather than retrying.
        setPdfError(`${e.message}\nThe prepress transform chain for that carton could not be read, `
          + 'so its artwork cannot be positioned. Re-run the layout with a prepress package '
          + 'that publishes the chain.');
      } else if (e instanceof VectorApiError && e.status === 400) {
        setPdfError(`${e.message}\nA die could not be resolved for one of the cartons. This usually means the artwork lost its DIELINE/CutContour convention.`);
      } else if (e instanceof VectorApiError && e.status === 500) {
        setPdfError('Imposition failed on the backend. Safe to retry.');
      } else {
        setPdfError(e instanceof VectorApiError ? e.message : String(e));
      }
    } finally {
      pdfAbortRef.current = null;
      setPdfBusy(false);
    }
  };

  /**
   * The editor asking prepress for a carton's real tile.
   *
   * Kept here rather than in the editor because this is the one thing on that
   * screen that leaves the browser, and API_BASE, auth and error shaping all
   * live at this level. The editor gets a plain async function and no
   * knowledge of transport.
   */
  const fetchPrepressTile = async ({ sourceId, vertices, cartonType, prepressConfig }) => {
    try {
      return await requestPrepressTile({
        apiBase: API_BASE, sourceId, vertices, cartonType, prepressConfig,
      });
    } catch (e) {
      if (e instanceof VectorApiError && e.status === 400) {
        // Prepress refusing is an ANSWER — the outline is not that family's
        // topology, or tiling is off for it — so it is reported as one.
        throw new Error(`${e.message}\nPrepress will not tile this carton as it stands.`);
      }
      if (e instanceof VectorApiError && e.status === 503) {
        throw new Error('Prepress is unavailable, so its tile cannot be built. The layout is unaffected.');
      }
      throw new Error(e instanceof VectorApiError ? e.message : String(e));
    }
  };

  /**
   * Handoff into the Layout Editor: take the finished nest and hand the
   * operator the controls.
   *
   * A fresh document every time, deliberately. Re-opening a layout means
   * "start from what the engine produced", and silently resuming a half-hour-old
   * edit of a DIFFERENT nest under the same button is the kind of surprise that
   * ends in a wrong plate — so an edit in progress is named and confirmed
   * before it is discarded.
   */
  const openInEditor = () => {
    if (!vecResult) return;
    if (editorState && editorState.past.length > 0) {
      const n = editorState.past.length;
      const held = editorFiles.length;
      const ok = window.confirm(
        `The editor holds ${n} unsaved edit${n > 1 ? 's' : ''}`
        + (held ? ` and ${held} carton${held > 1 ? 's' : ''} on its desk` : '')
        + '.\n\nLoading this layout replaces them. Continue?');
      if (!ok) return;
    }
    try {
      // The kerf this sheet was NESTED at — the largest carton spacing can
      // have raised it above the field — so the editor's "too close" check
      // holds the operator to the clearance the engine did.
      const doc = buildEditorDoc(vecResult, { kerf: effectiveKerf(vecResult, parseFloat(vecKerf) || 0) });
      // COPY the bench onto the editor's desk. The editor needs the PDF bytes
      // for every carton on its sheet — /export/vector/pdf re-sends one file
      // part per source_id — and it can no longer read them out of `vecFiles`,
      // because the two desks are now separate. A shallow copy per carton is
      // enough and is the point: the same File object, a distinct row, so
      // removing a carton from the Layout bench afterwards cannot orphan a
      // piece the editor has already placed.
      //
      // REPLACES the desk, and does not merge into it. The document is replaced
      // wholesale a line below — that is this button's stated contract, and the
      // operator has just confirmed it — so a carton kept on the desk would
      // have no prototype in the new document and no way to get one: bytes for
      // a die the palette cannot offer. The desk and the document say the same
      // thing or they say nothing.
      setEditorFiles(vecFiles.filter((c) => sourceIdOf(c)).map((c) => ({ ...c })));
      setEditorState(editorInit(doc));
      setWorkspace('editor');
      toast.notify({
        kind: 'success',
        title: 'Loaded into the editor',
        message: `${doc.pieces.length} piece(s) on a ${fmtLen(doc.sheet.width)} × ${fmtLen(doc.sheet.height)} sheet — move, rotate, add or remove, then export.`,
      });
    } catch (e) {
      toast.notify({
        kind: 'error',
        title: 'Could not open the editor',
        message: e?.message || String(e),
      });
    }
  };

  /**
   * Handoff from the Job Planner: a chosen plan's up_counts (keyed by sku_id)
   * become the per-file copy counts in the vector flow. SKU id is matched to an
   * uploaded PDF by filename-without-extension (the same source_id convention).
   * Files not yet uploaded are surfaced so the user knows what artwork to add.
   * Per contract, plans are proposals — this only stages the counts; the user
   * still runs the layout and (after sign-off) exports.
   */
  const usePlannerPlan = (rawUpCounts /* { sku_id: int } */, candidate) => {
    const srcId = (name) => name.replace(/\.pdf$/i, '');

    // Effective counts: prefer the candidate's top-level up_counts (v1.0); if a
    // multi-form candidate (v1.4+) carries counts only inside forms[], aggregate
    // them so the handoff isn't silently empty.
    let upCounts = rawUpCounts && Object.keys(rawUpCounts).length ? { ...rawUpCounts } : {};
    if (!Object.keys(upCounts).length) {
      const forms = candidate?.forms || candidate?._raw?.forms;
      if (Array.isArray(forms)) {
        forms.forEach((f) => Object.entries(f.up_counts || {}).forEach(([k, v]) => { upCounts[k] = (upCounts[k] || 0) + Number(v || 0); }));
      }
    }

    // Remember the counts so cartons added LATER also pick them up (see
    // commitVectorCartons). This is what makes "plan first, upload after" work.
    setPendingPlanCounts(upCounts);

    // Apply counts to any already-uploaded files that match a SKU id.
    setVecFiles((prev) => prev.map((c) => {
      const id = srcId(c.name);
      return id in upCounts ? { ...c, count: upCounts[id] } : c;
    }));

    // Carry the planner's sheet + margin + kerf into the vector flow so the
    // approved plan's configuration — not just its counts — drives the layout (#5).
    const w = Number(plSheet.w) || shW;
    const h = Number(plSheet.h) || shH;
    setShW(w);
    setShH(h);
    const g = parseFloat(plSheet.gripper) || 0;
    setMarginMode('uniform');
    setMarginUniform(g);
    // The plan was validated against the sheet's nesting; carry the candidate's
    // kerf if the planner surfaced one, else keep the current vector kerf.
    const planKerf = candidate?.sheet?.kerf ?? candidate?.kerf;
    if (planKerf != null) setVecKerf(String(planKerf));

    // Switch into the Auto Imposer workspace.
    setWorkspace('layout');
    setVecResult(null);

    const have = new Set(vecFiles.map((c) => srcId(c.name)));
    const missing = Object.keys(upCounts).filter((id) => !have.has(id));
    const cfgNote = `Sheet ${fmtLen(Number(w))} × ${fmtLen(Number(h))}, ${fmtLen(Number(g))} margin${planKerf != null ? `, ${fmtLen(Number(planKerf))} kerf` : ''} applied.`;
    if (missing.length) {
      setVecWarn(`Plan staged. ${cfgNote} Upload artwork for: ${missing.join(', ')} (counts apply once the PDFs are added).`);
    } else {
      setVecWarn(`Plan staged — counts and configuration applied. ${cfgNote} Review and run the layout.`);
    }
    toast.notify({
      kind: missing.length ? 'info' : 'success',
      title: 'Plan applied to layout',
      message: missing.length ? `Upload artwork for: ${missing.join(', ')}.` : 'Counts and sheet config applied — review and run.',
    });
  };

  // ── Saved jobs (decided 2 Nov with #85) ──────────────────────────────────

  // On return, the last run waits to be saved: one row in the tray and one
  // note — once per run, and only after the saved list has loaded, so a run
  // that IS saved is never offered.
  useEffect(() => {
    if (!savedLoaded) return;
    const wait = unsavedLastRun(returnedRun, savedJobs);
    if (!wait || offeredRunRef.current === wait.jobId) return;
    offeredRunRef.current = wait.jobId;
    jobs.registerJob({
      jobId: wait.jobId, kind: 'layout', status: 'succeeded', fraction: 1,
      label: 'Last run · not saved', message: 'Open it, then use Save job to keep it.',
      startedAt: wait.at || Date.now(), endedAt: wait.at || Date.now(), poll: wait.poll,
      onOpen: () => openRun({ jobId: wait.jobId, poll: wait.poll, ctx: wait, via: { includeDeleted: true } }),
    });
    toast.notify({
      kind: 'info', title: 'Your last layout isn’t saved',
      message: 'It is in the Jobs tray as “Last run”. Open it and use Save job, next to Download PDF, to keep it.',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedLoaded, returnedRun, savedJobs]);

  const savedRows = useMemo(() => savedJobRows(savedJobs), [savedJobs]);
  const savedForRun = useMemo(() => savedByJob(savedJobs), [savedJobs]);
  const saveCtl = saveControl({ result: vecResult, jobId: vecJobId,
    saved: vecJobId ? savedForRun[vecJobId] : null });

  /** The setup a run is saved with: as recorded when it ran, else the bench. */
  const setupForRun = (jobId) => jobContextsRef.current?.[jobId]
    || (lastRunRef.current && lastRunRef.current.jobId === jobId ? lastRunRef.current : null)
    || currentSetup();

  const openSaveDialog = () => { setSaveError(null); setSaveDone(null); setSaveDialog({ mode: 'save', name: '' }); };
  const openRenameDialog = (row) => { setSaveError(null); setSaveDone(null); setSaveDialog({ mode: 'rename', name: row.name, row }); };
  const closeSaveDialog = () => {
    if (saveBusy) return;
    setSaveDialog(null); setSaveDone(null); setSaveError(null);
  };
  const refusal = (e) => {
    const copy = e?.code && PLATFORM_ERROR_COPY[e.code];
    return copy ? `${copy.title}. ${copy.hint}` : (e?.message || String(e));
  };

  const submitSaveDialog = async (name) => {
    const d = saveDialog;
    if (!d) return;
    setSaveBusy(true); setSaveError(null);
    try {
      if (d.mode === 'rename') {
        const updated = await renameSavedJob({ apiBase: API_BASE, id: d.row.id, name });
        setSavedJobs((list) => list.map((it) => (it.id === updated.id ? { ...it, name: updated.name } : it)));
        setSaveDialog(null);
        toast.notify({ kind: 'success', title: 'Renamed', message: `The saved job is now “${updated.name}”.` });
      } else {
        const jobId = vecJobId;
        const { cartonIds, setup } = saveRequestFrom(setupForRun(jobId));
        const resp = await saveJob({ apiBase: API_BASE, jobId, name, cartonIds, setup });
        setSavedJobs((list) => [resp, ...list.filter((it) => it.id !== resp.id)]);
        // It is listed under Saved jobs now, so its unsaved row leaves.
        jobs.dropJobs((rows) => rows.filter((j) => j.jobId === jobId && j.status !== 'running').map((j) => j.id));
        setSaveDone(saveOutcome(resp));
      }
    } catch (e) {
      setSaveError(refusal(e));
      if (e?.code === 'job_already_saved' || e?.code === 'saved_job_not_found') refreshSavedJobs();
    } finally {
      setSaveBusy(false);
    }
  };

  const removeSaved = async (row) => {
    try {
      await removeSavedJob({ apiBase: API_BASE, id: row.id });
      setSavedJobs((list) => list.filter((it) => it.id !== row.id));
      toast.notify({ kind: 'success', title: 'Removed from saved jobs', message: `“${row.name}” is no longer kept. Its cartons stay in the library.` });
    } catch (e) {
      toast.notify({ kind: 'warn', title: 'Could not remove it', message: refusal(e) });
      refreshSavedJobs();
    }
  };

  return (
    <div style={{ fontFamily: FN, color: P.ink, background: P.bg, minHeight: '100vh', transition: 'background .25s, color .25s' }}>
      <TopBar workspace={workspace} setWorkspace={setWorkspace} showApprovals={canApprove(auth.user)}
        savedJobs={{ saved: savedRows, onOpenSaved: openSavedJob, onRenameSaved: openRenameDialog, onRemoveSaved: removeSaved }} />
      <div style={{ maxWidth: workspace === 'planner' ? 1000 : 1340, margin: '0 auto', padding: '22px 20px 48px' }}>

        {/* ═════ CARTON LIBRARY (PR ③) ═════ */}
        {workspace === 'library' && (
          <LibraryView onLoadCarton={loadCartonFromLibrary} onViewChanged={refreshRecentCartons} />
        )}

        {/* ═════ APPROVALS (contract v2.0 §7.2) ═════ */}
        {/* Nav entry is gated on canApprove(); the queue guards again itself. */}
        {workspace === 'approvals' && (
          <div>
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontFamily: MO, fontSize: 9.5, letterSpacing: 1.5, textTransform: 'uppercase', color: P.accent, marginBottom: 4 }}>Sign-off</div>
              <Ttl>Approvals</Ttl>
            </div>
            <ApprovalsQueue
              me={auth.user}
              onOpenJob={async (jobId) => {
                // Cloud feedback §3: an approver must be able to inspect the
                // actual layout before signing off. §6.3 makes a finished job's
                // record durable, so the id is enough to pull the result back.
                try {
                  const job = await pollJob({ apiBase: API_BASE, jobId });
                  if (job?.status === 'succeeded' && job.result) {
                    setVecResult(job.result);
                    setVecJobId(jobId);          // keeps the approval badge attached
                    setVecError(null);
                    setWorkspace('layout');
                    toast.notify({ kind: 'success', title: 'Layout loaded', message: 'Inspect it here, then decide from the Approvals tab.' });
                  } else {
                    toast.notify({ kind: 'warn', title: 'Nothing to inspect', message: `This job is ${job?.status || 'unavailable'} — no layout to load.` });
                  }
                } catch (e) {
                  toast.notify({ kind: 'error', title: 'Could not load layout', message: e?.message || String(e) });
                }
              }}
            />
          </div>
        )}

        {/* ═════ JOB PLANNER (contract v1.0) ═════ */}
        {plannerMode && (
          <div>
            <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, marginBottom: 18, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontFamily: MO, fontSize: 9.5, letterSpacing: 1.5, textTransform: 'uppercase', color: P.accent, marginBottom: 4 }}>Demand-driven planning</div>
                <Ttl>Job Planner</Ttl>
              </div>
              {/* Sub-tabs: Plan (everyday) vs Settings (shop-wide config, §4) */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 2, padding: 3, borderRadius: 8, background: P.elev, border: `0.5px solid ${P.ruleSoft}` }}>
                {[{ key: 'plan', label: 'Plan' }, { key: 'settings', label: 'Settings' }].map((t) => (
                  <button key={t.key} onClick={() => setPlTab(t.key)} style={{
                    fontFamily: MO, fontSize: 11, fontWeight: 500, padding: '6px 14px', borderRadius: 6, cursor: 'pointer',
                    background: plTab === t.key ? P.ink : 'transparent',
                    color: plTab === t.key ? P.sheet : P.muted, border: 'none',
                  }}>{t.label}</button>
                ))}
              </div>
            </div>

            {plTab === 'settings' ? (
              <PlannerSettings
                config={cfg}
                draft={cfgDraft}
                setDraft={setCfgDraft}
                busy={cfgBusy}
                error={cfgError}
                savedAt={cfgSavedAt}
                onSave={cfgSave}
                onReset={cfgReset}
                onReload={cfgLoad}
                advancedOpen={cfgAdvancedOpen}
                setAdvancedOpen={setCfgAdvancedOpen}
                canEdit={canEditConfig}
                signedIn={auth.signedIn}
              />
            ) : (
            <>
            <p style={{ fontSize: 12.5, color: P.muted, margin: '0 0 18px', lineHeight: 1.55, maxWidth: 640 }}>
              Enter customer orders and get ranked imposition plans — per-sheet counts, run length,
              and over/under-production. The planner recommends; a chosen plan's counts feed the
              layout and export flows. Plans require sign-off before press.
            </p>
            <PlannerPanel
              skus={plSkus}
              orderLines={plOrderLines}
              setOrderLines={setPlOrderLines}
              plan={plPlan}
              sheet={plSheet}
              setSheet={setPlSheet}
              busy={plBusy}
              error={plError}
              csvWarnings={plCsvWarnings}
              job={plJob}
              cancelling={plCancelling}
              onCancel={plCancel}
              onAddOrderLine={plAddOrderLine}
              onUploadCsv={plUploadCsv}
              onComputePlan={plComputePlan}
              onRefreshSkus={plRefreshSkus}
              onUsePlan={usePlannerPlan}
              knownSkuIds={vecFiles.map((c) => c.name.replace(/\.pdf$/i, ''))}
              draftSku={plDraftSku} setDraftSku={setPlDraftSku}
              draftQty={plDraftQty} setDraftQty={setPlDraftQty}
              draftDue={plDraftDue} setDraftDue={setPlDraftDue}
              draftOverTol={plDraftOverTol} setDraftOverTol={setPlDraftOverTol}
              draftRequired={plDraftRequired} setDraftRequired={setPlDraftRequired}
              draftUnderTol={plDraftUnderTol} setDraftUnderTol={setPlDraftUnderTol}
            />
            </>
            )}
          </div>
        )}

        {/* ═════ LAYOUT EDITOR — hand-finishing a nested sheet ═════ */}
        {workspace === 'editor' && (
          <LayoutEditor
            state={editorState}
            setState={setEditorState}
            onExport={downloadPressReadyPdf}
            exporting={pdfBusy}
            exportError={pdfError}
            removeBorders={removeBorders}
            onRemoveBordersChange={setRemoveBorders}
            borderReport={pdfBorders}
            pressSwitches={{
              marks: pdfMarks, onMarks: setPdfMarks,
              marksOnNeighbours: pdfMarksOnNeighbours, onMarksOnNeighbours: setPdfMarksOnNeighbours,
              bleed: pdfBleed, onBleed: setPdfBleed,
              autoBleed: pdfAutoBleed, onAutoBleed: setPdfAutoBleed,
            }}
            pressReport={pdfPress}
            // A carton can be placed by hand but only PLATED if its PDF bytes
            // are still on the bench — /export/vector/pdf re-sends the source
            // file per source_id. The editor blocks the export rather than
            // letting it 400 after the operator has finished their work.
            availableSourceIds={editorFiles.map((c) => sourceIdOf(c)).filter(Boolean)}
            onRequestPrepressTile={fetchPrepressTile}
            allowCustomPairArrangement={prepressCaps?.editorCustomPairArrangement === true}
            onGoToLayout={() => setWorkspace('layout')}
            onAddCarton={() => setAddTarget('editor')}
            onRemoveCarton={removeEditorCarton}
          />
        )}

        {/* ═════ VECTOR WORKSPACE — two-pane (rail + sheet) ═════ */}
        {workspace === 'layout' && (
          <div className="mono-twopane" style={{ display: 'grid', gridTemplateColumns: 'minmax(260px, 300px) 1fr', gap: 16, alignItems: 'start' }}>

            {/* ── LEFT RAIL ── */}
            <aside style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

              <section style={railCard}>
                <span style={railHead}>Cartons</span>
                <button onClick={() => setAddTarget('layout')} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', cursor: 'pointer',
                  fontFamily: MO, fontSize: 12, padding: '9px 12px', borderRadius: 6,
                  background: P.elev, border: `0.5px dashed ${P.rule}`, color: P.ink,
                }}>
                  + Add carton
                </button>
                <div style={{ fontFamily: FN, fontSize: 10, color: P.dim, marginTop: 8, lineHeight: 1.45 }}>
                  Drop a PDF — the die is detected from a <code style={{ fontFamily: MO }}>DIELINE</code> layer or a{' '}
                  <code style={{ fontFamily: MO }}>CutContour</code> spot, with a color preflight.
                </div>

                {/* Recent cartons (own + org-shared) from workspace hydration (PR ④ §1). */}
                {recentCartons.length > 0 && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontFamily: MO, fontSize: 9, color: P.dim, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Recent · quick load</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {recentCartons.slice(0, QUICK_LOAD_LIMIT).map((rc) => {
                        const parametric = rc.source_kind === 'parametric' || rc.has_source === false;
                        return (
                          <button key={rc.id} disabled={parametric}
                            title={parametric
                              ? 'No source PDF — can’t load for export'
                              : `Load ${rc.name} onto this bench`}
                            /* STRAIGHT ONTO THIS BENCH, no destination question.
                               These chips live in the Auto Imposer's own rail,
                               so where they load is where they are — and a
                               modal on a control called "quick load" would cost
                               more than the choice is worth. The Library
                               workspace, which has no rail of its own, is where
                               the question belongs. */
                            onClick={async () => {
                              try { await loadLibraryCartonInto(rc, 'layout'); }
                              catch (e) { toast.notify({ kind: 'error', title: 'Could not load', message: e.message || String(e) }); }
                            }}
                            style={{
                              fontFamily: MO, fontSize: 10, padding: '4px 9px', borderRadius: 4,
                              cursor: parametric ? 'not-allowed' : 'pointer', opacity: parametric ? 0.45 : 1,
                              background: 'transparent', color: P.muted, border: `0.5px solid ${P.rule}`,
                              maxWidth: 132, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            }}>{rc.name}</button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {vecFiles.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
                    {vecFiles.map((c, i) => {
                      const sid = sourceIdOf(c);
                      const decision = vecDecisions[sid];
                      // PR ⑬b: offer the tag only when the detected outline is
                      // one of the UNFLAPPED topologies this deployment takes.
                      // AFFORDANCE, not validation — pose, degeneracy and
                      // tolerance are all still checked server-side and the 400
                      // is authoritative. ⑭k: the counts come from
                      // capabilities, because they are 6 for BL, 10 for RTE and
                      // 8 or 12 for STE — testing for 6 made the tuck families
                      // untaggable from this rail entirely.
                      const blockReason = unflappedBlockReason(c.detection?.vertices, prepressCaps);
                      const tagged = !!c.prepress;
                      const errHere = prepressErrPiece === sid;
                      return (
                        <div key={c.id} style={{
                          display: 'flex', flexDirection: 'column', gap: 7, padding: '8px 10px', borderRadius: 6,
                          background: P.elev,
                          border: `0.5px solid ${errHere ? P.danger : decision === 'reexport' ? P.warn : tagged ? P.accent : P.ruleSoft}`,
                          opacity: c.count === 0 || decision === 'reexport' ? 0.55 : 1,
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                            <span style={{ width: 9, height: 9, borderRadius: 2, background: TC[i % TC.length], flexShrink: 0 }} />
                            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2, minWidth: 0, flex: 1 }}>
                              <span style={{ fontFamily: MO, fontSize: 11, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</span>
                              {decision === 'reexport' && (
                                <span style={{ fontFamily: MO, fontSize: 9, color: P.warn }}>awaiting re-export</span>
                              )}
                              {c.deleted && (
                                <span title="Deleted from the library after this job used it. It still works for this job."
                                  style={{ fontFamily: MO, fontSize: 9, color: P.warn }}>deleted from the library</span>
                              )}
                            </div>
                            <input type="number" min="0" step="1" value={c.count} title="Copies"
                              onChange={(e) => updateVecCount(c.id, e.target.value)}
                              style={{
                                fontFamily: MO, fontSize: 12, padding: '3px 6px', width: 48, borderRadius: 4,
                                border: `0.5px solid ${P.rule}`, background: P.sheet, color: P.ink, textAlign: 'center', boxSizing: 'border-box',
                              }} />
                            {/* 1.11: the clear space this carton needs around it.
                                The sheet nests at the largest spacing on it. */}
                            <label title={`Spacing — clear space around this carton on the sheet (0–${SPACING_MAX_MM} mm). `
                              + 'The layout keeps every carton at least the largest spacing apart; for a prepress '
                              + 'carton it is also the gap inside its tile. The printer\'s own layouts leave room '
                              + 'for bleed on both cartons and a lane of marks between them.'}
                              style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontFamily: MO, fontSize: 9, color: (c.spacingMm || 0) > 0 ? P.ink : P.dim }}>
                              gap
                              <LenInput mm={c.spacingMm || 0} onMm={(v) => updateVecSpacing(c.id, v)} stepMm={0.5}
                                style={{
                                  fontFamily: MO, fontSize: 11, padding: '3px 4px', width: 46, borderRadius: 4,
                                  border: `0.5px solid ${P.rule}`, background: P.sheet, color: P.ink, textAlign: 'center', boxSizing: 'border-box',
                                }} />
                            </label>
                            <button onClick={() => removeVecFile(c.id)} title="Remove" style={{
                              background: 'none', border: 'none', color: P.dim, cursor: 'pointer', fontSize: 16, lineHeight: 1, flexShrink: 0,
                            }}>×</button>
                          </div>

                          {/* ⑭d follow-up — A FIRM ORDER LINE IS NOT A PREPRESS
                              OPTION. This lived inside the `!blockReason`
                              prepress cluster below and was additionally gated
                              on mode === 'auto', which made it unreachable for
                              the one carton an operator most wants to mark: one
                              whose flaps are ALREADY DRAWN. That outline is a
                              finished dieline, `unflappedBlockReason` is truthy,
                              and the whole cluster renders null — so the control
                              was not disabled, it was absent, and no amount of
                              mode-switching could summon it.

                              The backend was never the constraint. It classes a
                              hand-flapped firm carton as legitimate and runs it
                              with no warning at all (`confirmation_warnings`:
                              "flaps already drawn by hand"), and the §7.1 halt
                              exists precisely to cover the OTHER case — firm on
                              a still-unflapped outline — which ⑭d §2 deliberately
                              kept reachable.

                              So it renders in the ROW HEADER, next to the copies
                              count, where the other order facts live: offered
                              wherever the deployment understands the flag, and
                              never hidden by a decision about dielines. When the
                              deployment would not honour it, the reason is shown
                              rather than the control removed. */}
                          {canMarkRequired && (() => {
                            const firm = firmLineHonoured(c, vecFiles, prepressCaps);
                            const own = requiredOwnershipOf(prepressCaps);
                            return (
                              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 5, paddingLeft: 18, flexDirection: 'column' }}>
                                <label style={{
                                  display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer',
                                  fontFamily: MO, fontSize: 10,
                                  color: c.required ? (firm.honoured ? P.warn : P.danger) : P.dim,
                                }} title={`Protect this line even if it costs cartons elsewhere on the sheet. Enforced by ${own.owner} on this deployment.`}>
                                  <input
                                    type="checkbox"
                                    checked={c.required === true}
                                    onChange={(e) => updateVecRequired(c.id, e.target.checked)}
                                    style={{ margin: 0, cursor: 'pointer' }}
                                  />
                                  firm order line
                                </label>
                                {c.required === true && !firm.honoured && (
                                  <span style={{ fontFamily: FN, fontSize: 9.5, color: P.warn, lineHeight: 1.45, paddingLeft: 18 }}>
                                    Will not be honoured — {firm.reason}
                                  </span>
                                )}
                              </div>
                            );
                          })()}

                          {/* Prepress tag. Untagged is the resting state — a rail
                              where every row shows a control is a rail nobody reads. */}
                          {!blockReason ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 7, paddingLeft: 18, flexWrap: 'wrap' }}>
                              <label style={{
                                display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer',
                                fontFamily: MO, fontSize: 10, color: tagged ? P.ink : P.dim,
                              }}>
                                <input
                                  type="checkbox"
                                  checked={tagged}
                                  onChange={(e) => updateVecPrepress(c.id, e.target.checked ? 'plain' : null)}
                                  style={{ margin: 0, cursor: 'pointer' }}
                                />
                                Generate dieline (prepress)
                              </label>
                              {/* `auto` measures every candidate on this sheet and
                                  is safe with tiling off — it is then restricted to
                                  non-tiling candidates and returns plain. */}
                              {tagged && railModes.length > 1 && (
                                <select
                                  value={c.prepress}
                                  onChange={(e) => updateVecPrepress(c.id, e.target.value)}
                                  style={{
                                    fontFamily: MO, fontSize: 10, padding: '2px 4px', borderRadius: 4,
                                    border: `0.5px solid ${P.rule}`, background: P.sheet, color: P.ink,
                                  }}>
                                  {railModes.map((m) => (
                                    <option key={m} value={m}>{m === 'auto' ? 'automatic (20–30s)' : m}</option>
                                  ))}
                                </select>
                              )}
                              {/* ⑭l / rev 12 §7.3 — WHICH DIE GETS CUT.
                                  Driven by capabilities.carton_types minus
                                  families_unavailable: `carton_types` is what
                                  the BACKEND accepts, and whether the INSTALLED
                                  package supports each is measured separately,
                                  because the package version cannot answer it
                                  (contract v3.0–v3.4 all ship as 0.17.0).
                                  Offered only where there is a choice to make,
                                  so a BL-only deployment's rail is unchanged.
                                  The suggested types come FIRST but nothing is
                                  preselected — carton_type is a manufacturing
                                  decision and neither we nor the backend infer
                                  it from geometry. */}
                              {tagged && offerableTypes.length > 1 && (() => {
                                const suggested = suggestedCartonTypes(prepressCaps, c.detection?.vertices);
                                const ordered = [
                                  ...suggested,
                                  ...offerableTypes.filter((t) => !suggested.includes(t)),
                                ];
                                const bad = cartonTypeBlockReason(c, prepressCaps);
                                return (
                                  <select
                                    value={c.cartonType || ''}
                                    onChange={(e) => updateVecCartonType(c.id, e.target.value)}
                                    title="Which die this carton is cut as"
                                    style={{
                                      fontFamily: MO, fontSize: 10, padding: '2px 4px', borderRadius: 4,
                                      border: `0.5px solid ${bad ? P.warn : P.rule}`,
                                      background: P.sheet, color: bad ? P.warn : P.ink,
                                    }}>
                                    <option value="">carton type…</option>
                                    {ordered.map((t) => (
                                      <option key={t} value={t}>
                                        {t}{suggested.includes(t) && suggested.length < offerableTypes.length ? ' · fits' : ''}
                                      </option>
                                    ))}
                                  </select>
                                );
                              })()}
                              {/* ⑭e: config belongs to the CARTON, so it is
                                  reached from the carton's own row. Offered
                                  only on a tagged carton — config without a
                                  mode is meaningless and the server rejects
                                  a stray key rather than ignoring it. */}
                              {tagged && (
                                <button
                                  type="button"
                                  onClick={() => setConfigCartonId(c.id)}
                                  style={{
                                    background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                                    fontFamily: MO, fontSize: 10, textDecoration: 'underline',
                                    color: c.prepressConfig ? P.accent : P.dim,
                                  }}
                                  title="Flap ranges and prepress settings for this carton"
                                >
                                  {c.prepressConfig ? 'config ✓' : 'config'}
                                </button>
                              )}
                            </div>
                          ) : tagged ? (
                            // Tagged but the outline no longer qualifies (a
                            // different file was restored under the same name).
                            // Say so rather than sending a request that will 400.
                            <div style={{ paddingLeft: 18, fontFamily: MO, fontSize: 9.5, color: P.warn, lineHeight: 1.4 }}>
                              ⚠ Tagged for prepress, but {blockReason.toLowerCase()}
                              {' '}
                              <button onClick={() => updateVecPrepress(c.id, null)} style={{
                                background: 'none', border: 'none', color: P.accent, cursor: 'pointer',
                                fontFamily: MO, fontSize: 9.5, padding: 0, textDecoration: 'underline',
                              }}>remove tag</button>
                            </div>
                          ) : null}

                          {/* The unflapped-and-untagged refusal, on the row
                              that carries the checkbox which clears it. Shown
                              in DANGER rather than warn: this one disables Run
                              layout outright, and the operator should be able
                              to tell it apart from the advisory notes above. */}
                          {(() => {
                            const bad = unflappedUntaggedReason(c, prepressCaps);
                            return bad ? (
                              <div style={{ paddingLeft: 18, fontFamily: FN, fontSize: 9.5, color: P.danger, lineHeight: 1.45 }}>
                                {bad}
                              </div>
                            ) : null;
                          })()}

                          {/* ⑭l: the reason on the row that has the control,
                              not only in the rail-wide banner. */}
                          {(() => {
                            const bad = cartonTypeBlockReason(c, prepressCaps);
                            return bad ? (
                              <div style={{ paddingLeft: 18, fontFamily: FN, fontSize: 9.5, color: P.warn, lineHeight: 1.45 }}>
                                {bad}
                              </div>
                            ) : null;
                          })()}

                          {errHere && (
                            <div style={{ paddingLeft: 18, fontFamily: MO, fontSize: 9.5, color: P.danger, lineHeight: 1.4 }}>
                              Prepress rejected this carton — see the message below.
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ fontFamily: MO, fontSize: 11, color: P.dim, marginTop: 12 }}>No cartons yet.</div>
                )}
              </section>

              <section style={railCard}>
                <span style={railHead}>Sheet</span>
                <div style={{ display: 'flex', gap: 10 }}>
                  <label style={railField}>W ({unit})
                    <LenInput mm={shW} onMm={(v) => setShW(v)} stepMm={1} style={inp} />
                  </label>
                  <label style={railField}>H ({unit})
                    <LenInput mm={shH} onMm={(v) => setShH(v)} stepMm={1} style={inp} />
                  </label>
                </div>
                <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
                  {[{ l: '28×20', w: 711, h: 508 }, { l: '26×20', w: 660, h: 508 }, { l: 'B2', w: 707, h: 500 }].map((p) => (
                    <button key={p.l} onClick={() => { setShW(p.w); setShH(p.h); }} style={{
                      fontFamily: MO, fontSize: 10, padding: '4px 9px', borderRadius: 4, cursor: 'pointer',
                      background: (shW === p.w && shH === p.h) ? P.ink : 'transparent',
                      color: (shW === p.w && shH === p.h) ? P.sheet : P.muted,
                      border: `0.5px solid ${(shW === p.w && shH === p.h) ? P.ink : P.rule}`,
                    }}>{p.l}</button>
                  ))}
                </div>
              </section>

              <section style={railCard}>
                <span style={railHead}>Nesting</span>
                <div style={{ display: 'flex', gap: 10 }}>
                  <label style={railField}>Kerf ({unit})
                    <LenInput mm={Number(vecKerf) || 0} onMm={(v) => setVecKerf(String(v))} stepMm={0.5} style={inp} />
                  </label>
                </div>
                <div style={{ fontFamily: FN, fontSize: 10, color: P.dim, marginTop: 10, lineHeight: 1.45 }}>
                  Kerf is the gap <strong>between</strong> dies for the knife/laser — not bleed, not the gripper strip. Higher kerf fits fewer pieces.
                </div>
              </section>

              {/* Margins get their own card now that all four edges are
                  separately settable — they are a different decision from kerf
                  (reserved strips at the sheet edge, not spacing between dies)
                  and no longer fit beside it as one number. */}
              <section style={railCard}>
                <span style={railHead}>Margins</span>
                <MarginFields
                  mode={marginMode}
                  onMode={switchMarginMode}
                  uniform={marginUniform}
                  sides={marginSides}
                  onUniform={setMarginUniform}
                  onSides={setMarginSides}
                  error={marginError}
                />
              </section>

              {/* Planning defaults (PR ④ §2) — remembered per user; applied to
                  Sheet/Nesting on next sign-in. Only shown when a workspace
                  backend is available and the user is signed in. */}
              {auth.signedIn && wsSupported !== false && (
                <section style={railCard}>
                  <span style={railHead}>Planning defaults</span>

                  {/* ⑭h / rev 8 §1: rotation policy is resolved by the backend
                      from deployment configuration and is REFUSED from a client
                      (400 EnginePolicyNotAccepted). The toggle is gone rather
                      than left as a display: the resolved value is `null`
                      (the engine decides), so there is no honest value to show,
                      and a control that changes nothing is the silent no-op
                      this whole surface exists to end. */}
                  <div style={{ fontFamily: FN, fontSize: 10, color: P.dim, marginBottom: 12, lineHeight: 1.45 }}>
                    Rotation policy is set on the server for this deployment — measured once for
                    the carton family rather than chosen per job.
                  </div>

                  {/* PR ⑬d: `auto` nests once per candidate, so the wait is
                      structural rather than a slow server. Say so before the
                      run, not with a spinner during it. */}
                  {runNote && (
                    <div style={{
                      fontFamily: FN, fontSize: 10.5, color: P.muted, lineHeight: 1.45,
                      padding: '8px 10px', borderRadius: 6, background: P.elev,
                      border: `0.5px solid ${P.ruleSoft}`, marginBottom: 12,
                    }}>{runNote}</div>
                  )}

                  {dupeBlock && (
                    <div style={{
                      fontFamily: FN, fontSize: 10.5, color: P.danger, lineHeight: 1.45,
                      padding: '8px 10px', borderRadius: 6, background: P.dangerSoft,
                      border: `0.5px solid ${P.dangerLine}`, marginBottom: 12,
                    }}>{dupeBlock}</div>
                  )}

                  {/* ⑭d: a firm line outside automatic mode is inert, and the
                      export path rejects it outright. Say so before the upload,
                      not after it. */}
                  {unflappedBlock && (
                    <div style={{
                      marginBottom: 10, padding: '9px 11px', borderRadius: 6,
                      background: P.dangerSoft, border: `0.5px solid ${P.dangerLine}`,
                      fontFamily: FN, fontSize: 11, color: P.ink,
                    }}>{unflappedBlock}</div>
                  )}
                  {typeBlock && (
                    <div style={{
                      fontFamily: FN, fontSize: 10.5, color: P.warn, lineHeight: 1.45,
                      padding: '7px 9px', borderRadius: 6, background: P.elev,
                      border: `0.5px solid ${P.warn}`,
                    }}>{typeBlock}</div>
                  )}
                  {requiredBlock && (
                    <div style={{
                      fontFamily: FN, fontSize: 10.5, color: P.danger, lineHeight: 1.45,
                      padding: '8px 10px', borderRadius: 6, background: P.dangerSoft,
                      border: `0.5px solid ${P.dangerLine}`, marginBottom: 12,
                    }}>{requiredBlock}</div>
                  )}

                  {/* ⑭e: the per-carton config editor. Mounted once and
                      driven by which carton is open, so two cartons can never
                      have an editor on screen at the same time. */}
                  {configCartonId && (() => {
                    const target = vecFiles.find((c) => c.id === configCartonId);
                    if (!target) return null;
                    return (
                      <PrepressConfigOverlay
                        carton={target}
                        apiBase={API_BASE}
                        caps={prepressCaps}
                        offeredTypes={prepressCaps?.cartonTypes || []}
                        onApply={(cfg) => { updateVecConfig(target.id, cfg); setConfigCartonId(null); }}
                        onClose={() => setConfigCartonId(null)}
                      />
                    );
                  })()}

                  {/* ⑭c: a halt, not an error. The job did not run and can
                      still be run — so this is a decision, not a failure
                      notice. Warning text is shown VERBATIM: it names the
                      carton and says what would be cut, and any rewording of
                      ours loses one or the other. */}
                  {prepressHalt && (
                    <div style={{
                      fontFamily: FN, fontSize: 11, color: P.ink, lineHeight: 1.5,
                      padding: '10px 12px', borderRadius: 6, background: P.warnSoft,
                      border: `0.5px solid ${P.warn}`, marginBottom: 12,
                      display: 'flex', flexDirection: 'column', gap: 8,
                    }}>
                      <div style={{ fontFamily: MO, fontSize: 10, color: P.warn, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Confirm before running
                      </div>
                      {prepressHalt.warnings.map((w, i) => (
                        <div key={w.piece || i}>{w.message}</div>
                      ))}
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <button type="button" onClick={continueDespiteHalt} style={{
                          fontFamily: MO, fontSize: 10.5, padding: '5px 10px', borderRadius: 5,
                          background: P.warn, color: P.sheet, border: 'none', cursor: 'pointer',
                        }}>Continue anyway</button>
                        <button type="button" onClick={() => setPrepressHalt(null)} style={{
                          fontFamily: MO, fontSize: 10.5, padding: '5px 10px', borderRadius: 5,
                          background: 'transparent', color: P.muted, border: `0.5px solid ${P.ruleSoft}`, cursor: 'pointer',
                        }}>Go back and tag it</button>
                      </div>
                    </div>
                  )}

                  {/* ⑬i: capabilities is the only authority since ⑭a, so a
                      failed fetch must not look like a working default. */}
                  {prepressCapsError !== null && (
                    <div style={{
                      fontFamily: FN, fontSize: 10.5, color: P.warn, lineHeight: 1.45,
                      padding: '8px 10px', borderRadius: 6, background: P.warnSoft,
                      border: `0.5px solid ${P.warn}`, marginBottom: 12,
                    }}>
                      Could not read this server’s prepress capabilities
                      {prepressCapsError === 'offline' ? '' : ` (${prepressCapsError})`}.
                      The options below are defaults, not this deployment’s — check a result
                      before quoting from it.
                    </div>
                  )}

                  {prepressCaps && !prepressCaps.available && (
                    <div style={{
                      fontFamily: FN, fontSize: 10.5, color: P.muted, lineHeight: 1.45,
                      padding: '8px 10px', borderRadius: 6, background: P.elev,
                      border: `0.5px solid ${P.ruleSoft}`, marginBottom: 12,
                    }}>
                      Prepress is not available on this server.
                      {prepressCaps.detail ? ` ${prepressCaps.detail}` : ''}
                    </div>
                  )}

                  {/* ⑭a: read off `features`, not the deprecated
                      `min_package_version` (rev 5 §8.2). No
                      `features.allow_tiling` entry means this deployment's
                      package predates the gate entirely, so a tiled result
                      could appear even with the flag off. */}
                  {prepressCaps?.tilingGateUnreliable && (
                    <div style={{
                      fontFamily: FN, fontSize: 10.5, color: P.warn, lineHeight: 1.45,
                      padding: '8px 10px', borderRadius: 6, background: P.warnSoft,
                      border: `0.5px solid ${P.warn}`, marginBottom: 12,
                    }}>
                      This server runs prepress {prepressCaps.packageVersion || '(unreported)'}, which
                      predates the tiling gate — it cannot be relied on to hold.
                      Check <code>prepress_tiled</code> on every result before quoting.
                    </div>
                  )}

                  {/* The manufacturing gate, once the server has answered. */}
                  {prepressCaps && !prepressCaps.allowTiling && (
                    <div style={{
                      fontFamily: FN, fontSize: 10.5, color: P.warn, lineHeight: 1.45,
                      padding: '8px 10px', borderRadius: 6, background: P.warnSoft,
                      border: `0.5px solid ${P.warn}`, marginBottom: 12,
                    }}>
                      Paired and automatic layouts are turned off on this server. That is a
                      die-shop decision, not a setting — the interlocked tile encloses a scrap
                      pocket someone has to confirm strips at production speed.
                    </div>
                  )}


                  {presetsError && (
                    <div style={{ marginTop: 10, fontFamily: MO, fontSize: 10, color: P.danger }}>⚠ {presetsError}</div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
                    <Btn small onClick={savePresets} disabled={presetsBusy}>
                      {presetsBusy ? 'Saving…' : 'Save as my defaults'}
                    </Btn>
                    {presetsSavedAt && !presetsBusy && !presetsError && (
                      <span style={{ fontFamily: MO, fontSize: 10, color: P.ok }}>Saved.</span>
                    )}
                  </div>
                  <div style={{ fontFamily: FN, fontSize: 10, color: P.dim, marginTop: 8, lineHeight: 1.45 }}>
                    Saves the current sheet, margins, kerf and the two fields above. Replaces your whole
                    default set; applied to your forms next time you sign in.
                  </div>
                </section>
              )}

              <section style={railCard}>
                <span style={railHead}>Engine</span>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5, fontFamily: MO, fontSize: 10.5, color: P.muted, marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}><span style={{ color: P.dim }}>Die source</span><span style={{ textAlign: 'right' }}>PDF · DIELINE / CutContour</span></div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}><span style={{ color: P.dim }}>Strategy</span><span>irregular nest</span></div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}><span style={{ color: P.dim }}>Color</span><span>CMYK preserved</span></div>
                </div>
                {vecError && (
                  <div style={{
                    marginBottom: 10, padding: '9px 11px', borderRadius: 6,
                    background: P.dangerSoft, border: `0.5px solid ${P.dangerLine}`,
                    fontFamily: FN, fontSize: 11, color: P.ink, whiteSpace: 'pre-line',
                  }}>{vecError}</div>
                )}
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <Btn onClick={runVectorLayout} disabled={vecLoading || vecFiles.length === 0 || !!marginError || !!dupeBlock || !!requiredBlock || !!unflappedBlock || !!typeBlock} color={P.accent}>
                    {vecLoading ? 'Nesting…' : 'Run layout'}
                  </Btn>
                  {vecLoading && (
                    <button onClick={cancelVectorLayout} disabled={!!vecJob?.cancel_requested} title="Cancel this layout job (interrupts the engine mid-nest)" style={{
                      fontFamily: MO, fontSize: 11, padding: '7px 13px', borderRadius: 6,
                      cursor: vecJob?.cancel_requested ? 'default' : 'pointer', opacity: vecJob?.cancel_requested ? 0.5 : 1,
                      background: 'transparent', color: P.danger, border: `0.5px solid ${P.dangerLine}`,
                    }}>{vecJob?.cancel_requested ? 'Cancelling…' : 'Cancel'}</button>
                  )}
                </div>
                {marginError && (
                  <div style={{ marginTop: 8, fontSize: 11, color: P.danger, fontFamily: MO }}>⚠ {marginError}</div>
                )}
                {vecLoading && (
                  <div style={{ marginTop: 8, fontFamily: MO, fontSize: 10, color: P.dim }}>
                    {vecJob?.cancel_requested
                      ? 'Cancelling…'
                      : (vecJob?.progress?.message || vecJob?.progress?.phase || 'Calling /export/vector…')}
                  </div>
                )}
              </section>
            </aside>

            {/* ── MAIN: SHEET ── */}
            <main style={{ minWidth: 0 }}>
              {vecResult ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {vecWarn && (
                    <div style={{
                      padding: '10px 14px', borderRadius: 8,
                      background: P.warnSoft, border: `0.5px solid ${P.warnLine}`,
                      fontFamily: FN, fontSize: 12, color: P.ink,
                    }}>⚠ {vecWarn}</div>
                  )}

                  {/* PR ⑬b §6: positive confirmation, not absence of error.
                      A silent skip nests the bare outline and looks identical
                      to a success, so this states which way it went. */}
                  {/* ⑭d / rev 5 §6.2 — "showing this is not optional".
                      The only prepress outcome that deliberately produces less
                      than was ordered. Both numbers side by side and above the
                      verification banner, because this is the one that changes
                      an invoice. */}
                  {prepressReduced && (
                    <div style={{
                      padding: '10px 12px', borderRadius: 6, marginBottom: 10,
                      background: P.warnSoft, border: `0.5px solid ${P.warn}`,
                      display: 'flex', flexDirection: 'column', gap: 8,
                    }}>
                      <div style={{ fontFamily: MO, fontSize: 10, color: P.warn, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Reduced to protect a firm order line
                      </div>
                      <div style={{ display: 'flex', gap: 22, alignItems: 'baseline', flexWrap: 'wrap' }}>
                        <div>
                          <div style={{ fontFamily: MO, fontSize: 9, color: P.dim, textTransform: 'uppercase', letterSpacing: 0.4 }}>Ordered</div>
                          <div style={{ fontFamily: MO, fontSize: 20, color: P.ink }}>{prepressReduced.requested ?? '—'}</div>
                        </div>
                        <div>
                          <div style={{ fontFamily: MO, fontSize: 9, color: P.dim, textTransform: 'uppercase', letterSpacing: 0.4 }}>Capacity</div>
                          <div style={{ fontFamily: MO, fontSize: 20, color: P.warn }}>{prepressReduced.capacity ?? '—'}</div>
                        </div>
                        {prepressReduced.shortfall !== null && (
                          <div>
                            <div style={{ fontFamily: MO, fontSize: 9, color: P.dim, textTransform: 'uppercase', letterSpacing: 0.4 }}>Short by</div>
                            <div style={{ fontFamily: MO, fontSize: 20, color: P.warn }}>{prepressReduced.shortfall}</div>
                          </div>
                        )}
                      </div>

                      {/* ⑭f: which order line. A job-wide shortfall tells an
                          operator to investigate; a name tells them who to
                          call, which is the next action either way. */}
                      {prepressReduced.byCarton.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                          {prepressReduced.byCarton.map((c) => (
                            <div key={c.name} style={{ fontFamily: MO, fontSize: 10.5, color: P.ink }}>
                              {c.name}
                              <span style={{ color: P.muted }}>
                                {' — ordered '}{c.requested ?? '—'}{', capacity '}{c.capacity ?? '—'}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                      {prepressReduced.attribution && (
                        <div style={{ fontFamily: FN, fontSize: 10.5, color: P.muted, lineHeight: 1.45 }}>
                          {prepressReduced.attribution}
                        </div>
                      )}
                      <div style={{ fontFamily: FN, fontSize: 11, color: P.ink, lineHeight: 1.5 }}>
                        {prepressReduced.notice}
                      </div>
                    </div>
                  )}

                  {prepressCheck && (
                    <div style={{
                      padding: '10px 14px', borderRadius: 8,
                      background: prepressCheck.ok ? P.okSoft : P.dangerSoft,
                      border: `0.5px solid ${prepressCheck.ok ? P.ok : P.dangerLine}`,
                      fontFamily: FN, fontSize: 12, color: P.ink, lineHeight: 1.5,
                    }}>
                      {prepressCheck.ok
                        ? '✓ Prepress ran — the nested dieline is the generated one, with flaps.'
                        : `⚠ ${prepressCheck.reason}`}
                    </div>
                  )}

                  {/* PR ⑬c: what the machine will actually cut, per tagged
                      carton. Correlated by name inside the component. */}
                  <PrepressReport result={vecResult} caps={prepressCaps} />

                  {/* PR ⑬d: why `auto` picked what it picked. Renders nothing
                      unless the response carries a selection block. */}
                  <SelectionPanel result={vecResult} />

                  {/* PR ⑥ §7.1: advisory approval record for this layout. */}
                  {auth.signedIn && vecJobId && (
                    <ApprovalBadge jobId={vecJobId} job={{ status: 'succeeded' }} me={auth.user} />
                  )}

                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    {/* PR ⑬a §4: "Cartons", not "Placed" — the unit is the point.
                        A pair tile is one placed piece and two cartons. */}
                    <Stat label="Cartons" value={`${cartonsPlaced(vecResult)}${vecResult.n_unplaced ? ` (+${vecResult.n_unplaced} unplaced)` : ''}`} />
                    <Stat
                      label="Utilization"
                      value={`${(vecResult.utilization * 100).toFixed(1)}% gross`}
                      title={utilizationCaveat(vecResult) || undefined}
                      muted={!!utilizationCaveat(vecResult)}
                    />
                    {vecHasMargins(vecResult) && (
                      <Stat label="Net" value={`${(vecNetUtil(vecResult) * 100).toFixed(1)}% of usable`} />
                    )}
                    <Stat label="Kerf" value={fmtLen(vecResult.sheet.kerf ?? 0)} />
                    <Stat label="Runtime" value={`${vecResult.runtime_ms?.toFixed?.(0) ?? vecResult.runtime_ms} ms`} />
                  </div>

                  <div style={{ height: 520, borderRadius: 10, overflow: 'hidden', border: `0.5px solid ${P.rule}`, background: P.sheet }}>
                    <VectorLayoutView result={vecResult} />
                  </div>

                  <section style={railCard}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                      <span style={{ fontFamily: MO, fontSize: 10, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', color: P.dim }}>Job Planner SKUs</span>
                      {skuRegStatus && (
                        <span style={{ fontFamily: FN, fontSize: 11, color: skuRegStatus.kind === 'ok' ? P.ok : skuRegStatus.kind === 'warn' ? P.warn : P.muted }}>{skuRegStatus.text}</span>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {vecResult.sources.map((s) => {
                        const done = registeredSkuIds.includes(s.source_id);
                        return (
                          <button key={s.source_id} onClick={() => registerOneSku(s)}
                            title={done ? 'Registered — click to update' : 'Register this die as a planner SKU'}
                            style={{
                              fontFamily: MO, fontSize: 11, padding: '5px 10px', borderRadius: 4, cursor: 'pointer',
                              background: done ? P.okSoft : 'transparent', color: done ? P.ok : P.muted,
                              border: `0.5px solid ${done ? P.okLine : P.rule}`,
                            }}>{done ? '✓ ' : '+ '}{s.source_id}</button>
                        );
                      })}
                    </div>
                  </section>

                  {pdfError && (
                    <div style={{
                      padding: '12px 14px', borderRadius: 8,
                      background: P.dangerSoft, border: `0.5px solid ${P.dangerLine}`,
                      fontFamily: FN, fontSize: 12, color: P.ink, whiteSpace: 'pre-line',
                    }}>{pdfError}</div>
                  )}
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Btn onClick={() => downloadPressReadyPdf()} disabled={pdfBusy}
                      title="Gang the cartons losslessly (vector Form XObjects, CMYK preserved, crop + registration marks) and download the press-ready sheet PDF.">
                      {pdfBusy ? 'Building PDF…' : 'Download press-ready PDF'}
                    </Btn>
                    {/* Saved jobs (decided 2 Nov): a run is kept only when saved, by name. */}
                    {saveCtl.state === 'save' && (
                      <button onClick={openSaveDialog}
                        title="Keep this layout and its setup under a name, in the Jobs tray. Its draft cartons are saved to your library."
                        style={{
                          fontFamily: MO, fontSize: 11, padding: '7px 13px', borderRadius: 6, cursor: 'pointer',
                          background: 'transparent', color: P.ink, border: `0.5px solid ${P.rule}`,
                        }}>Save job</button>
                    )}
                    {saveCtl.state === 'saved' && (
                      <span style={{ fontFamily: MO, fontSize: 11, color: P.ok }}>✓ {saveCtl.label}</span>
                    )}
                    {saveCtl.state === 'blocked' && (
                      <span title={saveCtl.why} style={{ fontFamily: MO, fontSize: 10.5, color: P.dim }}>Can’t be saved</span>
                    )}
                    {pdfBusy && (
                      <button onClick={cancelPdfExport} title="Stop waiting for this export (it may still finish server-side)" style={{
                        fontFamily: MO, fontSize: 11, padding: '7px 13px', borderRadius: 6, cursor: 'pointer',
                        background: 'transparent', color: P.danger, border: `0.5px solid ${P.dangerLine}`,
                      }}>Cancel</button>
                    )}
                    <RemoveBordersSwitch value={removeBorders} onChange={setRemoveBorders} disabled={pdfBusy} />
                    <PressSheetSwitches
                      marks={pdfMarks} onMarks={setPdfMarks}
                      marksOnNeighbours={pdfMarksOnNeighbours} onMarksOnNeighbours={setPdfMarksOnNeighbours}
                      bleed={pdfBleed} onBleed={setPdfBleed}
                      autoBleed={pdfAutoBleed} onAutoBleed={setPdfAutoBleed} disabled={pdfBusy} />
                    {pdfBusy && (<span style={{ fontFamily: MO, fontSize: 10, color: P.dim }}>Calling /export/vector/pdf…</span>)}
                    {/* The nest is close and not always right. This is the door
                        to finishing it by hand instead of re-running and hoping. */}
                    <button onClick={openInEditor} disabled={pdfBusy}
                      title="Load this layout into the manual editor — move, rotate, add or remove cartons and change the sheet, then export from there."
                      style={{
                        fontFamily: MO, fontSize: 11, padding: '7px 13px', borderRadius: 6,
                        cursor: pdfBusy ? 'default' : 'pointer', opacity: pdfBusy ? 0.4 : 1,
                        background: 'transparent', color: P.accent, border: `0.5px solid ${P.accentLine}`,
                      }}>Open in editor →</button>
                    <span style={{ fontFamily: MO, fontSize: 9, color: P.dim, marginLeft: 'auto' }}>job_id: {vecResult.job_id}</span>
                  </div>
                  {pdfBorders && (
                    <BorderReportList report={pdfBorders.report} requested={pdfBorders.requested} />
                  )}
                  {pdfPress && (
                    <PressReportList report={pdfPress.report} marks={pdfPress.marks} bleed={pdfPress.bleed} />
                  )}
                </div>
              ) : (
                <div style={{
                  height: 520, borderRadius: 10, border: `0.5px dashed ${P.rule}`, background: P.sheet,
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, textAlign: 'center', padding: 24,
                }}>
                  <div style={{ fontFamily: DISPLAY, fontSize: 24, color: P.muted }}>
                    {vecLoading ? (vecJob?.cancel_requested ? 'Cancelling…' : 'Nesting…') : 'No layout yet'}
                  </div>
                  <div style={{ fontFamily: MO, fontSize: 11, color: P.dim, maxWidth: 320, lineHeight: 1.5 }}>
                    {vecLoading
                      ? (vecJob?.progress?.message || vecJob?.progress?.phase || 'The engine is packing your cartons onto the sheet.')
                      : 'Add carton PDFs in the rail, set the sheet and kerf, then run the layout to see the nested sheet here.'}
                  </div>
                  {/* §1: live completion percentage. Hidden while a persisted
                      replay is showing, where the counters are a zeroed stub. */}
                  {vecLoading && typeof vecJob?.progress?.fraction === 'number' && (
                    <div style={{ width: 220, marginTop: 2 }}>
                      <div style={{ height: 5, borderRadius: 3, background: P.elev, overflow: 'hidden' }}>
                        <div style={{
                          height: '100%', background: P.accent, transition: 'width .3s ease',
                          width: `${Math.round(Math.max(0, Math.min(1, vecJob.progress.fraction)) * 100)}%`,
                        }} />
                      </div>
                      <div style={{ fontFamily: MO, fontSize: 12, color: P.ink, marginTop: 6 }}>
                        {Math.round(Math.max(0, Math.min(1, vecJob.progress.fraction)) * 100)}%
                      </div>
                    </div>
                  )}
                  {vecLoading && !vecJob?.cancel_requested && (
                    <button onClick={cancelVectorLayout} style={{
                      fontFamily: MO, fontSize: 11, padding: '6px 14px', borderRadius: 6, cursor: 'pointer', marginTop: 4,
                      background: 'transparent', color: P.danger, border: `0.5px solid ${P.dangerLine}`,
                    }}>Cancel</button>
                  )}
                </div>
              )}
            </main>
          </div>
        )}

        {/* Saved jobs (decided 2 Nov): name a run to keep it, or rename one. */}
        <SaveJobDialog
          dialog={saveDialog}
          busy={saveBusy}
          error={saveError}
          outcome={saveDone}
          onSubmit={submitSaveDialog}
          onClose={closeSaveDialog}
        />

        {/* Which desk? Only the Library workspace asks — every other way in
            already knows where the carton is going. */}
        <LoadDestinationPrompt
          pending={libLoad}
          busy={!!libLoad?.busy}
          error={libLoad?.error || null}
          onLoad={(dest) => loadLibraryCartonInto(libLoad.carton, dest, { prompted: true })}
          onCancel={() => setLibLoad(null)}
        />

        {/* The flap question, for a carton that arrived on the editor's desk
            as an unflapped base. Sits ABOVE the add overlay in the stack (z 210
            vs 200) because it is raised by that overlay's own commit. */}
        <EditorFlapPrompt
          pending={flapQueue.length
            ? { ...flapQueue[0], remaining: flapQueue.length - 1 }
            : null}
          busy={flapBusy}
          error={flapErr}
          described={flapDescribed}
          caps={prepressCaps}
          cartonType={flapType}
          onCartonType={(t) => { setFlapType(t); setFlapDescribed(null); setFlapErr(null); }}
          onAddFlaps={addFlapsToPending}
          onPlaceAsDrawn={placePendingAsDrawn}
          onCancel={nextFlapCarton}
        />

        {/* Add-carton overlay — the vector add flow (drop PDF or pick from the
            library → detect die → preflight → commit), ONE flow serving TWO
            desks. Which desk it commits to is the button that opened it, so the
            Layout rail and the editor's rail offer the same surface and neither
            has to grow its own. */}
        <AddCartonPdfOverlay
          open={addTarget !== null}
          onClose={() => setAddTarget(null)}
          onCommit={addTarget === 'editor' ? commitEditorCartons : commitVectorCartons}
          apiBase={API_BASE}
          caps={prepressCaps}
        />
      </div>
    </div>
  );
}
